import { V3_INPUT_CLS } from './controlStyles'
import { RecordWorkspace } from './RecordWorkspace'
import { recordWorkspaceLayout, recordFieldGroups } from './recordWorkspaceLayout'
// AB#1622/1866 — shared runtime view renderer (promoted from apps/tenant so the designer preview renders
// exactly what the tenant shows — one renderer, no drift). Every layout slot (including section-header)
// and the component dispatcher live here once: the tenant's ViewRenderer is now a thin host that
// delegates all layout to this file and only injects the live, data-coupled seams (FK option loader,
// custom-component / documents / map / button / barcode / chart slots, FK-aware EditField). In this
// shared package those data-coupled pieces fall back to placeholders / the preview EditField, keeping
// the package free of tenant data-fetch coupling.
import { useState, useEffect, useCallback, useMemo, useRef, useId, type ReactNode, type ComponentType, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { instantInputValue, instantStoredValue } from './dateTimeInstant'
import { MessageList } from './MessageList'
import { ComparisonList } from './ComparisonList'
import { PlanningTimeline } from './PlanningTimeline'
import { DataWorkspace, type DataWorkspaceProps } from './DataWorkspace'
import { sealedFields } from './sealedFields'
// The string-expression evaluator the runtime renderer uses (field=value / truthy / permission:),
// distinct from the legacy object-condition evaluateShowIf this package also exports.
import { evaluateShowIf } from './evaluateShowIfExpr'
import { isTruthyValue } from './booleanValue'
import { requiresWholeQuantity, wholeQuantityError } from './wholeUnitQuantity'
import { applyComputes } from './computeFormula'
import { interpolateTokens } from './interpolate'
import { CURRENCIES, currencyLabel } from './currencies'
import { applyCopyFrom, type FetchEntityRecord } from './copyFrom'
import { useUnitCatalogue } from './unitCatalogue'
import { useEntityMeta, useEntityMetaOptions } from './entityMeta'
import { createRowKeys } from './rowKeys'
import { buildRequest, isPriceReplaceable, type FetchLinePrice } from './livePricing'
import { COUNTRIES } from './countries'
import { type AddressValue } from './addressMetadata'
import { MONTHS, monthName } from './months'
import { formatEnumLabel, enumOptions, enumOptionLabel } from './enumLabel'
import { resolveOptionTone, tonePillClasses, toneAccentBorderClasses, isStatusTone } from './statusTone'
import type { StatusTone } from './statusTone'
import { Icon } from '../icons'
import { FieldHint } from './FieldHint'
import { Modal } from './Modal'
import type {
    ViewDefinition, ViewComponent, ViewComponentType, ViewContext, EntityFieldMeta, ViewFieldMeta, FieldSlot,
    TableColumn,
} from './viewTypes'
import { packRows } from './packRows'
import { readDataField } from './readDataField'
import { useViewport, ViewportProvider, type ViewportTier } from './viewport'
// AB#3953 / AB#3954 — table column personalisation: identity, the per-user layout hook (with its
// store seam) and the shared configurator every table primitive renders.
import { tableIdentity, EMPTY_COLUMNS } from './columnLayout'
import { useTableLayout, useTableLayoutStore, type TableLayoutStore } from './useTableLayout'
import { useColumnConfigurator } from './ColumnConfigurator'
import { DashboardGrid } from './DashboardGrid'
import './moduleThemes.css'
// AB#3989 — the tile presentation (ViewDefinition.presentation === 'tile'): body-only, chrome-free
// components that fill a host-owned tile. The cockpit is the first host.
import './tile.css'
import { ComparisonReport } from './ComparisonReportPanel'
import { AsyncLookupCombobox } from './AsyncLookupCombobox'
import { useLookupCreate } from './lookupCreate'
import { AddressEditor, type FetchAddressSearch, type AddressMapComponent } from './AddressEditor'

// AB#1399 (epic AB#1398) — fresh, context-driven tenant view renderer.
//
// Renders a view definition's readonly grid against a context object (the inputs the open-view
// stage data-bound into the view). Each component resolves its dataBinding ($order, $order.lines)
// against the context; field metadata (labels, value formatting) comes from the entity definitions
// of the bound context entries. Read-only here; edit mode + save is AB#1400.

// ── Binding + formatting helpers ─────────────────────────────────────────────

function resolveBinding(binding: string | undefined, context: ViewContext): unknown {
    if (!binding) { return undefined }
    const path = binding.startsWith('$') ? binding.slice(1) : binding
    let cur: unknown = context
    for (const seg of path.split('.').filter(Boolean)) {
        if (cur && typeof cur === 'object') {
            cur = (cur as Record<string, unknown>)[seg]
        } else {
            return undefined
        }
    }
    return cur
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

// AB#2049 — tenant-locale + base-currency-aware formatting. The shared formatValue used a bare
// toLocaleDateString() / toLocaleString() (BROWSER locale, not the tenant's) and money rendered as a
// symbol-less 2-dp number. This context threads the tenant's BCP 47 locale + ISO 4217 base currency down
// from the thin host; both are OPTIONAL — undefined preserves the previous (browser-locale, bare-number)
// behaviour verbatim, so the designer preview (which passes nothing) is byte-identical.
export interface FormatContext {
    /** IETF BCP 47 locale tag (e.g. "en-GB", "de-DE"). Undefined → the browser's default locale. */
    locale?: string | undefined
    /** ISO 4217 currency code for money fields (e.g. "GBP"). Undefined → money renders as a bare number. */
    currency?: string | undefined
}

// AB#2049 — Intl.* constructors are expensive; cache one instance per locale (+ options signature) at the
// module level so repeated cell formatting reuses it. `undefined` locale keys the browser-default instance.
const dateFormatCache = new Map<string, Intl.DateTimeFormat>()
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>()
const numberFormatCache = new Map<string, Intl.NumberFormat>()
const moneyFormatCache = new Map<string, Intl.NumberFormat>()

function dateFormatter(locale: string | undefined): Intl.DateTimeFormat {
    const key = locale ?? ''
    let f = dateFormatCache.get(key)
    if (!f) { f = new Intl.DateTimeFormat(locale, { dateStyle: 'short' }); dateFormatCache.set(key, f) }
    return f
}

function dateTimeFormatter(locale: string | undefined): Intl.DateTimeFormat {
    const key = locale ?? ''
    let f = dateTimeFormatCache.get(key)
    if (!f) { f = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }); dateTimeFormatCache.set(key, f) }
    return f
}

function numberFormatter(locale: string | undefined): Intl.NumberFormat {
    const key = locale ?? ''
    let f = numberFormatCache.get(key)
    if (!f) { f = new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }); numberFormatCache.set(key, f) }
    return f
}

function moneyFormatter(locale: string | undefined, currency: string): Intl.NumberFormat {
    const key = `${locale ?? ''}|${currency}`
    let f = moneyFormatCache.get(key)
    if (!f) { f = new Intl.NumberFormat(locale, { style: 'currency', currency }); moneyFormatCache.set(key, f) }
    return f
}

// AB#3022 — how one dimension's accepted values read. A rule that names three bill-tos should say so;
// a rule that names 1,847 must not try, or the one cell swallows the row and the collapse that made the
// book legible is undone in the renderer.
const KEY_MAP_INLINE_LIMIT = 3

function formatKeyMapValue(value: unknown, expand: boolean): string {
    const members = keyMapMembers(value)
    if (members) {
        // An empty set is a rule that matches NOTHING (the engine fails closed on an unreadable
        // binding rather than widening it). Saying so beats an empty cell that reads as "any".
        if (members.length === 0) { return 'none' }
        if (expand || members.length <= KEY_MAP_INLINE_LIMIT) { return members.map(String).join(', ') }
        return `${members.length.toLocaleString()} values`
    }
    // Any other DeterminationBinding — starts with / between — already has prose. Reusing it keeps one
    // vocabulary on screen instead of a pricing dialect beside a determination one.
    if (value && typeof value === 'object') { return describeBinding(value) }
    return String(value)
}

/** The accepted values of a set, whether stored as a bare array or as an `in` binding. */
function keyMapMembers(value: unknown): unknown[] | null {
    if (Array.isArray(value)) { return value }
    if (value && typeof value === 'object') {
        const o = value as Record<string, unknown>
        if (o['op'] === 'in') { return Array.isArray(o['values']) ? o['values'] : [] }
    }
    return null
}

// `expand` — AB#3022: a set-valued dimension holds up to a few thousand members. A list cell shows
// the COUNT; the record's own detail field shows them all, because that page is where "which
// ones?" is asked. Defaulted to the compact form so every existing caller is unchanged.
function formatValue(value: unknown, type?: string, customType?: string, fmt?: FormatContext, expand = false): string {
    if (isSentinel(value)) { return '••••' }
    if (value === null || value === undefined || value === '') { return '—' }
    const locale = fmt?.locale
    // AB#1925 — read-mode only: a month-number renders as the month NAME (1 → January … 12 → December).
    // An out-of-range / non-numeric value falls back to the raw number (via the String() default below).
    // AB#1930 — shares the MONTHS source with the edit-mode month picker (one mapping, no drift).
    if (customType === 'month-number') {
        const name = monthName(typeof value === 'number' ? value : Number(value))
        if (name) { return name }
    }
    // AB#3022 — `key-map`: a field holding a JSON OBJECT as a string renders as readable pairs
    // rather than raw JSON. The pricing rule book is the case that forced it: 15,201 rules whose
    // ONLY distinguishing data is their dimension map, which the list could not show without
    // printing {"billTo":"ASE35711"} in a table cell. Two rules that differ only in that map looked
    // IDENTICAL on screen, which is what made the book read worse than the SAP it replaces.
    //
    // Deliberately a display concern and nothing else: the stored value, the parser and every
    // matcher are untouched, so this cannot move a price. Non-object or unparseable JSON falls
    // through to the raw string below — a formatter that throws on unexpected data would blank a
    // column rather than show something imperfect.
    if (customType === 'key-map') {
        const src = typeof value === 'string' ? value : null
        if (src) {
            try {
                const parsed: unknown = JSON.parse(src)
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    const pairs = Object.entries(parsed as Record<string, unknown>)
                    if (pairs.length === 0) { return 'Any' }
                    return pairs
                        .map(([k, v]) => `${k}: ${formatKeyMapValue(v, expand)}`)
                        .join(' · ')
                }
            } catch { /* fall through to the raw string */ }
        }
    }
    // AB#2049 — dates/datetimes format in the tenant locale via a cached Intl.DateTimeFormat when a locale
    // is threaded. With NO locale (the designer preview) they fall back to the previous bare
    // toLocaleDateString()/toLocaleString() so the no-context output is byte-identical (the datetime path in
    // particular keeps its seconds, which a short timeStyle would drop).
    if (type === 'date' && typeof value === 'string') {
        const d = new Date(value)
        if (isNaN(d.getTime())) { return String(value) }
        return locale ? dateFormatter(locale).format(d) : d.toLocaleDateString()
    }
    if (type === 'datetime' && typeof value === 'string') {
        const d = new Date(value)
        if (isNaN(d.getTime())) { return String(value) }
        return locale ? dateTimeFormatter(locale).format(d) : d.toLocaleString()
    }
    // AB#2815 — a string-typed "false" (an authored boolean default) must not print Yes.
    if (type === 'boolean') { return isTruthyValue(value) ? 'Yes' : 'No' }
    // AB#2412 — an enum's stored SCREAMING_SNAKE code is shown as a human label (RUN_OUT → Run Out).
    // Display only: the stored code is untouched, exactly as lookupValue already works.
    if (type === 'enum' && typeof value === 'string') { return formatEnumLabel(value) }
    if ((type === 'number' || type === 'decimal' || type === 'integer') && typeof value === 'number') {
        return numberFormatter(locale).format(value)
    }
    // AB#2049 — a money field renders as localised currency (symbol + locale grouping/decimals) when the
    // tenant's base currency is known; with no currency it falls back to the previous bare 2-dp number.
    if (type === 'money' && typeof value === 'number') {
        return fmt?.currency
            ? moneyFormatter(locale, fmt.currency).format(value)
            : value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    // AB#2776 — COMPOSITE values ({ amount, unit } / { amount, currency }) fell through to String() and
    // rendered as the literal "[object Object]". Seen on a delivery line's Gross Weight, where the material
    // carries { "amount": 1 } with no unit at all: the grid showed "[object Object]" while the SAME value on
    // the detail screen showed an em dash, so two renderers disagreed about one field and neither showed the
    // number that was there.
    //
    // The unit half is deliberately optional. A half-populated composite is exactly the case that produced
    // this, and an amount with no unit is still worth more to a reader than either wrong answer.
    if (typeof value === 'object' && !Array.isArray(value)) {
        const o = value as { amount?: unknown; unit?: unknown; currency?: unknown }
        if (o.amount !== null && o.amount !== undefined && o.amount !== '') {
            const amount = typeof o.amount === 'number'
                ? numberFormatter(locale).format(o.amount)
                : String(o.amount)
            const suffix = typeof o.unit === 'string' && o.unit
                ? o.unit
                : (typeof o.currency === 'string' && o.currency ? o.currency : '')
            return suffix ? `${amount} ${suffix}` : amount
        }
        // Nothing renderable in it. An em dash is honest; "[object Object]" is never the right answer, and
        // a reader cannot tell it from a value that genuinely is one.
        return '—'
    }
    return String(value)
}

// AB#1921 — the server-resolved FK label for a field on a bound record. FK labels are attached per
// record under `__lookups` (keyed by FK field name) by the list (per-row) and the detail GET (per-
// record). Returns the label when present, else undefined so the caller falls back to formatValue.
// Mirrors the ListView cell pattern (`row['__lookups']?.[field]`).
function lookupLabel(record: Record<string, unknown>, field: string | undefined): string | undefined {
    if (!field) { return undefined }
    // AB#1921 — an empty-string (or non-string) label must NOT short-circuit the `??` fallback chain and
    // render blank; only a non-empty string label wins, otherwise fall back to the raw value via formatValue.
    const lbl = (record['__lookups'] as Record<string, unknown> | undefined)?.[field]
    return typeof lbl === 'string' && lbl !== '' ? lbl : undefined
}

// AB#2421 — the server-rendered related-entity roll-up for a column (the __lookups twin, keyed by the
// entity's declared relatedRollups name — e.g. activatedCompanies → "GB01, (IE01)"). Rows without one
// (no related rows, the designer preview, any non-list surface) return undefined and the cell falls
// through the normal lookup/format chain — a roll-up name is not a data key, so that renders blank.
function rollupLabel(record: Record<string, unknown>, field: string | undefined): string | undefined {
    if (!field) { return undefined }
    const lbl = (record['__rollups'] as Record<string, unknown> | undefined)?.[field]
    return typeof lbl === 'string' && lbl !== '' ? lbl : undefined
}

function metaFor(binding: string | undefined, view: ViewDefinition, entityFields: EntityFieldMeta): ViewFieldMeta[] {
    if (!binding) { return [] }
    const root = (binding.startsWith('$') ? binding.slice(1) : binding).split('.')[0]
    const entry = view.context?.find(c => c.name === root)
    return entry ? (entityFields[entry.entityType] ?? []) : []
}

function guidanceField(view: ViewDefinition, binding: string | undefined, field?: string): string | undefined {
    const [root, ...path] = (binding ?? '').replace(/^\$/, '').split('.')
    const entity = view.context?.find(c => c.name === root)?.entityType
    return entity ? [entity, ...path, ...(field ? [field] : [])].join('.') : undefined
}

function guidanceFields(view: ViewDefinition, components: ViewComponent[]): string[] {
    return components.flatMap(component => component.fields?.length
        ? normalizeSlots(component.fields).map(slot => guidanceField(view, component.dataBinding, slot.field))
        : [guidanceField(view, component.dataBinding, component.field)])
        .filter((field): field is string => !!field)
}

function normalizeSlots(fields: Array<string | FieldSlot> | undefined): FieldSlot[] {
    return (fields ?? []).map(f => typeof f === 'string' ? { field: f } : f)
}

// AB#2959 — immutable-after-create: the field is editable while the bound record is UNSAVED (a create
// draft has no id yet — the same test LineItemsSlot's isCreateForm uses) and locked once it exists.
// Promoted to plain readOnly here, where the slot holds both the field's meta and the record, so every
// input control's existing disabled={meta?.readOnly} enforces it unchanged.
function lockImmutableAfterCreate(meta: ViewFieldMeta, record: Record<string, unknown>): ViewFieldMeta
function lockImmutableAfterCreate(meta: ViewFieldMeta | undefined, record: Record<string, unknown>): ViewFieldMeta | undefined
function lockImmutableAfterCreate(meta: ViewFieldMeta | undefined, record: Record<string, unknown>): ViewFieldMeta | undefined {
    return meta?.immutableAfterCreate && typeof record['id'] === 'string' ? { ...meta, readOnly: true } : meta
}

// ── Per-component renderers (read-only) ──────────────────────────────────────

/**
 * AB#3967 — the scope a drill-down carries into its target: the cockpit's active tab scope (company,
 * site, valuation area). Every member is optional and the host serialises only the defined ones. The
 * shared renderer never interprets it — it travels from the `navigationScope` prop to the
 * `onNavigateToProcess` callback untouched, so a host that passes none navigates exactly as before.
 */
export interface DrillDownScope {
    companyId?: string | undefined
    siteId?: string | undefined
    valuationAreaId?: string | undefined
}

interface SlotProps {
    pageHeader?: boolean
    component: ViewComponent
    view: ViewDefinition
    context: ViewContext
    entityFields: EntityFieldMeta
    // AB#2879 — the current user's permission codes. See ViewRendererProps.permissions: without this
    // the renderer evaluated every `permission:CODE` showIf as false and hid the component from
    // everyone. Undefined = not yet known and fails open.
    permissions?: ReadonlySet<string> | undefined
    stateColors?: Record<string, string> | undefined
    // AB#2889 — host navigation callback for a metric tile's declared drillDown (the process name is
    // passed verbatim; the tenant host maps it to /app/view/<process>). Absent (designer preview) =
    // the tile is display-only, exactly as before the drill-down existed.
    // AB#3967 — the optional second argument is the `navigationScope` the host gave this renderer,
    // forwarded by the tile so the target opens narrowed to the same company / site / valuation area.
    onNavigateToProcess?: ((processName: string, scope?: DrillDownScope) => void) | undefined
    navigationScope?: DrillDownScope | undefined
    // AB#1400 — edit mode: when editable, field/input components render controls bound to the draft.
    editable?: boolean | undefined
    draft?: ViewContext | undefined
    onChange?: ((draft: ViewContext) => void) | undefined
    // The active grid's components (display or edit grid) — used to resolve tab-slot children.
    gridComponents: ViewComponent[]
    // AB#3280 — the resolved layout tier, threaded from ViewRenderer so every nested grid/slot
    // agrees with the top level (and with a forced tier from tests / the designer phone preview).
    // Absent = 'wide': every slot's compact branch is strictly opt-in.
    viewport?: ViewportTier | undefined
    // AB#1611 — nesting depth for recursive containers (depth-capped to prevent runaway layouts).
    depth?: number | undefined
    // AB#1839 — host-provided FK option loader + the in-screen company (parity with the tenant renderer).
    fetchEntityOptions?: FetchEntityOptions | undefined
    // AB#2040 — host-provided fetch-by-id label resolver for the searchable FK combobox (off-page label).
    fetchEntityById?: FetchEntityById | undefined
    // AB#2518 — fetches a referenced record's fields so a copyFrom can seed from it.
    fetchEntityRecord?: FetchEntityRecord | undefined
    // AB#2520 — host seam that prices a provisional document line. Absent (designer preview) = no
    // live pricing at all.
    fetchLinePrice?: FetchLinePrice | undefined
    // AB#2266 — host-provided reference-data lookup option loader for lookupValue fields.
    fetchLookupOptions?: FetchLookupOptions | undefined
    // AB#2393/2395 — host-provided unit-of-measure catalogue loader, for the quantity-input unit select.
    fetchUnitOptions?: FetchUnitOptions | undefined
    // AB#2981 — host-provided outbound-connection loader, for the connection-picker editor.
    fetchConnectionOptions?: FetchConnectionOptions | undefined
    // AB#3291 — host-provided user-directory loader, for the user-picker editor.
    fetchUserOptions?: FetchUserOptions | undefined
    // AB#2447 — host-provided audited reveal for a sensitive field's full value. Optional seam:
    // absent (the designer preview) = no reveal affordance.
    fetchRevealField?: FetchRevealField | undefined
    // AB#2767 — host-injected per-field change-history affordance (button + modal). Rendered beside a
    // field's label when its meta declares fieldHistory; the designer injects nothing and shows nothing.
    fieldHistoryComponent?: React.ComponentType<{ entityType: string; recordId: string; field: string; label?: string | undefined }> | undefined
    // AB#2450 — host-provided address lookup (autocomplete + resolve). Optional seam: absent = the
    // address editor is manual-only.
    fetchAddressSearch?: FetchAddressSearch | undefined
    // AB#2451 — host-injected address map for showMap address blocks. Absent = no map.
    addressMapComponent?: AddressMapComponent | undefined
    scopeCompanyId?: string | undefined
    // AB#1866 — seams the host injects (tenant = live; designer = undefined → placeholders / preview).
    customComponent?: CustomSlotComponent | undefined
    documentsComponent?: CustomSlotComponent | undefined
    // AB#2386 — file-attachments is its OWN seam, separate from documentsComponent (which stays on the
    // AB#1395 generated-document / comms tracker). The tenant injects a real upload/download/delete panel
    // over the AB#2021 attachment endpoints; hosts that omit it keep the placeholder.
    attachmentsComponent?: CustomSlotComponent | undefined
    mapComponent?: CustomSlotComponent | undefined
    buttonComponent?: CustomSlotComponent | undefined
    barcodeComponent?: CustomSlotComponent | undefined
    chartComponent?: CustomSlotComponent | undefined
    // AB#2046 — audit-trail / document-flow live surfaces the tenant injects (the change-history panel and
    // the quote→order→delivery→invoice flow). Same documentsComponent-style seam: the tenant wires real
    // implementations, the designer omits them and RenderComponent falls back to PlaceholderSlot.
    auditTrailComponent?: CustomSlotComponent | undefined
    documentFlowComponent?: CustomSlotComponent | undefined
    // AB#2046/2121 — search-panel seam: the tenant now injects a live search surface (SearchPanel, backed by
    // the V3 entities query with a `search` term); the designer (and any host that omits it) keeps the placeholder.
    searchPanelComponent?: CustomSlotComponent | undefined
    // AB#2316 — host-injected live related-entity-list: fetches a child entity's rows scoped to the parent
    // FK and provides modal add/edit (running the child's own save process). The designer/preview omit it,
    // so `related-entity-list` falls back to the read-only, context-bound LineItemsSlot (unchanged).
    relatedListComponent?: CustomSlotComponent | undefined
    // AB#2169 — pricing platform seams (stack-canvas / calc-trace / matrix-grid): the tenant injects its
    // live lazy slots; the designer (and any host that omits them) keeps the placeholder.
    //
    // AB#2688 — the two WRITE-capable seams (stack-canvas, matrix-grid) now receive `editable`, because
    // a slot that can mutate has to know whether the page is in edit mode; without it the stack canvas
    // offered drag-to-reorder on a read-only detail page. The display-only seams (calc-trace,
    // determination-trace) deliberately do NOT get it — there is nothing for them to gate.
    stackCanvasComponent?: CustomSlotComponent | undefined
    calcTraceComponent?: CustomSlotComponent | undefined
    // AB#2679 � the determination decision trace (why THIS site / account / printer was chosen).
    determinationTraceComponent?: CustomSlotComponent | undefined
    matrixGridComponent?: CustomSlotComponent | undefined
    // AB#3063 — module-owned cache stats/Clear card; display+action seam, no `editable` (nothing to gate).
    managedCacheComponent?: CustomSlotComponent | undefined
    // AB#3285 — timeline multi-event mode: the tenant injects the live multi-source feed (fetches the
    // POST /api/v3/timeline union). Only consulted when the component declares sources[]; the legacy
    // single-dateField timeline stays rendered by the shared TimelineSlot in every host.
    timelineComponent?: CustomSlotComponent | undefined
    // AB#3238 — user-calendar: the tenant injects the live agenda (GET /api/calendar/me interleaved
    // with POST /api/v3/agenda). A host without the seam renders the static UserCalendarSlot preview.
    userCalendarComponent?: CustomSlotComponent | undefined
    editFieldComponent?: EditFieldComponent | undefined
    // AB#2640 — host loader for a child grid's create-form PREVIEW rows (the rows the save will
    // seed, read from a defaults record — see ViewComponent.previewFrom). Absent (designer preview,
    // or a host that has not wired it) = no preview; the emptyText still explains.
    fetchPreviewRows?: FetchPreviewRows | undefined
    // AB#1937 — collapsible section-header wiring (set by ComponentGrid only for a section-header band):
    // whether this band's group is collapsed, and the toggle that flips it. Absent for every other slot.
    sectionCollapsed?: boolean | undefined
    onToggleSection?: (() => void) | undefined
    // AB#2049 — tenant locale + base currency for value formatting (dates / numbers / money). Undefined
    // for the designer preview → the previous browser-locale, bare-number output (byte-identical).
    fmt?: FormatContext | undefined
    // AB#2655 — host-provided synchronous company-id → "{code} - {name}" resolver, for the
    // object-header's company meta item. An entity that never declared a companyId FIELD still gets
    // the server-projected column value, which used to render as the raw GUID under the literal
    // label "companyId". Undefined (designer preview) keeps the raw value.
    resolveCompanyLabel?: ((companyId: string) => string | undefined) | undefined
    // AB#3954 — the per-user table layout store (see useTableLayout.ts). The tenant threads its
    // preference-backed store; the designer omits it and every table's layout is in-memory only.
    // Absent AND no ambient TableLayoutStoreProvider = in-memory. Read by the four table primitives
    // (line-items-table, data-table, json-table and, through the host, related-entity-list).
    tableLayoutStore?: TableLayoutStore | undefined
}

const MAX_CONTAINER_DEPTH = 4

// AB#1937 — stable key for a section-header band, used to track collapsed state across renders. Prefers
// the generator-managed `groupKey` (set by generateView/reorganiseView); a hand-placed section-header has
// no groupKey, so its position among the visible components keys it instead.
function sectionKey(component: ViewComponent, index: number): string {
    return component.groupKey ?? `section-${index}`
}

// AB#1937/1955 — packRows (column-aware re-pack) lives in ./packRows so it can be unit-tested in isolation
// (see packRows.test.ts). It keeps same-stored-row components on one render row and tucks short fields
// beside a tall rowSpan component in a free column (the AB#1952 map-card).

// AB#1611/1834 — lays a list of components onto a 24-column grid (matches the designer's validateView
// + generateView GRID_WIDTH), threading edit state + nesting depth.
// Shared by the top-level ViewRenderer and nested containers so layout is identical at any depth.
// AB#1937 — section-header bands are collapsible: each section-header begins a group owning every following
// component up to the next section-header. Clicking a band collapses/expands its group; the band stays
// visible (with a chevron) and the visible components re-flow into contiguous rows so no empty rows remain.
// Components before the first section-header (object-header, status-badge, ungrouped fields) belong to no
// group and always render. State is session-local and defaults to all-expanded (empty collapsed set), so a
// view with no section-headers is unaffected (no group is ever formed, nothing is collapsible).
function ComponentGrid({ components, props, evalData }: { components: ViewComponent[]; props: SlotProps; evalData: ViewContext }) {
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
    const toggle = (key: string) => setCollapsed(prev => {
        const next = new Set(prev)
        if (next.has(key)) { next.delete(key) } else { next.add(key) }
        return next
    })

    // AB#2332 — a component-level showIf may be a string expression or the designer's `{field,eq,…}`
    // object; passing the object to a string-only evaluator threw "t.trim is not a function" and crashed
    // the screen. AB#2774 — evaluateShowIf now takes either form directly, so the showIfToExpr round-trip
    // is gone: normalising to a string had no spelling for `in` and silently degraded a three-way status
    // filter to "status is set".
    // AB#2879 — `permission` is a first-class gate beside showIf, and the user's codes now actually
    // reach the evaluator. Both are HIDE rather than disable, matching ViewAction.permission and
    // ADR-0008's no-upgrade-prompts rule: a control you cannot use is noise, not an advertisement.
    // A component may carry both; it must pass both.
    const visible = components.filter(c =>
        (!c.permission || (props.permissions?.has(c.permission) ?? true))
        && (!c.showIf
            || evaluateShowIf(
                c.showIf,
                asRecord(resolveBinding(c.dataBinding, evalData)),
                props.permissions)))

    // AB#2476 — drop a section-header band whose every member has been filtered out in THIS context, so a
    // labelled box never renders over nothing. The vendor bank-account child modal was the report: its
    // Identity group holds accountNo (system-assigned, no form slot), companyId (hidden) and vendorId (the
    // parent FK, stripped by RelatedEntityListSlot because the modal always knows its parent) — all three
    // gone, the band still drawn.
    //
    // Deliberately a RENDER-time decision, not a generation-time one: suppression is contextual. The same
    // view shows Identity correctly on the standalone create screen, where the parent FK IS offered. It
    // therefore also covers showIf-emptied bands for free.
    //
    // The test is simply "no members follow it", because suppression happens UPSTREAM of this grid:
    // RelatedEntityListSlot strips the parent-FK component out of the array entirely, so an emptied band
    // and a band authored with no members arrive here identical. Both should be silent — a labelled box
    // over nothing is noise either way, and in the designer the author edits the component tree in
    // ViewCanvas while the preview's job is to show what the USER will see. (AB#1915's "Section heading"
    // placeholder is for an untitled band, a different case, and still applies to bands that do render.)
    const bandHasMembers = new Map<ViewComponent, boolean>()
    let currentBand: ViewComponent | undefined
    for (const c of visible) {
        if (c.type === 'section-header') { currentBand = c; bandHasMembers.set(c, false); continue }
        if (currentBand) { bandHasMembers.set(currentBand, true) }
    }
    const emptiedBands = new Set([...bandHasMembers].filter(([, has]) => !has).map(([band]) => band))
    const survivors = emptiedBands.size > 0 ? visible.filter(c => !emptiedBands.has(c)) : visible

    // AB#1937 — build the render list: walk the visible components, dropping the fields of any collapsed
    // group (the section-header band itself always renders), then re-pack the survivors onto contiguous
    // rows. `collapsedFor` lets the section-header band render the right chevron + toggle its own group.
    const rendered: ViewComponent[] = []
    const collapsedFor = new Map<ViewComponent, boolean>()
    const toggleFor = new Map<ViewComponent, () => void>()
    let activeCollapsed = false
    let inSection = false
    let sectionIndex = 0
    // AB#1944 — components that sit UNDER a section-header band are left-indented (pl-4) so they nest
    // under the heading; the object-header, the bands themselves, and any ungrouped fields above the
    // first band stay flush left. `inSection` flips true at the first band and stays true thereafter.
    const grouped = new Set<ViewComponent>()
    for (const component of survivors) {
        if (component.type === 'section-header') {
            const key = sectionKey(component, sectionIndex++)
            const isCollapsed = collapsed.has(key)
            activeCollapsed = isCollapsed
            inSection = true
            collapsedFor.set(component, isCollapsed)
            toggleFor.set(component, () => toggle(key))
            rendered.push(component)
            continue
        }
        if (inSection) { grouped.add(component) }
        if (!activeCollapsed) { rendered.push(component) }
    }

    // AB#3280 — compact (phone) tier: one column, stored source order, packRows bypassed entirely.
    // A 24-column grid squeezed to 375px gives every component ~15px; instead each visible component
    // takes the full width in the order the author stored it (top-to-bottom, left-to-right — the
    // reading order of the wide layout). Section collapse/indent wiring is identical to the wide
    // path, so a collapsed band hides the same components in both tiers. Nested grids (containers,
    // tab slots, repeats) inherit the tier through SlotProps and collapse the same way.
    if (props.view.kind === 'dashboard') {
        // AB#3989 — a tile view has no grid to pack: the host's shell IS the cell, so the components
        // render straight into a flex column that fills it. None of the DashboardGrid vocabulary
        // (.dashboard-layout / -grid / -metrics / -panel) is emitted, so none of a host's module-
        // dashboard CSS can reach in — which is the whole point (the cockpit lost that cascade tie).
        if (isTile(props.view)) {
            return <div className="view-tile">{rendered.map((component, i) => (
                <RenderComponent key={i} {...props} component={component}
                    sectionCollapsed={collapsedFor.get(component)} onToggleSection={toggleFor.get(component)} />
            ))}</div>
        }
        return <DashboardGrid components={rendered} compact={props.viewport === 'compact'} render={component => (
            <RenderComponent {...props} component={component}
                sectionCollapsed={collapsedFor.get(component)} onToggleSection={toggleFor.get(component)} />
        )} />
    }
    if (props.viewport === 'compact') {
        return (
            <div className="grid grid-cols-1 gap-y-2" data-viewport="compact">
                {rendered.map((component, i) => (
                    <div key={i} className={`min-w-0 ${grouped.has(component) ? 'pl-4' : ''}`}>
                        <RenderComponent {...props} component={component}
                            sectionCollapsed={collapsedFor.get(component)}
                            onToggleSection={toggleFor.get(component)} />
                    </div>
                ))}
            </div>
        )
    }

    const placed = packRows(rendered)

    return (
        <div className="grid gap-x-3 gap-y-2" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
            {placed.map(({ component, row, col }, i) => (
                <div key={i} className={grouped.has(component) ? 'pl-4' : undefined} style={{ gridColumn: `${col} / span ${component.colSpan}`, gridRow: `${row} / span ${component.rowSpan}` }}>
                    <RenderComponent {...props} component={component}
                        sectionCollapsed={collapsedFor.get(component)}
                        onToggleSection={toggleFor.get(component)} />
                </div>
            ))}
        </div>
    )
}

// AB#1611 — general recursive container: a (optionally collapsible) titled box that renders its
// child components on a nested grid. Children inherit the container's dataBinding unless they
// declare their own (binding cascade). Depth-capped.
function ContainerSlot(props: SlotProps) {
    const { component, editable, draft, context } = props
    const depth = props.depth ?? 0
    const [collapsed, setCollapsed] = useState(!!component.defaultCollapsed)
    const evalData = editable && draft ? draft! : context
    const variant = component.variant ?? 'section'

    if (depth >= MAX_CONTAINER_DEPTH) {
        return <div className="text-xs text-red-500 italic">Container nesting too deep</div>
    }

    const childProps: SlotProps = { ...props, depth: depth + 1 }

    let body: ReactNode
    if (component.repeatBinding) {
        // AB#1861 — repeating container: render the children once per element of the bound collection,
        // each element re-scoped as `$item` so a child's field resolves against the element.
        const raw = resolveBinding(component.repeatBinding, evalData)
        const rows: unknown[] = Array.isArray(raw) ? raw : []
        const itemChildren = (component.children ?? []).map(c => c.dataBinding ? c : { ...c, dataBinding: '$item' })
        body = rows.length > 0
            ? <div className="space-y-4">{rows.map((row, i) =>
                <ComponentGrid key={i} components={itemChildren} props={childProps} evalData={{ ...evalData, item: row }} />)}</div>
            : <div className="text-xs text-neutral-400 dark:text-slate-500 italic">No items</div>
    } else {
        // Binding cascade: a child with no dataBinding inherits the container's (when it has one).
        const inherited = component.dataBinding
        const children = (component.children ?? []).map(c =>
            (c.dataBinding || !inherited) ? c : { ...c, dataBinding: inherited })
        body = children.length > 0
            ? <ComponentGrid components={children} props={childProps} evalData={evalData} />
            : <div className="text-xs text-neutral-400 dark:text-slate-500 italic">Empty container</div>
    }

    if (variant === 'plain') {
        return collapsed ? <></> : body
    }

    const wrapCls = variant === 'card'
        ? 'bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-4'
        : 'rounded-lg border border-neutral-200 dark:border-slate-700 bg-neutral-50/40 dark:bg-slate-900/40 p-4'

    return (
        <div className={wrapCls}>
            {(component.title || component.collapsible) && (
                <div className="flex items-center justify-between mb-3">
                    {component.title && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200">{component.title}</h3>}
                    {component.collapsible && (
                        <button type="button" onClick={() => setCollapsed(c => !c)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800">
                            {collapsed ? 'Show' : 'Hide'}
                        </button>
                    )}
                </div>
            )}
            {!collapsed && body}
        </div>
    )
}

// AB#1400 — immutably set a field on the record at a (single-level) binding within the draft context.
function setBindingField(draft: ViewContext, binding: string | undefined, field: string, value: unknown): ViewContext {
    const root = binding ? (binding.startsWith('$') ? binding.slice(1) : binding).split('.')[0] : undefined
    // AB#3471 — view-save merges a patch into stored data. An explicit clear must
    // travel as JSON null; deleting the key tells persist to KEEP the old value.
    // Some structured editors emit undefined on clear. Untouched fields never call
    // this setter, so they remain absent without discarding an explicit edit.
    const nextValue = value === '' || value === null || value === undefined ? null : value
    if (!root) {
        const next = { ...draft }
        next[field] = nextValue
        return next
    }
    const rec = { ...asRecord(draft[root]) }
    rec[field] = nextValue
    return { ...draft, [root]: rec }
}

// AB#1839 — FK option loader, host-provided (the designer preview wires its data source). The
// companyId argument re-scopes company-restricted options, matching the tenant renderer.
// AB#2040 — `searchTerm` is the searchable FK typeahead's server-side search term (the loader passes it
// to the entity query's `search` param so the FULL referenced set is reachable, not just the first page).
// It is the LAST argument and optional, so it is backward compatible: a host that ignores it (the
// designer preview's static loader) keeps working unchanged (it just returns its static page).
// AB#2447 — the audited reveal: POSTs the reveal endpoint and returns the field's full value.
export type FetchRevealField = (
    entityType: string,
    recordId: string,
    field: string,
) => Promise<unknown>

// AB#2450 — the address lookup seam: type-ahead suggestions + resolve-to-composite, backed by the
// tenant's server-side proxy. Optional — absent (designer preview / no server key) the address
// editor is manual-only, which always remains possible regardless.
// AB#2467 — the address seams moved to ./AddressEditor with the editor they serve; re-exported here
// so every existing importer of these types is unaffected.
export type { FetchAddressSearch, AddressMapComponent } from './AddressEditor'

export type FetchEntityOptions = (
    sourceEntity: string,
    displayField: string | undefined,
    valueField: string | undefined,
    // AB#1866 — cascading FK filter (tenant runtime); the shared preview loader passes undefined.
    filterBy?: { field: string; value: unknown },
    companyId?: string,
    searchTerm?: string,
    // AB#2435 — company-activation narrowing: the declared lookupField.companyFilter, sent as a
    // rollupFilters EXISTS on the option query when a scope company is present.
    companyFilter?: { rollup: string },
    // AB#2485 — the declared lookupField.optionFilters: field/value pairs the option query filters on,
    // so a picker offers only records that are VALID for the field. Last and optional, so hosts that
    // ignore it (the designer preview) keep working unchanged.
    optionFilters?: Record<string, string | number | boolean>,
) => Promise<{ value: string; label: string; id?: string }[]>

// AB#2040 — resolves a SINGLE FK record's display label by its id, host-provided. The searchable
// combobox uses it so the collapsed control shows the current selection's label even when that record
// is NOT in the current search page (fetch-by-id fallback). Optional on EditFieldProps — a host that
// doesn't wire it (or a value it can't resolve) falls back to showing the raw value, never a blank.
// AB#2428 — `valueField` is the lookup's declared value field: when it names a data field (not 'id')
// the stored value is that field's value (a CODE, not a record id), so the host must resolve the
// label by that field instead of fetch-by-id. LAST argument and optional, so existing hosts that
// ignore it (the designer preview) keep working unchanged.
export type FetchEntityById = (
    sourceEntity: string,
    displayField: string | undefined,
    id: string,
    companyId?: string,
    valueField?: string,
) => Promise<string | undefined>

// AB#2266 — reference-data lookup option loader, host-provided (the tenant wires
// GET /api/v3/lookups/{lookupType}; active + company-visible filtering is server-side). The shared
// select sorts client-side (sortOrder when the DTO carries it, else label) so every host orders
// identically, and options are cached per loader identity + lookupType + companyId (loadLookupOptions)
// so a render tree with many lookupValue fields of one type fetches once.
// AB#3281 — `tone` is the value's declared semantic status word, carried all the way from the module's
// lookups/{key}.json. Optional so a host on an older API shape still type-checks; unknown words are
// ignored by isStatusTone at the point of use, exactly as an enum option's tone is.
export type FetchLookupOptions = (
    lookupType: string,
    companyId?: string,
) => Promise<{ code: string; label: string; sortOrder?: number; tone?: string | null }[]>

// AB#2640 — loads a child grid's create-form PREVIEW rows: the `collection` child rows of the
// (company-scoped) `entity` record whose `keyField` equals `keyValue`. The host resolves the company
// the same way it scopes pickers; no matching record resolves to an empty array, never an error.
export type FetchPreviewRows = (
    entity: string,
    keyField: string,
    keyValue: string,
    collection: string,
    companyId?: string,
) => Promise<Record<string, unknown>[]>

// AB#2393/2395 — the tenant's unit-of-measure catalogue, for the quantity-input unit select. Cached per
// loader identity like the lookup options, so a screen with six quantity fields fetches the list once.
export type FetchUnitOptions = () => Promise<{ code: string; name?: string; dimension?: string }[]>

// AB#2981 — the tenant's configured outbound connections (Settings > Integrations), for
// `connection-picker`. A host seam like FetchUnitOptions rather than a fetch inside the renderer:
// @marque/ui is shared by the tenant app and the designer, and the designer has no tenant to ask —
// absent simply means no options, which is the same posture every other loader here takes.
export type FetchConnectionOptions = () => Promise<{ name: string; kind?: string; active?: boolean }[]>

// AB#3291 — the tenant's user directory (id + displayName, both populations), for `user-picker`.
// Same host-seam posture as the loaders above: the designer has no tenant to ask, and an absent
// loader simply means no options.
export type FetchUserOptions = () => Promise<{ id: string; displayName: string }[]>


export interface EditFieldProps {
    value: unknown
    meta: ViewFieldMeta | undefined
    multiline?: boolean | undefined
    onChange: (v: unknown) => void
    // AB#1839 — when set (with a lookupField meta), the FK field renders as a searchable select; the
    // option set re-fetches when scopeCompanyId changes (reactive company re-scoping in the preview).
    fetchEntityOptions?: FetchEntityOptions | undefined
    // AB#2040 — resolves the current FK value's label off-page (fetch-by-id fallback) so the searchable
    // combobox shows the selection's label even when its record isn't in the current search page.
    fetchEntityById?: FetchEntityById | undefined
    // AB#2266 — when set (with a type:lookupValue meta carrying lookupType), the field renders as a
    // select of the lookup type's active values (code stored, label shown). Absent (the designer
    // preview) → the field falls through to the plain text input, so editing is never blocked.
    fetchLookupOptions?: FetchLookupOptions | undefined
    scopeCompanyId?: string | undefined
    // AB#1866 — extras the tenant-injected EditField consumes (the shared preview EditField ignores them):
    // a standalone FK lookup, and a cascading filter narrowing the option set.
    lookupField?: ViewFieldMeta['lookupField'] | undefined
    lookupFilterBy?: { field: string; value: unknown } | undefined
    // AB#2652/AB#2654 — the resolved value of the field's `metaOptions.field` driver, read off the
    // record (a child cell reads its own row first, then the parent record). The entity whose
    // statuses / field names become the option set. Undefined (driver blank, or a call site that
    // does not resolve it) falls through to the plain text input.
    metaOptionsEntity?: string | undefined
    quantityUnit?: unknown
    quantityUnitReference?: { entity: string; field: string; id: string } | undefined
    /**
     * AB#2543 — reports the record the user picked, so a host can keep its id alongside the submitted
     * value ("carry both"): a code-valued picker submits a CODE, and the id it came from was previously
     * projected away by the loader. Advisory — `onChange` stays authoritative for the stored value, and
     * a host MUST discard a reported pick once the field's value no longer equals the reported `value`.
     */
    onPick?: ((picked: { value: string; label: string; id?: string } | undefined) => void) | undefined
}

// AB#1866 — renderer seams: the tenant injects live implementations of the data-coupled pieces; the
// designer omits them and gets the shared placeholders / preview EditField.
// AB#2568 — `draft` is the LIVE edit draft, forwarded only to the related-list seam: its prefill has to
// react to driver fields (document type, customer) as the user picks them, and `context` is the static
// open-time snapshot. Optional, so every existing injected slot remains assignable unchanged.
export type CustomSlotComponent = ComponentType<{ component: ViewComponent; view: ViewDefinition; context: ViewContext; draft?: ViewContext | undefined; editable?: boolean | undefined; onChange?: ((draft: ViewContext) => void) | undefined }>
export type EditFieldComponent = ComponentType<EditFieldProps>

// AB#1839/2040 — renders an FK field as a SEARCHABLE typeahead over the referenced entity (id stored,
// display shown). AB#2040 replaced the old plain <select> — which was fed by ONE options fetch the host
// capped at 200 rows, silently hiding rows 201+ — with a debounced server-side search through the host
// loader, so the full referenced set is always reachable no matter how large it is.
// AB#2043 — the cascading lookupFilterBy (AB#1866, formerly only in the tenant's forked LookupRefField)
// threads through here too, narrowing the search when a sibling field scopes it (e.g. product filtered by
// the chosen category). Hosts whose loaders ignore filterBy / the search term (the designer preview) are
// unaffected — they simply return their static page and the typeahead filters nothing server-side.
function LookupRefField({ value, meta, onChange, onPick, fetchEntityOptions, fetchEntityById, scopeCompanyId, lookupFilterBy }: EditFieldProps) {
    const lookup = meta!.lookupField!
    const source = lookup.sourceEntity
    const displayField = lookup.displayField
    const valueField = lookup.valueField
    // Destructured so the callbacks re-create on VALUE change, not on the (per-render) object identity.
    const filterField = lookupFilterBy?.field
    const filterValue = lookupFilterBy?.value
    // AB#2435 — destructured to the rollup NAME for the same dependency-identity reason.
    const companyFilterRollup = lookup.companyFilter?.rollup
    // AB#2485 — serialised for the same dependency-identity reason: the declaration is a fresh object
    // every render, so depending on it directly would rebuild the loader (and refetch) on every keystroke.
    const optionFiltersKey = lookup.optionFilters ? JSON.stringify(lookup.optionFilters) : undefined

    // AB#2040 — server-side search through the host loader. `searchTerm` is the last (backward-compatible)
    // loader arg; the designer preview's static loader ignores it and returns its page unchanged.
    const loadOptions = useCallback((searchTerm: string) =>
        fetchEntityOptions!(source, displayField, valueField,
            filterField !== undefined ? { field: filterField, value: filterValue } : undefined,
            scopeCompanyId, searchTerm,
            companyFilterRollup !== undefined ? { rollup: companyFilterRollup } : undefined,
            optionFiltersKey !== undefined
                ? JSON.parse(optionFiltersKey) as Record<string, string | number | boolean>
                : undefined),
    [fetchEntityOptions, source, displayField, valueField, filterField, filterValue, scopeCompanyId, companyFilterRollup, optionFiltersKey])

    // AB#2040 — fetch-by-id fallback for the collapsed label when the selected record isn't in the page.
    // AB#2428 — valueField rides along so a code-valued lookup resolves its label by the code field.
    const resolveLabel = useMemo(() => fetchEntityById
        ? (id: string) => fetchEntityById(source, displayField, id, scopeCompanyId, valueField)
        : undefined,
    [fetchEntityById, source, displayField, scopeCompanyId, valueField])

    // AB#2598 — the create affordance appears only when the FIELD declares it AND the host supplies a
    // handler. Both halves are required on purpose: the declaration says the affordance belongs here, the
    // host says it is possible here. The designer preview supplies no handler and so shows no create row,
    // which is correct — it cannot create anything.
    const createHandler = useLookupCreate()
    const onCreate = useMemo(() => lookup.allowCreate && createHandler && !meta?.readOnly
        ? () => createHandler({
            sourceEntity: source,
            companyId: scopeCompanyId,
            optionFilters: optionFiltersKey !== undefined
                ? JSON.parse(optionFiltersKey) as Record<string, string | number | boolean>
                : undefined,
        })
        : undefined,
    [lookup.allowCreate, createHandler, meta?.readOnly, source, scopeCompanyId, optionFiltersKey])

    return (
        <AsyncLookupCombobox
            value={String(value ?? '')}
            onChange={onChange}
            onPick={onPick}
            loadOptions={loadOptions}
            resolveLabel={resolveLabel}
            disabled={meta?.readOnly}
            required={meta?.required}
            placeholder={`Search ${source}…`}
            ariaLabel={meta?.label ?? meta?.name}
            onCreate={onCreate}
            // AB#3039 — declared on the FIELD, unlike onCreate which also needs a host handler: accepting
            // the typed value needs nothing from the host, so the declaration alone is enough and it
            // works identically in the designer preview.
            allowFreeText={lookup.allowFreeText}
        />
    )
}

// AB#2266 — a lookup type's loaded (sorted) option set: code is the stored value, label the display.
// AB#3281 — `tone` is the value's declared semantic status word, or absent/null when untoned.
interface LookupValueOption { code: string; label: string; sortOrder?: number | undefined; tone?: string | null | undefined }

// AB#2266 — lookup options cached per loader identity (≈ per host render tree — hosts pass a stable
// loader) keyed by lookupType + companyId, so a view with many lookupValue fields of one type fetches
// once. A WeakMap keyed on the loader keeps host/test instances isolated with no explicit invalidation;
// a FAILED fetch is evicted so a later mount retries rather than caching the outage.
const lookupOptionsCache = new WeakMap<FetchLookupOptions, Map<string, Promise<LookupValueOption[]>>>()

function loadLookupOptions(
    fetchLookupOptions: FetchLookupOptions,
    lookupType: string,
    companyId: string | undefined,
): Promise<LookupValueOption[]> {
    let cache = lookupOptionsCache.get(fetchLookupOptions)
    if (!cache) { cache = new Map(); lookupOptionsCache.set(fetchLookupOptions, cache) }
    const byKey = cache
    const key = `${lookupType}|${companyId ?? ''}`
    let promise = byKey.get(key)
    if (!promise) {
        // sortOrder orders when the DTO carries it; label breaks ties (and orders when it does not).
        promise = fetchLookupOptions(lookupType, companyId).then(opts =>
            [...opts].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label)))
        promise.catch(() => byKey.delete(key))
        byKey.set(key, promise)
    }
    return promise
}

// AB#2266 — subscribe to a lookup type's (cached) options. `options` stays undefined while loading;
// `failed` flips true when the fetch rejects — the callers then fall back to code text / a free-text
// input, because a lookup outage must never block reading or editing the record.
function useLookupOptions(
    lookupType: string | undefined,
    fetchLookupOptions: FetchLookupOptions | undefined,
    companyId: string | undefined,
): { options: LookupValueOption[] | undefined; failed: boolean } {
    const [state, setState] = useState<{ options: LookupValueOption[] | undefined; failed: boolean }>({ options: undefined, failed: false })
    useEffect(() => {
        if (!lookupType || !fetchLookupOptions) { return }
        let ignore = false
        loadLookupOptions(fetchLookupOptions, lookupType, companyId)
            .then(opts => { if (!ignore) { setState({ options: opts, failed: false }) } })
            .catch(() => { if (!ignore) { setState({ options: undefined, failed: true }) } })
        return () => { ignore = true }
    }, [lookupType, fetchLookupOptions, companyId])
    return state
}

// AB#2439 — the ONE edit-control class. Tailwind v4 preflight gives form controls `color: inherit;
// background-color: transparent`, so without explicit text/bg classes the value text follows the
// page's base colour, not the control's theme. Exported: the tenant's CalendarRefField (its only
// data-coupled control) uses it too, so the string cannot drift across the three copies again.
export { V3_INPUT_CLS } from './controlStyles'

// AB#2266 — a reference-data lookupValue field renders as a select of the lookup type's active values:
// the option CODE is stored (what the import pipeline validates and stores verbatim), the label shown.
// Loading → a disabled select with a placeholder; a FAILED fetch falls back to a free-text input so a
// lookup outage never blocks editing. A stored code outside the loaded set is kept as a fallback option
// (mirrors the currency select) so existing data is never silently lost, and the blank option mirrors
// the enum select (clearing a value is save-validation's concern, not the control's).
function LookupValueField({ value, meta, onChange, fetchLookupOptions, scopeCompanyId }: EditFieldProps) {
    const { options, failed } = useLookupOptions(meta!.lookupType, fetchLookupOptions, scopeCompanyId)
    const cls = V3_INPUT_CLS
    const cur = value === null || value === undefined ? '' : String(value)
    if (failed) {
        return <input type="text" value={cur} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls} />
    }
    if (options === undefined) {
        return (
            <select value="" disabled className={cls}>
                <option value="">Loading…</option>
            </select>
        )
    }
    const known = options.some(o => o.code === cur)
    return (
        <select value={cur} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls}>
            <option value="">—</option>
            {cur && !known && <option value={cur}>{cur}</option>}
            {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
    )
}

// AB#2266 — read-mode display for a lookupValue field: the stored CODE resolves to its label once the
// (cached) options load; until then — and on a failed fetch or a code outside the set — the code
// itself renders, so the cell is never blank and never blocked on the lookup fetch.
//
// AB#3281 — when the matched option declares a semantic tone, the label renders as the SAME toned pill
// an enum option's tone produces (resolveOptionTone → tonePillClasses). A lookup-backed status and an
// enum-backed one are the same thing to a user, and until this the first was the only one that could not
// be coloured — the tone was authored, accepted by the gate, and then had nowhere to go. Untoned values
// and every pre-load / failed-fetch / unknown-code path keep the plain text byte-for-byte, and the text
// is always kept, so colour is never the only signal.
function LookupValueText({ value, lookupType, fetchLookupOptions, scopeCompanyId }: {
    value: unknown
    lookupType: string
    fetchLookupOptions: FetchLookupOptions | undefined
    scopeCompanyId: string | undefined
}): ReactNode {
    const { options } = useLookupOptions(lookupType, fetchLookupOptions, scopeCompanyId)
    if (value === null || value === undefined || value === '') { return '—' }
    const code = String(value)
    const option = options?.find(o => o.code === code)
    const text = option?.label ?? code
    const tone = isStatusTone(option?.tone) ? option.tone : undefined
    return tone
        ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tonePillClasses[tone]}`}>{text}</span>
        : text
}

// AB#1878 — a chip editor for `tags` fields: the value normalises to a string[] (accepts an existing
// array or a legacy comma-separated string), each tag is a removable chip, a tag is added on
// Enter/comma, and Backspace on an empty input removes the last chip. (AB#2043 — ported up from the
// tenant fork so the designer preview edits tags exactly like the tenant runtime.)
function TagsEditor({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
    const tags = Array.isArray(value)
        ? value.map(String)
        : (typeof value === 'string' && value ? value.split(',').map(s => s.trim()).filter(Boolean) : [])
    const [input, setInput] = useState('')
    const add = (t: string) => { const v = t.trim(); if (v && !tags.includes(v)) { onChange([...tags, v]) } setInput('') }
    const remove = (t: string) => onChange(tags.filter(x => x !== t))
    return (
        <div className="w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-neutral-900 dark:text-slate-100 px-1.5 py-1 text-sm flex flex-wrap gap-1 items-center focus-within:ring-1 focus-within:ring-blue-500">
            {tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 rounded bg-neutral-100 dark:bg-slate-700 dark:text-slate-200 px-1.5 py-0.5 text-xs">
                    {t}
                    <button type="button" onClick={() => remove(t)} className="text-neutral-400 dark:text-slate-500 hover:text-red-500" aria-label={`Remove ${t}`}>×</button>
                </span>
            ))}
            <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ',') && input.trim()) { e.preventDefault(); add(input) }
                    else if (e.key === 'Backspace' && !input && tags.length > 0) { remove(tags[tags.length - 1]!) }
                }}
                onBlur={() => { if (input.trim()) { add(input) } }}
                placeholder={tags.length === 0 ? 'Add tag…' : ''}
                className="marque-input flex-1 min-w-[60px] border-0 p-0.5 text-sm focus:outline-none focus:ring-0"
            />
        </div>
    )
}

// AB#2652/AB#2654 — the metadata-sourced options control: a select (or chip multi-select storing the
// comma-joined set) over the driving entity's statuses or field names. The option members reuse the
// enum branch's "CODE:Label wins, else humanise" parsing so a statuses list renders exactly like the
// enum it came from. A failed metadata fetch falls back to the free-text input — an outage must never
// block editing the record.
function MetaOptionsField(props: EditFieldProps) {
    const { value, meta, onChange, metaOptionsEntity } = props
    const spec = meta!.metaOptions!
    const fetchEntityMeta = useEntityMeta()
    const { options, failed } = useEntityMetaOptions(metaOptionsEntity, fetchEntityMeta)
    const cls = V3_INPUT_CLS
    const members = useMemo(() => {
        if (!options) { return undefined }
        if (spec.kind === 'fields') {
            return options.fields.map(f => ({ val: f.name, label: f.label || f.name }))
        }
        // AB#2913 — statusValues are the entity's own status field values, so they normalise the same
        // way as any other enum option set.
        return enumOptions(options.statusValues).map(o => ({ val: o.value, label: o.label }))
    }, [options, spec.kind])
    if (failed) {
        return <input type="text" value={String(value ?? '')} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls} />
    }
    if (!spec.multi) {
        const current = String(value ?? '')
        // A stored value the entity no longer offers stays selectable — hiding it would silently
        // rewrite the record on the next save.
        const stale = current && !(members ?? []).some(m => m.val === current)
        return (
            <select value={current} disabled={meta?.readOnly || !members} onChange={e => onChange(e.target.value)} className={cls}>
                <option value="">—</option>
                {stale ? <option value={current}>{current}</option> : null}
                {(members ?? []).map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
            </select>
        )
    }
    const selected = String(value ?? '').split(',').map(s => s.trim()).filter(Boolean)
    const remaining = (members ?? []).filter(m => !selected.includes(m.val))
    const labelFor = (v: string) => (members ?? []).find(m => m.val === v)?.label ?? v
    return (
        <div className="w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-neutral-900 dark:text-slate-100 px-1.5 py-1 text-sm flex flex-wrap gap-1 items-center focus-within:ring-1 focus-within:ring-blue-500">
            {selected.map(v => (
                <span key={v} className="inline-flex items-center gap-1 rounded bg-neutral-100 dark:bg-slate-700 dark:text-slate-200 px-1.5 py-0.5 text-xs">
                    {labelFor(v)}
                    {meta?.readOnly ? null : (
                        <button type="button" onClick={() => onChange(selected.filter(x => x !== v).join(','))} className="text-neutral-400 dark:text-slate-500 hover:text-red-500" aria-label={`Remove ${labelFor(v)}`}>×</button>
                    )}
                </span>
            ))}
            <select
                value=""
                disabled={meta?.readOnly || !members || remaining.length === 0}
                onChange={e => { if (e.target.value) { onChange([...selected, e.target.value].join(',')) } }}
                className="marque-input flex-1 min-w-[80px] border-0 bg-transparent p-0.5 text-sm focus:outline-none focus:ring-0"
            >
                <option value="">{!members ? '…' : remaining.length === 0 ? (selected.length === 0 ? 'No options' : '') : 'Add…'}</option>
                {remaining.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
            </select>
        </div>
    )
}

// AB#2043 — exported (and consumed by the tenant's injected EditField, which delegates everything except
// its data-coupled calendar-ref picker here) so the type→control mapping — FK lookup / boolean / enum /
// currency / month-number / tags chips / image URL / multiline / typed input — has exactly one home and
// cannot drift between the tenant runtime and the designer preview again.
function quantityReference(meta: ViewFieldMeta | undefined, fields: ViewFieldMeta[], record: Record<string, unknown>) {
    const parts = meta?.quantityUnitField?.split('.')
    if (!parts || parts.length !== 2) { return undefined }
    const source = fields.find(f => f.name === parts[0])?.lookupField?.sourceEntity
    const id = record[parts[0]!]
    return source && typeof id === 'string' && id ? { entity: source, field: parts[1]!, id } : undefined
}

export function EditField(props: EditFieldProps) {
    const reference = props.quantityUnitReference
    const [resolvedQuantityUnit, setResolvedQuantityUnit] = useState<{ key: string; unit: string | undefined }>()
    const referenceKey = reference ? `${reference.entity}/${reference.id}/${reference.field}/${props.scopeCompanyId ?? ''}` : ''
    useEffect(() => {
        let cancelled = false
        if (reference && props.fetchEntityById) {
            void props.fetchEntityById(reference.entity, reference.field, reference.id, props.scopeCompanyId, 'id')
                .then(unit => { if (!cancelled) { setResolvedQuantityUnit({ key: referenceKey, unit }) } })
                .catch(() => { if (!cancelled) { setResolvedQuantityUnit({ key: referenceKey, unit: undefined }) } })
        }
        return () => { cancelled = true }
    }, [reference?.entity, reference?.field, reference?.id, referenceKey, props.fetchEntityById, props.scopeCompanyId])
    // A referenced unit comes from the scoped lookup, never a stale or supplied dotted record value.
    const quantityUnit = reference
        ? (resolvedQuantityUnit?.key === referenceKey ? resolvedQuantityUnit.unit : undefined)
        : props.quantityUnit
    const { value, meta, multiline, onChange, fetchEntityOptions, fetchLookupOptions } = props
    const type = meta?.type
    const compact = useViewport() === 'compact'
    const cls = compact ? `${V3_INPUT_CLS} !text-base min-h-11` : V3_INPUT_CLS
    // AB#2610 — the tenant's UOM catalogue, or undefined when the host wired none (designer preview).
    const units = useUnitCatalogue()
    // AB#2652/AB#2654 — the host's entity-metadata loader, or undefined when the host wired none.
    const fetchEntityMeta = useEntityMeta()

    // AB#3549 — sensitive strings use a masked editor in every generated host.
    if (type === 'string' && meta?.sensitive) {
        return <input type="password" autoComplete="new-password" aria-label={meta.label ?? meta.name}
            value={String(value ?? '')} disabled={meta.readOnly} maxLength={meta.facets?.maxLength}
            onChange={e => onChange(e.target.value)} className={cls} />
    }
    // AB#1839 — an FK/relationship field renders as a searchable select when the host wired a loader.
    if (meta?.lookupField && fetchEntityOptions) {
        return <LookupRefField {...props} />
    }
    // AB#2266 — a reference-data lookupValue field renders as a value select (code stored, label shown)
    // when the host wired a loader; without one (the designer preview) it falls through to the plain
    // text input below, so editing is never blocked.
    if (type === 'lookupValue' && meta?.lookupType && fetchLookupOptions) {
        return <LookupValueField {...props} />
    }
    // AB#2652/AB#2654 — a metadata-sourced options field renders a select (or chip multi-select) of
    // the driving entity's statuses / field names, when the host wired the seam AND the call site
    // resolved the driving value. Either absent (the designer preview; a blank driver) falls through
    // to the plain text input, so editing is never blocked.
    if (meta?.metaOptions && fetchEntityMeta && props.metaOptionsEntity) {
        return <MetaOptionsField {...props} />
    }
    if (type === 'boolean') {
        return <input type="checkbox" aria-label={meta?.label ?? meta?.name} checked={isTruthyValue(value)} disabled={meta?.readOnly} onChange={e => onChange(e.target.checked)} className="h-4 w-4 rounded border-neutral-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" />
    }
    if ((type === 'enum' || (meta?.enum && meta.enum.length > 0))) {
        // AB#2913 — a declared label wins, otherwise the code is humanised. Shared with the header
        // status select and the filter bar, replacing the colon parsing all three used to duplicate.
        const members = enumOptions(meta?.enum).map(o => ({ val: o.value, label: o.label }))
        // AB#2429 — meta.radio renders the members as a radio group (the FBL-style mode choice): every
        // option visible at once, one click to pick. An optional field gets an explicit "—" (none)
        // member, since a radio offers no blank row the way the select does.
        if (meta?.radio) {
            const groupName = `radio-${meta.name}`
            const current = String(value ?? '')
            return (
                <div role="radiogroup" className="flex flex-wrap gap-x-4 gap-y-1.5 py-1">
                    {(meta.required ? members : [{ val: '', label: '—' }, ...members]).map(m => (
                        <label key={m.val} className={`flex items-center gap-1.5 text-sm ${meta.readOnly ? 'text-neutral-400 dark:text-slate-500' : 'text-neutral-700 dark:text-slate-300 cursor-pointer'}`}>
                            <input
                                type="radio"
                                name={groupName}
                                value={m.val}
                                checked={current === m.val}
                                disabled={meta.readOnly}
                                onChange={() => onChange(m.val)}
                                className="h-3.5 w-3.5 border-neutral-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                            />
                            {m.label}
                        </label>
                    ))}
                </div>
            )
        }
        return (
            <select aria-label={meta?.label ?? meta?.name} value={String(value ?? '')} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls}>
                <option value="">—</option>
                {members.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
            </select>
        )
    }
    // AB#1910 — a currency-type field is a select of ISO codes (a stored value outside the list is kept).
    // AB#2516 — the OPTION reads "GBP (£)"; the stored value is still the bare code. A code we don't
    // ship keeps its own label, so an unlisted stored value is never relabelled or hidden.
    if (type === 'currency') {
        const cur = typeof value === 'string' ? value : ''
        const opts = cur && !CURRENCIES.includes(cur) ? [cur, ...CURRENCIES] : CURRENCIES
        return (
            <select value={cur} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls}>
                <option value="">—</option>
                {opts.map(c => <option key={c} value={c}>{currencyLabel(c)}</option>)}
            </select>
        )
    }
    // AB#2610 — a unit-type field is a select over the TENANT's units-of-measure catalogue. Currency
    // above could import its list; this one is tenant data, so it arrives by context and the field falls
    // through to the plain text input when the host wired none (the designer preview) or the fetch has
    // not resolved — editing is never blocked by a missing catalogue.
    //
    // Same two rules the currency select follows, and for the same reasons: the STORED VALUE IS THE BARE
    // CODE, and a stored value outside the catalogue is kept as its own option, so a unit that has since
    // been deactivated (or predates the catalogue) is never silently relabelled, blanked or dropped from
    // a historic record.
    if (type === 'unit' && units && units.length > 0) {
        const cur = typeof value === 'string' ? value : ''
        const known = units.some(u => u.code === cur)
        return (
            <select value={cur} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls}>
                <option value="">—</option>
                {cur && !known && <option value={cur}>{cur}</option>}
                {units.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </select>
        )
    }
    // AB#2197 — a country-code field is a select of ISO 3166 countries whose option VALUES are the
    // alpha-2 codes. Free text let a typo silently break the Company Finance Profile's country-driven
    // tax-scheme default. A stored value outside the list is kept as a fallback option (as the currency
    // select does) so existing data still renders and stays valid.
    if (meta?.customType === 'country-code') {
        const cur = typeof value === 'string' ? value : ''
        const known = COUNTRIES.some(c => c.code === cur)
        return (
            <select value={cur} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls}>
                <option value="">—</option>
                {cur && !known && <option value={cur}>{cur}</option>}
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
        )
    }
    // AB#1930 — a month-number field is a January..December select whose option VALUES are 1..12. The
    // stored value's shape (int vs numeric string) is preserved on change, and a stored value outside
    // 1..12 is kept as a fallback option (mirrors the currency select) so data is never silently lost.
    if (meta?.customType === 'month-number') {
        const numericString = typeof value === 'string'
        const cur = value === null || value === undefined || value === '' ? '' : String(value)
        const inRange = MONTHS.some(m => String(m.value) === cur)
        return (
            <select value={cur} disabled={meta?.readOnly}
                onChange={e => onChange(e.target.value === '' ? '' : (numericString ? e.target.value : Number(e.target.value)))}
                className={cls}>
                <option value="">—</option>
                {cur && !inRange && <option value={cur}>{cur}</option>}
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
            </select>
        )
    }
    // AB#1878 — tags render a chip editor (string[]), not a single-value text input. (AB#2043 — ported
    // up from the tenant fork; InputSlot's tags pass-through now gets a real chip editor everywhere.)
    if (type === 'tags') {
        return <TagsEditor value={value} onChange={onChange} />
    }
    // AB#1878 — image fields edit as a URL with a live preview (no upload backend; URL is the contract).
    if (type === 'image') {
        const url = typeof value === 'string' ? value : ''
        return (
            <div className="space-y-1">
                <input type="text" value={url} placeholder="Image URL" disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls} />
                {url && <img src={url} alt="" className="max-h-24 rounded border border-neutral-200 dark:border-slate-700 object-contain" />}
            </div>
        )
    }
    // AB#2853 — quantity-amount gets a REAL structured editor in any edit surface (inline grid cell,
    // row modal, form). It used to fall through to the plain text input below, which rendered
    // String({amount:1}) as "[object Object]" and committed a plain STRING into the composite on the
    // first keystroke — silent data corruption. Same semantics as QuantityInputSlot (merge halves;
    // clearing both stores null; a stored unit missing from the catalogue stays selectable per
    // AB#2610); no catalogue (designer preview) degrades to a small unit TEXT input, never to the
    // corruptible single input.
    if (type === 'quantity-amount') {
        const parts = (value !== null && typeof value === 'object' && !Array.isArray(value))
            ? value as Record<string, unknown> : {}
        const amount = parts.amount
        const unit = parts.unit == null ? '' : String(parts.unit)
        const emit = (next: { amount?: unknown; unit?: unknown }) => {
            const merged = { ...parts, ...next }
            // Clearing both halves clears the field rather than storing an empty shell.
            const cleared = (merged.amount === '' || merged.amount == null) && !merged.unit
            onChange(cleared ? null : merged)
        }
        return (
            <div className="flex gap-2">
                <input type="number" className={`${cls} flex-1 min-w-0`} disabled={meta?.readOnly}
                    step={requiresWholeQuantity(unit) ? 1 : 'any'}
                    aria-invalid={!!wholeQuantityError(parts)}
                    value={amount == null ? '' : String(amount)}
                    onChange={e => emit({ amount: e.target.value === '' ? null : Number(e.target.value) })} />
                {units && units.length > 0
                    ? (
                        <select className={`${cls} w-20 shrink-0`} disabled={meta?.readOnly}
                            value={unit} onChange={e => emit({ unit: e.target.value || null })}>
                            <option value="">—</option>
                            {unit && !units.some(u => u.code === unit) && <option value={unit}>{unit}</option>}
                            {units.map(u => <option key={u.code} value={u.code}>{u.code}</option>)}
                        </select>
                    )
                    : <input type="text" className={`${cls} w-20 shrink-0`} disabled={meta?.readOnly}
                        placeholder="unit" value={unit} onChange={e => emit({ unit: e.target.value || null })} />}
                {wholeQuantityError(parts) && <span role="alert" className="text-xs text-red-600">{wholeQuantityError(parts)}</span>}
            </div>
        )
    }
    // AB#2853 — every OTHER composite is read-only formatted text until it has a real editor
    // (address-block and bank-account-block exist as dedicated components; the rest are AB#1876
    // territory). The value-shaped backstop also catches an object landing on a scalar-typed field,
    // so "[object Object]" can never render inside an input again — typing a string into a composite
    // was never valid.
    const compositeReadOnly = (type && COMPOSITE_READONLY_TYPES.has(type))
        || (value !== null && typeof value === 'object' && !Array.isArray(value))
    if (compositeReadOnly) {
        return (
            <span className="text-sm text-neutral-900 dark:text-slate-100">
                {formatValue(value, type, meta?.customType)}
            </span>
        )
    }
    if (multiline) {
        return <textarea aria-label={meta?.label ?? meta?.name} value={String(value ?? '')} rows={3} disabled={meta?.readOnly} onChange={e => onChange(e.target.value)} className={cls} />
    }
    const inputType = type === 'date' ? 'date' : type === 'datetime' ? 'datetime-local'
        : (type === 'integer' || type === 'decimal' || type === 'money' || type === 'number') ? 'number' : 'text'
    const step = requiresWholeQuantity(quantityUnit) ? '1' : type === 'money' ? '0.01' : type === 'decimal' ? 'any' : undefined
    const quantityError = wholeQuantityError({ amount: value, unit: quantityUnit })
    const instant = type === 'datetime' && meta?.instant
    const input = <input aria-label={meta?.label ?? meta?.name} type={inputType} {...(compact && inputType === 'number' ? { inputMode: type === 'integer' ? 'numeric' as const : 'decimal' as const } : {})} value={instant ? instantInputValue(value) : String(value ?? '')} step={step} aria-invalid={quantityError ? true : undefined} disabled={meta?.readOnly} onChange={e => onChange(inputType === 'number' && e.target.value !== '' ? Number(e.target.value) : instant ? instantStoredValue(e.target.value) : e.target.value)} className={cls} />
    return meta?.quantityUnitField || props.quantityUnit !== undefined || reference
        ? <div>{input}{quantityError && <span role="alert" className="text-xs text-red-600">{quantityError}</span>}</div>
        : input
}

// AB#2853 — the composites that render read-only in EditField until a real editor exists.
const COMPOSITE_READONLY_TYPES = new Set([
    'address', 'money-amount', 'date-range', 'phone-number', 'person-name', 'bank-account', 'geo',
])

// AB#2415 — `extra` is opt-in per call site rather than baked in: card() has 14 callers and most sit
// in flow layout where a height utility would do nothing (or harm).
function card(children: ReactNode, extra?: string) {
    return <div className={`bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-4${extra ? ` ${extra}` : ''}`}>{children}</div>
}

// AB#3989 — true when the view renders inside a host-owned tile (ViewDefinition.presentation). A slot
// that honours it emits its body only — no card(), no title — and fills the tile (tile.css). Dashboard
// views only: an entity view never asks for it, and a stored view never carries it.
function isTile(view: ViewDefinition): boolean {
    return view.kind === 'dashboard' && view.presentation === 'tile'
}

/** The prototype's open-arrow glyph a tile metric shows when it is a drill-down button. */
function TileArrow() {
    return (
        <svg className="view-tile-metric-arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M7 17 17 7" /><path d="M9 7h8v8" />
        </svg>
    )
}

function FieldSectionSlot({ component, view, context, entityFields, permissions, editable, draft, onChange, fetchEntityOptions, fetchEntityById, fetchEntityRecord, fetchLookupOptions, fetchRevealField, scopeCompanyId, editFieldComponent, fmt }: SlotProps) {
    const EF = editFieldComponent ?? EditField
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const sealed = sealedFields(view, isEdit ? draft! : context, component.dataBinding)
    const meta = metaFor(component.dataBinding, view, entityFields)
    const slots: FieldSlot[] = component.fields && component.fields.length > 0
        ? normalizeSlots(component.fields)
        : meta.map(m => ({ field: m.name }))
    const columns = typeof component.columns === 'number' ? component.columns : 2
    // AB#2518 — what this form last seeded per field, so a re-seed can replace a stale previous value
    // without ever overwriting something the user typed. A ref, not state: it must not trigger a render.
    const seededRef = useRef<Record<string, unknown>>({})
    const draftRef = useRef(draft)
    draftRef.current = draft

    return card(
        <>
            {component.title && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-3">{component.title}</h3>}
            <div className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {/* AB#2879 — permissions reach the evaluator here too, so a `permission:CODE` showIf on
                    an individual field slot stops hiding the field from everyone who holds the code. */}
                {slots.filter(s => !s.showIf || evaluateShowIf(s.showIf, record, permissions)).map(slot => {
                    // AB#2959 — lock an immutable-after-create field once the bound record exists.
                    const original = lockImmutableAfterCreate(meta.find(m => m.name === slot.field), record)
                    const fd = sealed.includes(slot.field) ? { ...original, name: slot.field, readOnly: true } : original
                    return (
                        <div key={slot.field} data-guidance-field={isEdit ? guidanceField(view, component.dataBinding, slot.field) : undefined}
                            data-guidance-company={scopeCompanyId}
                            data-guidance-new={!record.id}
                            style={(slot.colSpan ?? 1) > 1 ? { gridColumn: `span ${slot.colSpan}` } : undefined}>
                            {/* AB#1928 — `mb-1` (was mb-0.5) gives the read-mode label a comfortable gap above
                                its value (small/muted label, value below) instead of a cramped 2px. */}
                            <dt className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                                <span>
                                    {slot.label ?? fd?.label ?? slot.field}
                                    {isEdit && fd?.required && <span className="text-red-500 ml-0.5">*</span>}
                                </span>
                                {/* AB#2382 — the entity's authored field hint. */}
                                <FieldHint text={fd?.helpText} label={slot.label ?? fd?.label ?? slot.field} />
                            </dt>
                            {(!isEdit || fd?.readOnly) && isSentinel(record[slot.field]) ? <MaskedFieldValue
                                key={JSON.stringify([component.dataBinding, record, slot.field, permissions, scopeCompanyId])}
                                value={record[slot.field]} field={slot.field} meta={fd} record={record}
                                binding={component.dataBinding} view={view} fetchRevealField={fetchRevealField} fmt={fmt} /> : isEdit ? (
                                <EF value={record[slot.field]} meta={fd} multiline={slot.multiline}
                                    quantityUnitReference={quantityReference(fd, meta, record)}
                                    quantityUnit={fd?.quantityUnitField ? record[fd.quantityUnitField] : undefined}
                                    fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId}
                                    {...(fd?.metaOptions ? { metaOptionsEntity: resolveMetaOptionsEntity(fd, record) } : {})}
                                    {...(slot.filterBy ? { lookupFilterBy: typeof slot.filterBy === 'string'
                                        ? { field: slot.filterBy, value: record[slot.filterBy] }
                                        : { field: slot.filterBy.field, value: slot.filterBy.value === '@current' ? record[slot.filterBy.field] : slot.filterBy.value } } : {})}
                                    onChange={v => {
                                        const next = setBindingField(draft!, component.dataBinding, slot.field, v)
                                        draftRef.current = next
                                        onChange!(next)
                                        // AB#2518 — the seed is a follow-up write: the user's own change lands
                                        // immediately, and the copied value arrives when the lookup returns.
                                        void applyCopyFrom(
                                            asRecord(resolveBinding(component.dataBinding, next)), slot.field, v,
                                            meta, fetchEntityRecord, seededRef.current, scopeCompanyId,
                                            () => draftRef.current ? asRecord(resolveBinding(component.dataBinding, draftRef.current)) : undefined,
                                        ).then(patch => {
                                            if (Object.keys(patch).length === 0) { return }
                                            // Live draft, not the pre-lookup snapshot — see InputSlot.
                                            let seeded = draftRef.current ?? next
                                            for (const [f, val] of Object.entries(patch)) {
                                                seeded = setBindingField(seeded, component.dataBinding, f, val)
                                            }
                                            draftRef.current = seeded
                                            onChange!(seeded)
                                        })
                                    }} />
                            ) : (
                                // AB#1921 — read mode prefers the server-resolved FK label over the raw GUID
                                // (mirrors the ListView cell); non-FK fields fall through to formatValue.
                                // AB#1925 — formatValue branches on the custom type (month-number → month name).
                                // AB#2266 — a lookupValue field resolves its stored code to the lookup label
                                // client-side (LookupResolver only labels entity-ref FKs, so __lookups never
                                // carries lookup values); code shown until options load / on a failed fetch.
                                <dd className="text-sm text-neutral-900 dark:text-slate-100">{fd?.type === 'lookupValue' && fd.lookupType
                                    ? <LookupValueText value={record[slot.field]} lookupType={fd.lookupType} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId} />
                                    : lookupLabel(record, slot.field) ?? enumOptionLabel(fd?.enum, record[slot.field]) ?? formatValue(record[slot.field], fd?.type, fd?.customType, fmt)}</dd>
                            )}
                        </div>
                    )
                })}
            </div>
        </>,
    )
}

function SummaryPanelSlot({ component, view, context, entityFields, fmt }: SlotProps) {
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const meta = metaFor(component.dataBinding, view, entityFields)
    const fields = normalizeSlots(component.fields)
    return card(
        <>
            {component.title && <h2 className="text-base font-semibold text-neutral-900 dark:text-slate-100 mb-4">{component.title}</h2>}
            <dl className="space-y-2">
                {fields.map(f => {
                    const fd = meta.find(m => m.name === f.field)
                    return (
                        <div key={f.field} className="flex justify-between items-baseline gap-4">
                            <dt className="text-xs font-medium text-neutral-500 dark:text-slate-400 flex-none flex items-center gap-1">
                                <span>{f.label ?? fd?.label ?? f.field}</span>
                                <FieldHint text={fd?.helpText} label={f.label ?? fd?.label ?? f.field} />
                            </dt>
                            <dd className="text-sm font-medium text-neutral-900 dark:text-slate-100 text-right truncate">{formatValue(record[f.field], fd?.type, fd?.customType, fmt)}</dd>
                        </div>
                    )
                })}
            </dl>
        </>,
    )
}

// AB#1610 — immutably replace the nested child array at `$root.field` within the draft.
function setBindingArray(draft: ViewContext, binding: string | undefined, rows: Record<string, unknown>[]): ViewContext {
    const path = (binding ?? '').startsWith('$') ? binding!.slice(1) : (binding ?? '')
    const segs = path.split('.').filter(Boolean)
    if (segs.length < 2) { return draft } // need <root>.<field>
    const [root, field] = segs
    const rec = { ...asRecord(draft[root!]) }
    rec[field!] = rows
    return { ...draft, [root!]: rec }
}

// AB#2216 — the row form behind `lineEditMode: 'modal'`. A child collection wider than a handful of
// fields cannot be edited in table cells: the customers Company Segments table is 16 columns with five
// async lookups, and at grid width its inputs clip to a few characters. This renders the whole row as
// a form instead.
//
// It lives in this file rather than beside Modal.tsx because it needs the module-private helpers
// (formatValue, lookupLabel, asRecord) and because the caller hands it `EF` — the editFieldComponent
// seam (AB#2043). Importing EditField directly would bypass the tenant's FK-aware override and
// silently downgrade every lookup to a raw-GUID text box.
//
// Draft isolation is the point: edits land on a LOCAL copy and reach the real draft only via onSave.
// Cancel therefore discards for free — which also fixes the pre-existing inline bug where `+ Add line`
// committed an empty row into the draft before the user had typed anything (the old addRow).
// AB#2521 — reads a line's stored pricing trace, returning null when there is nothing to show.
//
// Accepts BOTH shapes deliberately. The price-document stage writes a JsonNode (an object) into the
// row, but the field it writes to is declared `type: "string"` because the platform has no structured
// field type to declare instead — so a value that has been round-tripped through anything treating the
// field as its declared type arrives as a JSON string. Parsing both costs nothing; guessing wrong
// leaves a permanently empty modal, which looks like a broken feature rather than an unpriced line.
//
// "Nothing to show" is a trace with no layers, not merely a null: a line priced before this shipped, or
// hand-priced, has an absent or empty trace and must produce no action at all.
//
// AB#2680 — "has anything to show" is now asked per KIND. A pricing trace is empty without layers; a
// determination trace is a decision even when it considered one candidate, and is empty only when it
// has no candidates and nothing to say. The row action must appear exactly when the modal would have
// content, so each kind answers for itself rather than the pricing shape standing in for all of them.
export function readRowTrace(value: unknown, kind: string = 'calc-trace'): unknown | null {
    let v = value
    if (typeof v === 'string') {
        const s = v.trim()
        if (s === '') { return null }
        try { v = JSON.parse(s) } catch { return null }
    }
    if (Array.isArray(v)) { return v.length > 0 ? v : null }
    if (v && typeof v === 'object') {
        if (kind === 'determination-trace') {
            const t = v as { candidates?: unknown; excluded?: unknown; warnings?: unknown; chosen?: unknown; setCode?: unknown }
            const any = [t.candidates, t.excluded, t.warnings].some(a => Array.isArray(a) && a.length > 0)
            return any || t.chosen !== undefined || typeof t.setCode === 'string' ? v : null
        }
        const layers = (v as { layers?: unknown }).layers
        return Array.isArray(layers) && layers.length > 0 ? v : null
    }
    return null
}

// AB#2680 — the row traces a line grid offers, normalised. `rowTraceField` is kept as sugar for the
// pricing one (every quote and sales-order view authors it, and they must not drift), and `rowTraces`
// is the general form: a line may now carry several answers to "why", and a shipping-site decision is
// not a pricing question wearing a different label.
export interface RowTraceSpec {
    field: string
    kind: string
    label: string
    hint: string
    /** The modal heading, suffixed with the line number, and the row action's accessible name. */
    title: string
    /** The heading INSIDE the trace component, which names the artefact rather than the act. */
    panelTitle: string
}

export function rowTraceSpecs(component: ViewComponent): RowTraceSpec[] {
    const authored = component.rowTraces ?? []
    const specs: RowTraceSpec[] = authored.map(t => ({
        field: t.field,
        kind: t.kind ?? 'calc-trace',
        label: t.label ?? 'Why?',
        hint: t.hint ?? 'Why is this what it is?',
        title: t.title ?? 'Trace',
        panelTitle: t.panelTitle ?? t.title ?? 'Trace',
    }))
    if (component.rowTraceField && !specs.some(s => s.field === component.rowTraceField)) {
        // The AB#2521 wording, verbatim. Generalising the mechanism must not quietly relabel a
        // control that has been on every quote and sales order since it shipped.
        specs.unshift({
            field: component.rowTraceField,
            kind: 'calc-trace',
            label: 'Why?',
            hint: 'Why is this price what it is?',
            title: 'Pricing analysis',
            panelTitle: 'Pricing trace',
        })
    }
    return specs.filter(s => !!s.field)
}

// AB#2627 — whether a line-grid row is locked by the component's rowReadOnlyIf leaf.
// AB#2718 — extended two ways, both because ONE eq leaf could not express what a document needs.
//   * `in` — a SET of values. Two of the delivery's item categories are informational (the sales-kit
//     header and a text line), so a single eq could seal only one of them. ShowIfCondition has had `in`
//     since it shipped; this is the same idea in the same shape.
//   * `rowRules[]` — several rules over one grid. A collection that must seal isSystem-seeded rows AND
//     informational rows could previously express only one, and the generator silently picked isSystem,
//     because that was the only branch it had.
// Loose equality throughout, deliberately: child data may hold true or "true".
export function rowMatchesRule(
    rule: { field: string; eq?: string | number | boolean; in?: Array<string | number | boolean> },
    row: Record<string, unknown>,
): boolean {
    const actual = String(row[rule.field] ?? '')
    // A DECLARED `in` decides the match, empty or not. Falling through to the eq branch on an empty set
    // would silently match every row whose value is `true` — an author who declared a set and computed it
    // to nothing means "no rows", never "the truthy ones". Array-checked rather than truthiness-checked:
    // the gate refuses a non-array `in` on a rowRule, but AB#2627's `rowReadOnlyIf` has never been
    // validated at all, and `.some` on a string would throw and blank the whole view.
    if (Array.isArray(rule.in)) { return rule.in.some(v => String(v) === actual) }
    return actual === String(rule.eq ?? true)
}

export function rowIsLocked(
    component: {
        rowReadOnlyIf?: { field: string; eq?: string | number | boolean; in?: Array<string | number | boolean> }
        rowRules?: Array<{
            field: string
            eq?: string | number | boolean
            in?: Array<string | number | boolean>
            readOnly?: boolean
            style?: string
        }>
    },
    row: Record<string, unknown>,
): boolean {
    if (component.rowReadOnlyIf && rowMatchesRule(component.rowReadOnlyIf, row)) { return true }
    // A rule seals the row when it says so. `readOnly` defaults TRUE for an `informational` rule: a row
    // whose whole purpose is to be read has nothing editable on it, and making the author repeat that on
    // every declaration is how one gets forgotten.
    return (component.rowRules ?? []).some(r =>
        rowMatchesRule(r, row) && (r.readOnly ?? r.style === 'informational'))
}

// AB#2718 — whether the row's REMOVE control is withheld, which is deliberately NOT rowIsLocked.
//
// AB#2627 withdrew the remove ✕ from a locked row on the grounds that the server refuses the delete
// anyway (AB#2589's isSystem guard), so offering it can only fail. That reasoning does not carry to a
// `rowRules` seal: nothing server-side stops the delete, and withdrawing ✕ from every informational row
// leaves a delivery's kit HEADER undeletable — a user drops its components one by one and is then stuck
// with a header they cannot remove. So:
//
//   * `rowReadOnlyIf`, and a rule with an EXPLICIT `readOnly: true` — withheld, as before.
//   * a row with CHILDREN in the rendered hierarchy — withheld, because deleting it orphans them, and an
//     orphan renders at depth 0 as though it were a top-level line. Delete the children first and the ✕
//     appears: the kit comes apart from the leaves up, which is the only order that never orphans.
//   * an informational row with no children (a text note) — REMOVABLE. Read-only is about its cells.
export function rowRemoveWithheld(
    component: {
        rowReadOnlyIf?: { field: string; eq?: string | number | boolean; in?: Array<string | number | boolean> }
        rowRules?: Array<{
            field: string
            eq?: string | number | boolean
            in?: Array<string | number | boolean>
            readOnly?: boolean
            style?: string
        }>
    },
    row: Record<string, unknown>,
    hasChildren: boolean,
): boolean {
    if (hasChildren) { return true }
    if (component.rowReadOnlyIf && rowMatchesRule(component.rowReadOnlyIf, row)) { return true }
    return (component.rowRules ?? []).some(r => rowMatchesRule(r, row) && r.readOnly === true)
}

/// AB#2718 — the style a row's matched rules ask for, or undefined.
export function rowStyle(
    component: { rowRules?: Array<{ field: string; eq?: string | number | boolean; in?: Array<string | number | boolean>; style?: string }> },
    row: Record<string, unknown>,
): string | undefined {
    return (component.rowRules ?? []).find(r => r.style && rowMatchesRule(r, row))?.style
}

// AB#2718 — how a row is NAMED to a screen reader. It used to be the row's position in the source
// array, which the hierarchy's reordering divorced from the position anyone sees. A collection that
// declares a line key has a better answer than either: the line number the document itself uses, which
// is what the user would read out. Falls back to the source position for a grid with no line key.
export function lineLabel(
    component: { lineKeyField?: string },
    row: Record<string, unknown>,
    sourceIndex: number,
): string {
    const v = component.lineKeyField ? row[component.lineKeyField] : undefined
    if (v === null || v === undefined || v === '') { return String(sourceIndex + 1) }
    return String(v)
}

// AB#2718 — the trace action's screen-reader label and the title of the modal it opens, built in ONE
// place so they cannot name different lines. They did: the button was moved onto lineLabel and the modal
// title was left on the source position, so a click could announce "line 30" and open "line 3".
// (The modal's own call site is not render-tested — it exists only after a click, and this package has
// no interactive test harness — which is exactly why the two strings are built together here.)
export function traceLabels(
    title: string,
    component: { lineKeyField?: string },
    row: Record<string, unknown>,
    sourceIndex: number,
): { action: string; modal: string } {
    const name = lineLabel(component, row, sourceIndex)
    return { action: `${title} for line ${name}`, modal: `${title} — line ${name}` }
}

/**
 * AB#2718, ADR-0044 §5 — orders a line collection PARENT-THEN-CHILDREN and gives each row a depth, so a
 * sales-kit's informational header renders above the components it exploded into.
 *
 * Returns the rows paired with their ORIGINAL index, and that pairing is the whole point: every mutation
 * in the grid — edit a cell, open the row modal, remove the row, open its trace — indexes straight into
 * the source array. Reordering for display without carrying the source index would silently edit, delete
 * or explain a DIFFERENT line than the one clicked, and the further down the grid the worse it gets.
 *
 * Four properties that are not obvious and each of which is a defect if missed:
 *
 *  - **An orphan renders.** A component whose parent line was removed, or whose header was never
 *    persisted, appears at depth 0 in its original position. Nothing enforces referential integrity
 *    between the pointer and the key — no foreign key, no check constraint — so building a tree and
 *    rendering only reachable nodes makes a shipped component silently invisible on a document of record.
 *  - **A blank key never matches.** On a create form every new row's line number is blank (it is filled
 *    server-side at persist), so treating blank as a value would group every new row under the first one.
 *  - **A cycle degrades to flat.** The pointer is a free integer with no constraint; a self-reference or
 *    a loop must not hang the renderer or blow the stack, so the walk is visited-guarded and depth-capped.
 *  - **Original order is preserved otherwise.** Rows that are neither parents nor children stay exactly
 *    where they were, so a grid without hierarchy renders byte-identically.
 */
export function orderLineRows(
    rows: Array<Record<string, unknown>>,
    opts: { parentLineField?: string; lineKeyField?: string },
): Array<{ row: Record<string, unknown>; sourceIndex: number; depth: number; hasChildren: boolean }> {
    const flat = rows.map((row, sourceIndex) => ({ row, sourceIndex, depth: 0, hasChildren: false }))
    const { parentLineField, lineKeyField } = opts
    if (!parentLineField || !lineKeyField) { return flat }

    const key = (row: Record<string, unknown>): string | undefined => {
        const v = row[lineKeyField]
        if (v === null || v === undefined || v === '') { return undefined }
        return String(v)
    }
    const parentKey = (row: Record<string, unknown>): string | undefined => {
        const v = row[parentLineField]
        if (v === null || v === undefined || v === '') { return undefined }
        return String(v)
    }

    // Children by parent key, in source order.
    const childrenOf = new Map<string, Array<{ row: Record<string, unknown>; sourceIndex: number }>>()
    const keys = new Set<string>()
    for (const entry of flat) {
        const k = key(entry.row)
        if (k !== undefined) { keys.add(k) }
    }
    for (const entry of flat) {
        const p = parentKey(entry.row)
        // A row pointing at a key that is not present is an ORPHAN, and is left in the top-level pass.
        // Self-reference is refused here rather than in the walk, so the simplest cycle cannot form.
        if (p === undefined || !keys.has(p) || p === key(entry.row)) { continue }
        const list = childrenOf.get(p) ?? []
        list.push({ row: entry.row, sourceIndex: entry.sourceIndex })
        childrenOf.set(p, list)
    }
    if (childrenOf.size === 0) { return flat }

    const claimed = new Set<number>()
    for (const list of childrenOf.values()) {
        for (const c of list) { claimed.add(c.sourceIndex) }
    }

    const MAX_DEPTH = 8
    const visited = new Set<number>()
    const ordered: Array<{ row: Record<string, unknown>; sourceIndex: number; depth: number; hasChildren: boolean }> = []

    const emit = (entry: { row: Record<string, unknown>; sourceIndex: number }, depth: number): void => {
        if (visited.has(entry.sourceIndex)) { return }
        visited.add(entry.sourceIndex)
        const k = key(entry.row)
        // Reported so the grid can withhold the remove control from a row whose deletion would orphan
        // rows beneath it. Judged at the depth ceiling too: a row whose children this walk declined to
        // emit still HAS them, and offering to delete it there is the same orphaning.
        const children = k === undefined ? [] : (childrenOf.get(k) ?? [])
        ordered.push({ row: entry.row, sourceIndex: entry.sourceIndex, depth, hasChildren: children.length > 0 })
        if (depth >= MAX_DEPTH) { return }
        for (const child of children) { emit(child, depth + 1) }
    }

    // Top level in SOURCE order: every row that nothing claims as a child, orphans included.
    for (const entry of flat) {
        if (claimed.has(entry.sourceIndex)) { continue }
        emit(entry, 0)
    }
    // Anything a cycle left unreached still renders, flat and in source order — a row that exists must
    // be on the screen, whatever the data says about its parentage.
    for (const entry of flat) {
        if (!visited.has(entry.sourceIndex)) {
            const k = key(entry.row)
            // A cycle member can still be somebody's parent — withhold its delete on the same grounds.
            ordered.push({ ...entry, depth: 0, hasChildren: (k === undefined ? [] : childrenOf.get(k) ?? []).length > 0 })
        }
    }
    return ordered
}

// AB#2645/2647 — resolve a cell's EFFECTIVE lookup against its OWN ROW. Two entity-declared,
// sibling-driven behaviours meet here:
//   - lookupField.sourceBy (AB#2645): the picker's SOURCE switches on a sibling column's value
//     (Partner Requirements Function: catalogue CUSTOMER → PartnerFunctionDef, VENDOR →
//     VendorPartnerFunctionDef). A sibling value with no map entry drops the lookup entirely —
//     a plain input is honest when the driving column hasn't been chosen yet.
//   - filterBy (AB#2647): the option query filters the source's `field` by the sibling named in
//     `from` (or `field` itself) when value is "@current" — the follow-on Type column offering only
//     the picked Document's types. Literal values pass through, matching the slot-level filterBy.
// Pure and exported for tests; the two cell sites (inline + row modal) both go through it, so the
// grid and its dialog can never disagree about what a cell offers.
export function resolveCellLookup(
    fd: ViewFieldMeta | undefined,
    row: Record<string, unknown>,
): { meta: ViewFieldMeta | undefined; lookupFilterBy?: { field: string; value: unknown } } {
    if (!fd) { return { meta: fd } }
    let meta = fd
    const sourceBy = fd.lookupField?.sourceBy
    if (sourceBy) {
        const pick = sourceBy.map[String(row[sourceBy.field] ?? '')]
        meta = pick
            ? {
                ...fd,
                lookupField: {
                    ...fd.lookupField!,
                    sourceEntity: pick.sourceEntity,
                    ...(pick.valueField ? { valueField: pick.valueField } : {}),
                    ...(pick.displayField ? { displayField: pick.displayField } : {}),
                },
            }
            : { ...fd, lookupField: undefined }
    }
    const fb = fd.filterBy
    if (!fb) { return { meta } }
    return {
        meta,
        lookupFilterBy: { field: fb.field, value: fb.value === '@current' ? row[fb.from ?? fb.field] : fb.value },
    }
}

// AB#2652/AB#2654 — resolves a metaOptions field's DRIVING entity off the record: the row's own
// sibling first, then the parent record. A child collection's driver usually lives on the header —
// a follow-on rule's From Statuses are the statuses of the PARENT type's document, and the child
// row carries no documentEntityType column at all.
function resolveMetaOptionsEntity(
    fd: ViewFieldMeta | undefined,
    row: Record<string, unknown>,
    parentRow?: Record<string, unknown> | undefined,
): string | undefined {
    const f = fd?.metaOptions?.field
    if (!f) { return undefined }
    const own = row[f]
    const v = (own !== undefined && own !== null && own !== '') ? own : parentRow?.[f]
    return typeof v === 'string' && v ? v : undefined
}

function LineItemRowModal({
    title, initialRow, cols, childMeta, EF, onSave, onCancel,
    fetchEntityOptions, fetchEntityById, fetchLookupOptions, scopeCompanyId, fmt, parentRow,
}: {
    title: string
    initialRow: Record<string, unknown>
    cols: NonNullable<ViewComponent['tableColumns']>
    childMeta: ViewFieldMeta[]
    EF: EditFieldComponent
    onSave: (row: Record<string, unknown>) => void
    onCancel: () => void
    fetchEntityOptions?: FetchEntityOptions | undefined
    fetchEntityById?: FetchEntityById | undefined
    // AB#2518 — fetches a referenced record's fields so a copyFrom can seed from it.
    fetchEntityRecord?: FetchEntityRecord | undefined
    // AB#2520 — host seam that prices a provisional document line. Absent (designer preview) = no
    // live pricing at all.
    fetchLinePrice?: FetchLinePrice | undefined
    fetchLookupOptions?: FetchLookupOptions | undefined
    scopeCompanyId?: string | undefined
    fmt?: FormatContext | undefined
    // AB#2652/AB#2654 — the PARENT record, for metaOptions drivers that live on the header rather
    // than the row (a follow-on rule's From Statuses read the header's documentEntityType).
    parentRow?: Record<string, unknown> | undefined
}) {
    const [row, setRow] = useState<Record<string, unknown>>(() => ({ ...initialRow }))

    // A required column left empty blocks Save. Mirrors the cell gate: only columns the table says are
    // editable can be filled in here, so a required-but-locked column can never deadlock the dialog.
    // AB#3019 — unless the field says the omission is only worth mentioning, in which case it is said
    // and the user goes through. Without the split an author's only choices are to block a legitimate
    // flow or to say nothing, and "say nothing" always wins.
    const missing = missingRequiredCells(row, cols, childMeta)
    const blocking = missing.filter(m => m.severity === 'error')
    const advisory = missing.filter(m => m.severity === 'warning')

    return (
        <Modal title={title} onCancel={onCancel} onConfirm={() => onSave(row)} confirmDisabled={blocking.length > 0}>
            {advisory.length > 0 && (
                <div role="status" className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                    {advisory.map(a => a.label).join(', ')} {advisory.length === 1 ? 'has' : 'have'} no value. You can save the line without {advisory.length === 1 ? 'it' : 'them'}.
                </div>
            )}
            <div className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                {cols.map(c => {
                    const fd = childMeta.find(m => m.name === c.field)
                    // Same per-column gate as the inline cell (`c.editable === true`), so the modal can
                    // never make a locked column writable — a computed line total stays read-only and
                    // visibly recalculating rather than disappearing from the form.
                    const cellEditable = c.editable === true
                    return (
                        <div key={c.field}>
                            <dt className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                                <span>
                                    {c.label ?? fd?.label ?? c.field}
                                    {cellEditable && fd?.required && <span className="text-red-500 ml-0.5">*</span>}
                                </span>
                                {/* AB#2382 — the row-edit dialog is a form like any other, so the child
                                    entity's field hints belong here too. */}
                                <FieldHint text={fd?.helpText} label={c.label ?? fd?.label ?? c.field} />
                            </dt>
                            {cellEditable
                                ? (() => {
                                    // AB#2645/2647 — the dialog's cells cascade off the dialog's own
                                    // LIVE row state, so picking the Catalogue re-sources the Function
                                    // picker without leaving the form.
                                    const resolved = resolveCellLookup(fd, row)
                                    return <EF value={row[c.field]} meta={resolved.meta}
                                        quantityUnitReference={quantityReference(fd, childMeta, row)}
                                        quantityUnit={fd?.quantityUnitField ? row[fd.quantityUnitField] : undefined}
                                        fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById}
                                        fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId}
                                        {...(resolved.lookupFilterBy ? { lookupFilterBy: resolved.lookupFilterBy } : {})}
                                        {...(fd?.metaOptions ? { metaOptionsEntity: resolveMetaOptionsEntity(fd, row, parentRow) } : {})}
                                        // Recompute the child's computed fields on every keystroke so a line
                                        // total updates while the dialog is open. applyComputes is idempotent.
                                        onChange={v => setRow(r => applyComputes({ ...r, [c.field]: v }, childMeta))} />
                                })()
                                : <div className="text-sm text-neutral-900 dark:text-slate-100">
                                    {/* AB#2266 — a locked lookupValue column shows the lookup label, matching its table cell. */}
                                    {fd?.type === 'lookupValue' && fd.lookupType
                                        ? <LookupValueText value={row[c.field]} lookupType={fd.lookupType} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId} />
                                        : lookupLabel(row, c.field) ?? formatValue(row[c.field], fd?.type, fd?.customType, fmt)}
                                </div>}
                        </div>
                    )
                })}
            </div>
        </Modal>
    )
}

// AB#3019 — the required values a row is missing, and how much that matters.
//
// A line is where a document's commitments live: a purchase-order line with no unit price commits to
// buying something at a price nobody agreed, and until now the inline grid had no notion of a required
// column at all — `+ Add line` committed an empty row and the only feedback was the save failing later,
// if anything checked at all. The modal editor DID gate on `required`; inline (which every generated
// order grid uses) did not, so the same declaration behaved differently depending on the edit mode.
//
// Severity comes from the field: "warning" flags the cell and lets the user through, anything else
// (including absent) blocks. Same default as the server's validate rules, and deliberately the same
// word, so an author sets the entity flag and the rule's severity to the same value and the user is
// told the same thing in the form and on save.
//
// Only EDITABLE columns are considered, mirroring the modal gate: flagging a column the user cannot
// fill in would be an accusation with no remedy.
function missingRequiredCells(
    row: Record<string, unknown>,
    cols: { field: string; editable?: boolean | undefined }[],
    childMeta: ViewFieldMeta[],
): { field: string; label: string; severity: 'error' | 'warning' }[] {
    return cols.flatMap(c => {
        if (c.editable !== true) { return [] }
        const fd = childMeta.find(m => m.name === c.field)
        if (!fd?.required) { return [] }
        const v = row[c.field]
        if (v !== undefined && v !== null && v !== '') { return [] }
        return [{
            field: c.field,
            label: fd.label ?? c.field,
            severity: fd.requiredSeverity === 'warning' ? 'warning' as const : 'error' as const,
        }]
    })
}

// AB#3019 — a row's identity as the user reads it in the grid. The line number when it has one (it is
// assigned at persist, so a row added a moment ago has none), else its 1-based position — which is
// what the user is looking at either way. Mirrors the server-side EachLineEvaluator's labelling so the
// form and the save refusal name the same line.
function lineLabelOf(row: Record<string, unknown> | undefined, index: number): string {
    for (const candidate of ['lineNo', 'lineNumber', 'sequence']) {
        const v = row?.[candidate]
        if (v !== undefined && v !== null && v !== '') { return String(v) }
    }
    return String(index + 1)
}

function LineItemsSlot({ component, view, context, entityFields, editable, draft, onChange, fetchEntityOptions, fetchEntityById, fetchEntityRecord, fetchLinePrice, fetchLookupOptions, fetchPreviewRows, scopeCompanyId, editFieldComponent, calcTraceComponent, determinationTraceComponent, fmt, viewport, tableLayoutStore }: SlotProps) {
    const EF = editFieldComponent ?? EditField
    const isEdit = !!component.editable && !!editable && !!draft && !!onChange
    const sealed = sealedFields(view, isEdit ? draft! : context, component.dataBinding)
    if (sealed.length > 0) {
        component = { ...component, addable: false, removable: false,
            ...(component.tableColumns ? { tableColumns: component.tableColumns.map(c => sealed.includes(c.field) ? { ...c, editable: false } : c) } : {}) }
    }
    const raw = resolveBinding(component.dataBinding, isEdit ? draft! : context)
    const rows = Array.isArray(raw) ? raw as Record<string, unknown>[] : []
    // AB#2718 — parent-then-children display order, each row carrying its SOURCE index because every
    // mutation below indexes into `rows`. Identity when no hierarchy is declared.
    const orderedRows = orderLineRows(rows, component)
    // AB#3953 / AB#3954 — `allCols` is the module's declared grid: validation (rowGaps), the row
    // editor modal and the card projection's fallbacks always see every declared column, because
    // hiding is a display choice that never touches data. `cols` is what the user shows, in their
    // order; the table, the cards and the preview rows render those.
    const allCols: TableColumn[] = component.tableColumns ?? []
    const lineIdentity = tableIdentity(view, component)
    const lineLayout = useTableLayout(lineIdentity, allCols, EMPTY_COLUMNS, useTableLayoutStore(tableLayoutStore))
    const cols = lineLayout.columns
    const configurator = useColumnConfigurator({ identity: lineIdentity, layout: lineLayout, viewport, labelOf: f => allCols.find(c => c.field === f)?.label ?? f })
    // AB#1610 — row field metadata comes from the child entity, not the parent.
    const childMeta: ViewFieldMeta[] = (component.childEntityType ? (entityFields[component.childEntityType] ?? []) : [])
        .map(m => sealed.includes(m.name) ? { ...m, readOnly: true } : m)
    // AB#3019 — per-row required-value gaps, indexed to match `rows` (the row map indexes by
    // sourceIndex, not by display position). Only computed in edit mode: a read-only grid showing a
    // saved document must not accuse it of being incomplete, and a historical document legitimately
    // predates a rule added later.
    const rowGaps = isEdit ? rows.map(r => missingRequiredCells(r, allCols, childMeta)) : []
    const blockingRows = rowGaps
        .map((g, i) => ({ i, gaps: g.filter(x => x.severity === 'error') }))
        .filter(x => x.gaps.length > 0)
    const advisoryRows = rowGaps
        .map((g, i) => ({ i, gaps: g.filter(x => x.severity === 'warning') }))
        .filter(x => x.gaps.length > 0)
    // AB#1610 — parent entity metadata, for header computed fields (e.g. an order total over its lines).
    const parentRoot = (component.dataBinding ?? '').replace(/^\$/, '').split('.').filter(Boolean)[0]
    const parentType = view.context?.find(c => c.name === parentRoot)?.entityType
    const parentMeta: ViewFieldMeta[] = parentType ? (entityFields[parentType] ?? []) : []

    // AB#2640 — the create form PREVIEWS the rows the save will seed (previewFrom): display-only rows
    // read from the defaults record for the parent's current key value. Only on a CREATE form (a saved
    // record's grid shows the real seeded rows), only while the key has a value, and NEVER merged into
    // the draft — the server-side create-only seed stays authoritative, and draft copies would collide
    // with it on the unique index. Loading re-fires when the user re-picks the key (a different
    // Document previews that document's defaults).
    const previewFrom = component.previewFrom
    const parentRow = parentRoot ? (isEdit ? draft! : context)[parentRoot] as Record<string, unknown> | undefined : undefined
    const isCreateForm = isEdit && !parentRow?.['id']
    const previewKey = previewFrom && isCreateForm ? String(parentRow?.[previewFrom.parentField] ?? '') : ''
    const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([])
    useEffect(() => {
        if (!previewFrom || !fetchPreviewRows || !previewKey) { setPreviewRows([]); return }
        let ignore = false
        fetchPreviewRows(previewFrom.entity, previewFrom.keyField, previewKey, previewFrom.collection, scopeCompanyId)
            .then(r => { if (!ignore) { setPreviewRows(r) } })
            .catch(() => { if (!ignore) { setPreviewRows([]) } })
        return () => { ignore = true }
    }, [previewFrom, fetchPreviewRows, previewKey, scopeCompanyId])

    // AB#1610 — recalculate on every edit: recompute each row's computed child fields (e.g. lineTotal =
    // quantity * unitPrice), then the parent's computed fields (e.g. total = $sum(lines.lineTotal)), so
    // line and order totals update live as the user types — not only on save.
    // AB#2524 — a STABLE identity per row, because neither of the two obvious keys works here.
    //
    // The index is not stable: removing line 1 shifts line 2 into slot 0. The row OBJECT is not stable
    // either — `editCell` spreads a new object on every keystroke and `commit` maps rows through
    // `applyComputes`, which returns a fresh object whenever a computed value moved. Keying seed memory
    // on the object (AB#2518) therefore lost its entry on the first ordinary edit, so re-picking a
    // material left the previous material's description sitting under it — the one outcome copyFrom's
    // contract forbids.
    //
    // So: allocate a number per row and CARRY it whenever a row object is replaced. Held in a WeakMap so
    // a dropped row's entry goes with it, and never written onto the row itself — this is view state,
    // and a synthetic key must not end up in the saved document.
    // The mechanism lives in ./rowKeys so it can be unit-tested — this package has no jsdom, so logic
    // left inline in the component is unreachable by any test.
    const rowKeysRef = useRef(createRowKeys())
    const keyOf = (row: object) => rowKeysRef.current.keyOf(row)
    const carryKey = (from: object, to: object) => rowKeysRef.current.carry(from, to)

    // AB#2526 — the LIVE draft, not the one this render closed over. `commit` is a per-render closure and
    // the async copyFrom callback below calls it after a network round-trip, so reading the captured
    // `draft` there rebuilds the WHOLE document from a pre-fetch snapshot — silently reverting anything
    // the user changed meanwhile: a header field they typed, or a currency another copyFrom seeded onto
    // the document while this line's material lookup was still in flight. On quotes that field is
    // required, so the revert surfaces as a validation failure the user cannot explain.
    //
    // rowsRef below rescues the ROWS only; every other field of the bound record, and every other root of
    // the draft, came from the stale closure. FieldSectionSlot and InputSlot already keep exactly this
    // ref for exactly this reason — this was the one copyFrom call site without one.
    const draftRef = useRef(draft)
    draftRef.current = draft

    const commit = (nextRows: Record<string, unknown>[]) => {
        const live = draftRef.current
        const computedRows = childMeta.some(m => m.compute)
            ? nextRows.map(r => { const c = applyComputes(r, childMeta); carryKey(r, c); return c })
            : nextRows
        // AB#2789 — publish the committed rows to rowsRef NOW, not at the next render. Every async
        // continuation in this component (copyFrom, live pricing) re-reads rowsRef at landing time
        // precisely so it lands on current rows — but the ref was only refreshed while rendering, so a
        // continuation that resolved BEFORE React re-rendered read the rows as they were before the edit
        // that triggered it, and rebuilding the row from them silently dropped that edit.
        //
        // That is not a rare race. Choosing a material on a line fires copyFrom for the description,
        // unit and price, and the material record is already cached from the option list the user just
        // picked from — so the promise resolves in a microtask, always ahead of the re-render. The
        // symptom was total: a sales order line could not be given a material at all. The row showed the
        // selection, the save wrote every other field of that line, and materialId was simply absent.
        rowsRef.current = computedRows
        const path = (component.dataBinding ?? '').replace(/^\$/, '').split('.').filter(Boolean)
        if (path.length < 2) { onChange!(setBindingArray(live!, component.dataBinding, computedRows)); return }
        const [root, field] = path
        const parent = { ...asRecord(live![root!]), [field!]: computedRows }
        const computedParent = parentMeta.some(m => m.compute) ? applyComputes(parent, parentMeta) : parent
        onChange!({ ...live!, [root!]: computedParent })
    }
    // AB#2518 — a row's copyFrom resolves WITHIN the row: a quote line's description comes from the
    // material chosen on that same line.
    //
    // Seeded values are tracked per ROW OBJECT, not per index. Indexes are not stable — removing line 1
    // shifts line 2 into slot 0, and index-keyed state would then treat line 2's text as if line 1 had
    // seeded it, making a user's own wording look replaceable. A WeakMap keyed by the row keeps the
    // association with the row itself and is collected when the row goes.
    // Seed memory per STABLE row key (see keyOf) rather than per row object or index.
    const seededRef = useRef(new Map<number, Record<string, unknown>>())
    // The live rows, so an async seed lands on top of whatever the user has typed since — not on the
    // snapshot taken before the lookup started.
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    // AB#2520 — live line pricing. Off unless the view declares it AND the host wires the seam, so the
    // designer preview (which wires nothing) simply never prices.
    const priceSpec = component.type === 'line-items-table' ? component.livePricing : undefined
    const canPrice = !!priceSpec && !!fetchLinePrice && isEdit
    // What WE last priced, per stable row key — so re-pricing replaces our own number and never a price
    // the user negotiated by hand.
    const autoPricedRef = useRef(new Map<number, unknown>())
    const priceTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>())
    // Typing a quantity of 100 must not fire four pricing calls.
    const priceDebounceMs = 400

    // Cancel anything in flight when the grid goes away, so a landed price cannot call onChange after
    // unmount.
    useEffect(() => {
        const timers = priceTimersRef.current
        return () => { for (const t of timers.values()) { clearTimeout(t) } timers.clear() }
    }, [])

    const schedulePrice = (key: number) => {
        if (!canPrice) { return }
        const timers = priceTimersRef.current
        const pending = timers.get(key)
        if (pending) { clearTimeout(pending) }
        timers.set(key, setTimeout(() => {
            timers.delete(key)
            // Re-read everything at fire time: the row may have moved, changed or gone since the keystroke.
            const live = rowsRef.current
            const idx = live.findIndex(r => rowKeysRef.current.peek(r) === key)
            if (idx < 0) { return }
            const documentRoot = (component.dataBinding ?? '').replace(/^\$/, '').split('.').filter(Boolean)[0]
            const request = buildRequest(live[idx]!, asRecord(draftRef.current?.[documentRoot!]), priceSpec!)
            if (!request) { return }

            void fetchLinePrice!(request).then(price => {
                if (typeof price !== 'number' || !Number.isFinite(price)) { return }
                const now = rowsRef.current
                const i = now.findIndex(r => rowKeysRef.current.peek(r) === key)
                if (i < 0) { return }   // the line was removed while the price was in flight
                const target = now[i]!
                if (!isPriceReplaceable(target[priceSpec!.priceField], autoPricedRef.current.get(key))) { return }
                const merged = { ...target, [priceSpec!.priceField]: price }
                carryKey(target, merged)
                autoPricedRef.current.set(key, price)
                commit(now.map((r, j) => j === i ? merged : r))
            }).catch(() => {
                // Pricing is an aid here, not a gate — the save-time stage is the gate. A failed or slow
                // call leaves the user free to type a price and save.
            })
        }, priceDebounceMs))
    }

    const editCell = (rowIdx: number, field: string, value: unknown) => {
        const original = rows[rowIdx]
        if (!original) { return }
        // Establish the key BEFORE anything replaces the object, then carry it onto the replacement.
        const key = keyOf(original)
        const nextRows = rows.map((r, i) => {
            if (i !== rowIdx) { return r }
            const copy = { ...r, [field]: value }
            carryKey(r, copy)
            return copy
        })
        commit(nextRows)

        // AB#2520 — re-price on any edit EXCEPT one to the price itself: typing in the price column is
        // the user taking over, and re-pricing over them would fight the keystroke.
        if (field !== priceSpec?.priceField) { schedulePrice(key) }

        const target = nextRows[rowIdx]
        if (!target) { return }
        const seeded: Record<string, unknown> = { ...(seededRef.current.get(key) ?? {}) }

        void applyCopyFrom(
            target, field, value, childMeta, fetchEntityRecord, seeded, scopeCompanyId,
            () => rowsRef.current.find(r => rowKeysRef.current.peek(r) === key),
        ).then(patch => {
            if (Object.keys(patch).length === 0) { return }
            // AB#2524 — resolve the target by IDENTITY, not by the index captured when the edit began.
            // Deleting an earlier line while this lookup was in flight used to land the patch on whatever
            // row had shifted into the captured slot, silently overwriting a different line's text; the
            // old `if (!row)` guard only caught the array shrinking past the index entirely.
            const live = rowsRef.current
            const idx = live.findIndex(r => rowKeysRef.current.peek(r) === key)
            if (idx < 0) { return }   // the row was removed while the lookup was in flight
            const merged = { ...live[idx], ...patch }
            carryKey(live[idx]!, merged)
            seededRef.current.set(key, seeded)
            commit(live.map((r, i) => i === idx ? merged : r))
        })
    }
    const addRow = () => commit([...rows, {}])
    const removeRow = (rowIdx: number) => {
        const gone = rows[rowIdx]
        if (gone) {
            const key = keyOf(gone)
            seededRef.current.delete(key)
            // AB#2520 — drop any pending price for a line that no longer exists.
            autoPricedRef.current.delete(key)
            const pending = priceTimersRef.current.get(key)
            if (pending) { clearTimeout(pending); priceTimersRef.current.delete(key) }
        }
        commit(rows.filter((_, i) => i !== rowIdx))
    }

    // AB#2216 — 'modal' is the DEFAULT row-editing mode; a table opts back into cell editing with
    // lineEditMode: 'inline' (which the generator emits on the order-line tables it builds).
    // Type-gated: related-entity-list shares this renderer and is the read-only sibling, so it must
    // never open an editor. Also inert unless isEdit — a display-zone copy of the table (which the
    // generator emits with editable:false) stays exactly as read-only as it was.
    const modalMode = component.type === 'line-items-table' && (component.lineEditMode ?? 'modal') === 'modal'
    const [editing, setEditing] = useState<{ mode: 'add' | 'edit'; index: number } | null>(null)

    // AB#2521 — the per-row pricing analysis. Deliberately NOT gated on isEdit: "why is this price what
    // it is" is asked while READING a saved quote at least as often as while editing one, and the trace
    // is read-only output either way.
    //
    // Needs the host's trace seam — the designer preview injects none, so it simply shows no action
    // rather than a button that opens an empty box.
    //
    // AB#2680 — one column, one button per authored trace whose row actually has one. A line that is
    // both priced and site-determined answers both questions from the same place.
    const traceSeams: Record<string, CustomSlotComponent | undefined> = {
        'calc-trace': calcTraceComponent,
        'determination-trace': determinationTraceComponent,
    }
    const traceSpecs = (component.type === 'line-items-table' ? rowTraceSpecs(component) : [])
        .filter(s => !!traceSeams[s.kind])
    const traceEnabled = traceSpecs.length > 0
    const [tracing, setTracing] = useState<{ index: number; field: string } | null>(null)
    const traceCol = traceEnabled ? 1 : 0
    const saveRow = (next: Record<string, unknown>) => {
        if (!editing) { return }
        if (editing.mode === 'add') { commit([...rows, next]) }
        else {
            // AB#2524 — the dialog hands back a whole new row object; carry the replaced row's key so
            // its seed memory (and any in-flight lookup targeting it) still resolves.
            commit(rows.map((r, i) => {
                if (i !== editing.index) { return r }
                carryKey(r, next)
                return next
            }))
        }
        setEditing(null)
    }

    // AB#3280 — ONE cell renderer, shared by the wide table cell and the compact card row. A phone
    // card that rendered its own idea of a cell would drift from the table the moment either side
    // changed (the AB#2680 lesson), and the drift would be invisible until someone opened the screen
    // on a phone: same control, same FK/lookup/format resolution, same edit callback, both tiers.
    const cellEditableAt = (row: Record<string, unknown>, c: TableColumn) =>
        isEdit && !modalMode && c.editable === true && !rowIsLocked(component, row)
    // AB#3019 — the row's required-value gap for this column, or undefined. Only in an editable cell,
    // for the reason rowGaps itself is edit-only: a saved document must not be accused of being
    // incomplete.
    const cellGapAt = (sourceIndex: number, row: Record<string, unknown>, c: TableColumn) =>
        cellEditableAt(row, c) ? rowGaps[sourceIndex]?.find(g => g.field === c.field) : undefined
    const gapClassFor = (gap: { severity: string } | undefined) => gap
        ? gap.severity === 'error'
            ? ' bg-red-50 dark:bg-red-950/40 ring-1 ring-inset ring-red-300 dark:ring-red-800'
            : ' bg-amber-50 dark:bg-amber-950/40 ring-1 ring-inset ring-amber-300 dark:ring-amber-800'
        : ''
    const gapTitle = (gap: { severity: string; label: string } | undefined) => gap
        ? { title: gap.severity === 'error' ? `${gap.label} is required.` : `${gap.label} is missing.` }
        : {}
    const cellContent = (row: Record<string, unknown>, c: TableColumn, sourceIndex: number): ReactNode => {
        const fd = childMeta.find(m => m.name === c.field)
        // AB#2627 — a locked row (rowReadOnlyIf matched) renders read-only cells whatever the column
        // declares.
        return cellEditableAt(row, c)
            // AB#2645/2647 — the cell's lookup resolves against its OWN row: sourceBy switches the
            // picker's source on a sibling column, filterBy cascades the option query off one.
            ? (() => {
                const resolved = resolveCellLookup(fd, row)
                return <EF value={row[c.field]} meta={resolved.meta}
                    quantityUnitReference={quantityReference(fd, childMeta, row)}
                    quantityUnit={fd?.quantityUnitField ? row[fd.quantityUnitField] : undefined}
                    fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId}
                    {...(resolved.lookupFilterBy ? { lookupFilterBy: resolved.lookupFilterBy } : {})}
                    {...(fd?.metaOptions ? { metaOptionsEntity: resolveMetaOptionsEntity(fd, row, parentRow) } : {})}
                    onChange={v => editCell(sourceIndex, c.field, v)} />
            })()
            // AB#1922 — read mode prefers the server-resolved FK label for the row
            // (row.__lookups[field], keyed by FK field), mirroring the ListView / detail cell; non-FK
            // columns have no lookup entry and fall straight through to formatValue (AB#1925
            // month-number → month name).
            // AB#2266 — a lookupValue column resolves its stored code to the lookup label
            // client-side (__lookups never carries lookup values).
            : (fd?.type === 'lookupValue' && fd.lookupType
                ? <LookupValueText value={row[c.field]} lookupType={fd.lookupType} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId} />
                : lookupLabel(row, c.field) ?? formatValue(row[c.field], fd?.type, fd?.customType, fmt))
    }
    const infoChip = (
        <span className="mr-1.5 inline-flex items-center rounded bg-neutral-200 dark:bg-slate-700 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide not-italic text-neutral-600 dark:text-slate-300 whitespace-nowrap" title="For information only — nothing on this line is picked or shipped">Info</span>
    )
    const traceButtons = (row: Record<string, unknown>, sourceIndex: number) => traceSpecs.map(spec =>
        readRowTrace(row[spec.field], spec.kind) !== null && (
            <button key={spec.field} type="button"
                onClick={() => setTracing({ index: sourceIndex, field: spec.field })}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 ml-2 first:ml-0"
                title={spec.hint}
                aria-label={traceLabels(spec.title, component, row, sourceIndex).action}>{spec.label}</button>
        ))

    // AB#3280 — compact (phone) tier: a wide line grid is not a table any more, it is one CARD per
    // line. A horizontally scrolling table on a 375px screen is the worst of both worlds — the
    // columns are unreadable AND the page scrolls sideways under the thumb — so each line becomes a
    // heading row (component.cardFields, defaulting to the first declared column) with every other
    // column beneath it as a label/value pair.
    //
    // EDIT MODE IS INCLUDED, both flavours. A 'modal' table's cells are already read-only (the row
    // opens in a dialog), so its card is the read card plus the Edit/Remove controls. An 'inline'
    // table's editable columns render their REAL control in the card — the same <EF> the table cell
    // renders, through cellContent above — so live totals, copyFrom seeding, live pricing and the
    // AB#3019 gap markers all behave on a phone exactly as they do on a desktop. Inline cards stack
    // label ABOVE value, because a full-width input cannot share a line with its own label.
    const compact = viewport === 'compact'
    const cardHeadCols: TableColumn[] = component.cardFields && component.cardFields.length > 0
        ? component.cardFields.map(f => cols.find(c => c.field === f) ?? { field: f })
        : cols.slice(0, 1)
    const cardHeadFields = new Set(cardHeadCols.map(c => c.field))
    const cardBodyCols = cols.filter(c => !cardHeadFields.has(c.field))

    return (
        <div data-guidance-field={isEdit ? guidanceField(view, component.dataBinding) : undefined} data-guidance-company={scopeCompanyId}
            data-guidance-new={!asRecord(resolveBinding(`$${parentRoot}`, isEdit ? draft! : context)).id}
            className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 overflow-hidden">
            {(component.title || (isEdit && component.addable) || configurator.cog) && (
                <div className="px-4 py-2 border-b border-neutral-100 dark:border-slate-700 flex items-center justify-between">
                    {component.title && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200">{component.title}</h3>}
                    {/* AB#3953 — the column cog shares the title bar's right edge with Add line. */}
                    {configurator.cog
                        ? <div className="ml-auto flex items-center gap-2">
                            {isEdit && component.addable && (
                                <button type="button" onClick={modalMode ? () => setEditing({ mode: 'add', index: rows.length }) : addRow} className="text-xs font-medium text-blue-600 hover:text-blue-800">+ Add line</button>
                            )}
                            {configurator.cog}
                        </div>
                        : isEdit && component.addable && (
                            <button type="button" onClick={modalMode ? () => setEditing({ mode: 'add', index: rows.length }) : addRow} className="text-xs font-medium text-blue-600 hover:text-blue-800">+ Add line</button>
                        )}
                </div>
            )}
            {configurator.frame(compact ? <div data-testid="compact-lines" className="divide-y divide-neutral-200 dark:divide-slate-700">
                {orderedRows.length === 0 && previewRows.length === 0 && <p className="p-4">{component.emptyText ?? 'No rows'}</p>}
                {orderedRows.map(({ row, sourceIndex, depth, hasChildren }) => <article key={sourceIndex} className="p-4 space-y-3">
                    {depth > 0 && <p className="text-xs text-neutral-500">Sub-line · level {depth}</p>}
                    {rowStyle(component, row) === 'informational' && infoChip}
                    <dl className="space-y-2">{[...cardHeadCols, ...cardBodyCols].map(c => {
                        const gap = cellGapAt(sourceIndex, row, c)
                        return <div key={c.field} className={gapClassFor(gap)} {...gapTitle(gap)}>
                            <dt className="text-xs text-neutral-500">{c.label ?? c.field}</dt>
                            <dd className="break-words min-w-0">{cellContent(row, c, sourceIndex)}</dd>
                        </div>
                    })}</dl>
                    <div className="flex flex-wrap gap-3">{traceButtons(row, sourceIndex)}
                        {isEdit && modalMode && !rowIsLocked(component, row) && <button type="button" className="min-h-11 text-blue-600" onClick={() => setEditing({ mode: 'edit', index: sourceIndex })}>Edit line</button>}
                        {isEdit && component.removable && !rowRemoveWithheld(component, row, hasChildren) && <button type="button" className="min-h-11 text-red-600" onClick={() => removeRow(sourceIndex)}>Remove line</button>}
                    </div>
                </article>)}
                {previewRows.map((row, i) => <article key={`preview-${i}`} className="p-4 opacity-70"><p>Added when saved</p><dl>{cols.map(c => <div key={c.field}><dt>{c.label ?? c.field}</dt><dd>{formatValue(row[c.field], childMeta.find(m => m.name === c.field)?.type, undefined, fmt)}</dd></div>)}</dl></article>)}
            </div> : <div className="marque-table-frame overflow-x-auto">
                <table className="marque-table w-full text-sm">
                    <thead className="bg-neutral-50 dark:bg-slate-900/50 border-b border-neutral-200 dark:border-slate-700">
                        <tr>
                            {cols.map((c, ci) => {
                                // AB#3953 — a drop target with a drag grip while configuring; unchanged idle.
                                const decoration = configurator.header(c.field, ci)
                                return (
                                    <th key={c.field} {...decoration.props} className={`px-3 py-2 text-xs font-medium text-neutral-500 dark:text-slate-400 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}${decoration.className ? ` ${decoration.className}` : ''}`}>
                                        {decoration.grip
                                            ? <div className="flex items-center gap-1"><span className="min-w-0 flex-1">{c.label ?? c.field}</span>{decoration.grip}</div>
                                            : (c.label ?? c.field)}
                                    </th>
                                )
                            })}
                            {traceEnabled && <th className="px-3 py-2 w-12" />}
                            {isEdit && modalMode && <th className="px-3 py-2 w-10" />}
                            {isEdit && component.removable && <th className="px-3 py-2 w-10" />}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-slate-700">
                        {rows.length === 0 && previewRows.length === 0 ? (
                            <tr><td colSpan={Math.max(cols.length, 1) + traceCol + (isEdit && modalMode ? 1 : 0) + (isEdit && component.removable ? 1 : 0)} className="px-3 py-4 text-center text-neutral-400 dark:text-slate-500 text-xs italic">{component.emptyText ?? 'No rows'}</td></tr>
                        ) : orderedRows.map(({ row, sourceIndex: i, depth, hasChildren }) => {
                            // AB#2718 — an informational row is one whose whole purpose is to be READ: the
                            // sales-kit header above its exploded components, a text note. Marked by an "Info"
                            // chip and italics as well as by colour, so it survives a monochrome print and a
                            // colour-blind reader — colour alone would carry the meaning for nobody who cannot
                            // see it. The dimming goes on each CELL, not the row: every td sets its own text
                            // colour, which beats anything inherited from the tr, so a row-level class dims
                            // only the action columns. The background likewise has to differ from the grid's
                            // own dark surface (bg-white dark:bg-slate-800) or it is invisible in dark mode.
                            const style = rowStyle(component, row)
                            const informational = style === 'informational'
                            return (
                            <tr key={i} className={informational ? 'bg-neutral-50 dark:bg-slate-700/40 italic' : undefined}>
                                {cols.map((c, ci) => {
                                    // Depth indents the FIRST authored column rather than adding a column of
                                    // its own: an extra column would have to be counted into the empty-state
                                    // colSpan and into every action-column arithmetic below (the AB#2603 trap),
                                    // and it would show up as a blank heading on every flat grid.
                                    const indent = ci === 0 && depth > 0 ? { paddingLeft: `${0.75 + depth * 1.25}rem` } : undefined
                                    // AB#3019 — a required cell left empty is marked WHERE IT IS, the
                                    // moment the row exists. A banner alone sends the user hunting
                                    // across a sixty-line grid for the one column that is blank.
                                    const gap = cellGapAt(i, row, c)
                                    return (
                                        <td key={c.field} style={indent}
                                            {...gapTitle(gap)}
                                            className={`px-3 py-2 ${informational ? 'text-neutral-500 dark:text-slate-400' : 'text-neutral-900 dark:text-slate-100'} ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}${gapClassFor(gap)}`}>
                                            {ci === 0 && informational && infoChip}
                                            {cellContent(row, c, i)}
                                        </td>
                                    )
                                })}
                                {traceEnabled && (
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        {traceButtons(row, i)}
                                    </td>
                                )}
                                {isEdit && modalMode && (
                                    <td className="px-3 py-2 text-right">
                                        {!rowIsLocked(component, row) && (
                                            <button type="button" onClick={() => setEditing({ mode: 'edit', index: i })} className="text-xs font-medium text-blue-600 hover:text-blue-800" aria-label="Edit line">Edit</button>
                                        )}
                                    </td>
                                )}
                                {isEdit && component.removable && (
                                    <td className="px-3 py-2 text-right">
                                        {/* AB#2627 — the cell stays (columns must align); only the control goes.
                                            Where the server refuses the delete regardless (AB#2589), not offering
                                            it is the AB#2601 rule: a button that can only fail reads as a bug.
                                            AB#2718 — but a rowRules seal has no server guard behind it, so it is
                                            NOT sufficient on its own; see rowRemoveWithheld. */}
                                        {!rowRemoveWithheld(component, row, hasChildren) && (
                                            <button type="button" onClick={() => removeRow(i)} className="text-xs text-red-500 hover:text-red-700" aria-label="Remove line">✕</button>
                                        )}
                                    </td>
                                )}
                            </tr>
                            )
                        })}
                        {/* AB#2640 — the rows the save WILL seed, previewed on the create form:
                            display-only (no edit, no remove, never in the draft), dimmed, and marked
                            so "these arrive on save" cannot be misread as "these exist". */}
                        {previewRows.map((row, i) => {
                            const actionCols = traceCol + (isEdit && modalMode ? 1 : 0) + (isEdit && component.removable ? 1 : 0)
                            return (
                                <tr key={`preview-${i}`} className="opacity-70">
                                    {cols.map(c => {
                                        const fd = childMeta.find(m => m.name === c.field)
                                        return (
                                            <td key={c.field} className={`px-3 py-2 text-neutral-500 dark:text-slate-400 italic ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}>
                                                {formatValue(row[c.field], fd?.type, fd?.customType, fmt)}
                                            </td>
                                        )
                                    })}
                                    {actionCols > 0 && (
                                        <td colSpan={actionCols} className="px-3 py-2 text-right">
                                            <span className="inline-flex items-center rounded bg-neutral-100 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500 dark:text-slate-400 whitespace-nowrap">Added when saved</span>
                                        </td>
                                    )}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>)
            }
            {/* AB#3019 — the same gaps, said in words. The cell markers answer "which value"; this
                names the lines so a sixty-row grid does not have to be scanned.

                Both blocks state a FACT about the document, never a prediction about the save. This
                renderer is shared by every module and cannot see the save process's rules: an entity's
                `required` flag enforces nothing on its own — the validate rules are the authored
                contract (MpackValidator.ValidateRequiredFieldsAreValidated) — so BillOfMaterial lines,
                TransactionTypeDef partner requirements and JournalEntry lines all declare required
                fields their save processes do not check, and telling those users the document "cannot
                be saved" would be the opposite of the truth. Saying what is missing is true everywhere;
                what happens next is the server's to say, and it says it in the save refusal. */}
            {blockingRows.length > 0 && (
                <div role="alert" className="border-t border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-2 text-xs text-red-800 dark:text-red-200">
                    {blockingRows.map(({ i, gaps }) => `Line ${lineLabelOf(rows[i], i)}: ${gaps.map(g => g.label).join(', ')}`).join(' · ')}
                    {' — '}required.
                </div>
            )}
            {advisoryRows.length > 0 && (
                <div role="status" className="border-t border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-2 text-xs text-amber-800 dark:text-amber-200">
                    {advisoryRows.map(({ i, gaps }) => `Line ${lineLabelOf(rows[i], i)}: ${gaps.map(g => g.label).join(', ')}`).join(' · ')}
                    {' — '}missing, but not required.
                </div>
            )}
            {isEdit && modalMode && editing && (
                <LineItemRowModal
                    // Remount per open so the local draft resets rather than leaking the previous row.
                    key={`${editing.mode}:${editing.index}`}
                    title={editing.mode === 'add' ? `Add ${component.title ?? 'line'}` : `Edit ${component.title ?? 'line'}`}
                    initialRow={editing.mode === 'add' ? {} : (rows[editing.index] ?? {})}
                    cols={allCols} childMeta={childMeta} EF={EF}
                    onSave={saveRow} onCancel={() => setEditing(null)}
                    fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById}
                    fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId} fmt={fmt}
                    parentRow={parentRow} />
            )}
            {/* AB#2521 / AB#2680 — the row's analysis. Each trace is rendered through the host's
                EXISTING seam for its kind rather than a second renderer of the same data: the stored
                shape is already exactly what that component reads, and two renderers of one shape
                drift apart. Bound through a synthetic one-key context so the seam's normal binding
                path is used unchanged. Re-read from the live row each render, so a row removed while
                the modal is open closes it instead of showing a trace for a line that no longer
                exists. */}
            {tracing !== null && (() => {
                const spec = traceSpecs.find(s => s.field === tracing.field)
                const Seam = spec ? traceSeams[spec.kind] : undefined
                if (!spec || !Seam) { return null }
                const line = readRowTrace(rows[tracing.index]?.[spec.field], spec.kind)
                if (line === null) { return null }
                return (
                    <Modal title={traceLabels(spec.title, component, rows[tracing.index] ?? {}, tracing.index).modal} onCancel={() => setTracing(null)}>
                        <Seam
                            component={{ ...component, type: spec.kind as ViewComponentType, traceBinding: '$__trace', title: spec.panelTitle }}
                            view={view}
                            context={{ __trace: line } as unknown as ViewContext} />
                    </Modal>
                )
            })()}
        </div>
    )
}

// AB#1924 — the inline status pill in the object-header. Renders the bound record's status field next to
// the title: a boolean active flag → an "Active" (success) / "Inactive" (neutral) pill; an enum/string
// status → the formatted value pill. Renders NOTHING when the field or its value is absent (guarded).
// AB#3281 — `tone` is the option's declared semantic status tone (resolved by the caller from the
// field's enum meta via resolveOptionTone). Declared tone wins; absent tone keeps the pre-3281
// default blue byte-for-byte. The label always stays — colour is never the only signal.
function HeaderStatusPill({ value, tone }: { value: unknown; tone?: StatusTone | undefined }): ReactNode {
    if (value === null || value === undefined || value === '') { return null }
    if (typeof value === 'boolean') {
        return value
            ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
            : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-slate-700 text-neutral-600 dark:text-slate-300">Inactive</span>
    }
    const label = formatValue(value, 'enum')
    if (label === '—') { return null }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tone ? tonePillClasses[tone] : 'bg-blue-100 text-blue-800'}`}>{label}</span>
}

// AB#1990 — in edit/new mode the pill becomes the status INPUT. The header is the status field's ONLY
// home (the generator filters it out of the form fields, AB#1924), so without this a required status
// enum is unsettable and a new record can never pass its save process's isNotNull validation. Renders a
// pill-shaped select: enum → the definition's values; boolean active flag → Active/Inactive. Falls back
// to the read-only pill when the field has NO meta (unknown field / meta still loading — editability and
// readOnly are unknowable, so don't offer an input) or the meta offers no options. The field's declared
// TYPE decides the control — a legacy boolean value on an enum-typed field gets the enum options, not
// Active/Inactive, so choosing one repairs the record with a valid enum value.
// Unset value shows a visible "Set <label>…" placeholder — never nothing.
function HeaderStatusSelect({ value, meta, onChange }: {
    value: unknown
    meta: ViewFieldMeta | undefined
    onChange: (v: unknown) => void
}): ReactNode {
    if (!meta) { return <HeaderStatusPill value={value} /> }
    const label = meta.label ?? meta.name
    const base = 'rounded-full border px-2.5 py-0.5 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500'
    if (meta.type === 'boolean') {
        const cur = value === true ? 'true' : value === false ? 'false' : ''
        const tone = cur === 'true' ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800'
            : cur === 'false' ? 'bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                : 'bg-white text-neutral-500 border-neutral-300 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600'
        return (
            <select aria-label={label} value={cur} className={`${base} ${tone}`}
                onChange={e => onChange(e.target.value === '' ? '' : e.target.value === 'true')}>
                {cur === '' && <option value="">Set {label.toLowerCase()}…</option>}
                <option value="true">Active</option>
                <option value="false">Inactive</option>
            </select>
        )
    }
    const options = enumOptions(meta?.enum)
    if (options.length === 0) { return <HeaderStatusPill value={value} /> }
    const cur = value === null || value === undefined ? '' : String(value)
    const known = options.some(o => o.value === cur)
    return (
        <select aria-label={label} value={cur} onChange={e => onChange(e.target.value)}
            className={`${base} ${cur ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800' : 'bg-white text-neutral-500 border-neutral-300 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600'}`}>
            {cur === '' && <option value="">Set {label.toLowerCase()}…</option>}
            {/* a stored value outside the definition's list is kept (mirrors the currency select) */}
            {cur !== '' && !known && <option value={cur}>{cur}</option>}
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    )
}

// AB#2441 — one label-over-value meta item in the object-header. Read-only always:
// meta context (company etc.) is immutable after create (AB#2346/2414). New-record fallback: a
// company field with no draft value yet (companyId is stamped at persist, never drafted) resolves
// its label from the scope company via the host's fetch-by-id loader; everything else renders from
// the record's resolved lookups → raw value.
function HeaderMetaItem({ record, field, meta, fmt, scopeCompanyId, fetchEntityById, resolveCompanyLabel, align = 'right' }: {
    align?: 'left' | 'right'
    record: Record<string, unknown>
    field: string
    meta: ViewFieldMeta | undefined
    fmt: FormatContext | undefined
    scopeCompanyId: string | undefined
    fetchEntityById: FetchEntityById | undefined
    resolveCompanyLabel: ((companyId: string) => string | undefined) | undefined
}) {
    const raw = record[field]
    const isCompanyRef = field === 'companyId' || meta?.lookupField?.sourceEntity === 'Company'
    const needsScopeFallback = (raw === null || raw === undefined || raw === '')
        && isCompanyRef && !!scopeCompanyId && !!fetchEntityById
    const [fallbackLabel, setFallbackLabel] = useState<string | undefined>(undefined)
    useEffect(() => {
        if (!needsScopeFallback) { setFallbackLabel(undefined); return }
        let cancelled = false
        fetchEntityById!('Company', meta?.lookupField?.displayField, scopeCompanyId!)
            .then(l => { if (!cancelled) { setFallbackLabel(l) } })
            .catch(() => { /* display-only — a failed resolve renders nothing */ })
        return () => { cancelled = true }
    }, [needsScopeFallback, fetchEntityById, scopeCompanyId, meta?.lookupField?.displayField])

    // AB#2655 — the host's synchronous company resolver wins for a company item: an entity that never
    // DECLARED a companyId field has no meta, no lookupField, so no server-resolved __lookups entry —
    // the raw GUID was all the chain below could ever produce. First term, so a declared+resolved
    // lookup label is only consulted when the host resolver misses (id outside the accessible set).
    const companyLabel = isCompanyRef && resolveCompanyLabel && typeof raw === 'string' && raw
        ? resolveCompanyLabel(raw)
        : undefined
    const value = companyLabel
        ?? lookupLabel(record, field)
        ?? (raw !== null && raw !== undefined && raw !== ''
            ? formatValue(raw, meta?.type, meta?.customType, fmt)
            : fallbackLabel)
    if (value === undefined || value === '') { return null }
    return (
        <div className={align === 'left' ? 'text-left' : 'text-right'}>
            {/* AB#2655 — an undeclared companyId has no meta either, so the label used to render the
                literal field name. A company item reads "Company". */}
            <div className="text-xs text-neutral-500 dark:text-slate-400">{meta?.label ?? (isCompanyRef ? 'Company' : field)}</div>
            <div className="text-sm font-medium text-neutral-900 dark:text-slate-100">{value}</div>
        </div>
    )
}

function ObjectHeaderSlot({ component, view, context, entityFields, editable, draft, onChange, fmt, scopeCompanyId, fetchEntityById, resolveCompanyLabel, viewport, pageHeader }: SlotProps) {
    const frame = (children: ReactNode) => pageHeader ? <div className="min-w-0">{children}</div> : card(children)
    // AB#1990 — edit mode resolves against the draft so the status select round-trips the user's choice.
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    // AB#1921 — when the header titles on an FK field, prefer the server-resolved label (record.__lookups)
    // over the raw FK value (a GUID): resolved label → raw value → static component.title. The same applies
    // to the subtitle field. (lookups map only the FK fields; non-FK fields fall straight through to raw.)
    const fieldRaw = component.field != null && record[component.field] != null ? String(record[component.field]) : ''
    // AB#2476 — a BOUND header resolves to its record's value and nothing else. It used to fall back to
    // component.title when the value was empty, and the generator sets that title to the field's DISPLAY
    // NAME — so a create form rendered a large bold heading reading "Account Record No" where a record
    // identifier belongs. That is guaranteed on every create of every entity with a numberRange business
    // identifier (AB#2434 assigns those at save, so they are ALWAYS empty on create). The static title is
    // now only used when the component binds no field at all; an empty bound header shows the '—'
    // placeholder the render path already had.
    const title = component.field
        ? (lookupLabel(record, component.field) ?? fieldRaw)
        : (component.title ?? '')
    const subRaw = component.subtitle != null && record[component.subtitle] != null ? String(record[component.subtitle]) : ''
    const subtitle = component.subtitle
        ? (lookupLabel(record, component.subtitle) ?? subRaw)
        : undefined
    // AB#1924 — the status moved INTO the header (no separate status-badge band): render a pill from
    // record[statusField] inline beside the title. Guarded — absent field/value renders nothing.
    // AB#1990 — in edit mode the pill is an editable select (see HeaderStatusSelect) unless the field
    // is readOnly; read mode keeps the display-only pill.
    const statusValue = component.statusField ? record[component.statusField] : undefined
    const fieldMeta = metaFor(component.dataBinding, view, entityFields)
    const statusMeta = component.statusField
        ? fieldMeta.find(m => m.name === component.statusField)
        : undefined
    // AB#2441 — the right-hand meta region: label-over-value items for each declared meta field,
    // then the status pill/select hard right. Without metaFields the header keeps its pre-2441
    // shape byte-for-byte (status inline beside the title).
    const metaFields = component.metaFields ?? []
    // AB#3281 — the option's declared semantic tone (undefined when the option carries none, which
    // keeps the pre-3281 pill exactly).
    const statusTone = resolveOptionTone(statusMeta?.enum, statusValue)
    const statusControl = component.statusField
        ? (isEdit && !statusMeta?.readOnly
            ? <HeaderStatusSelect value={statusValue} meta={statusMeta}
                onChange={v => onChange!(setBindingField(draft!, component.dataBinding, component.statusField!, v))} />
            : <HeaderStatusPill value={statusValue} tone={statusTone} />)
        : null
    if (metaFields.length === 0) {
        return frame(
            <>
                <div className="flex min-w-0 items-center gap-2 flex-wrap">
                    {component.icon && <Icon name={component.icon} size={22} className="text-neutral-500 dark:text-slate-400 flex-none" />}
                    <h1 className="marque-page-title text-xl font-bold text-neutral-900 dark:text-slate-100 min-w-0 break-words">{title || '—'}</h1>
                    {statusControl}
                </div>
                {subtitle && <p className="text-sm text-neutral-500 dark:text-slate-400 mt-1">{subtitle}</p>}
            </>,
        )
    }
    // AB#3280 — compact (phone) tier: the side-by-side header (title left, meta hard right) forces
    // horizontal scroll at 375px, so stack it — title + status pill on one row, subtitle under it,
    // then the meta items as a wrapping row. Same data, same components, different flow. Wide keeps
    // the pre-3280 layout byte-for-byte below.
    if (viewport === 'compact' || pageHeader) {
        return frame(
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {component.icon && <Icon name={component.icon} size={22} className="text-neutral-500 dark:text-slate-400 flex-none" />}
                    <h1 className="marque-page-title text-xl font-bold text-neutral-900 dark:text-slate-100 min-w-0">{title || '—'}</h1>
                    {statusControl}
                </div>
                {subtitle && <p className="text-sm text-neutral-500 dark:text-slate-400 mt-1">{subtitle}</p>}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2">
                    {metaFields.map(f => (
                        <HeaderMetaItem key={f} record={record} field={f} align="left"
                            meta={fieldMeta.find(m => m.name === f)}
                            fmt={fmt} scopeCompanyId={scopeCompanyId} fetchEntityById={fetchEntityById}
                            resolveCompanyLabel={resolveCompanyLabel} />
                    ))}
                </div>
            </div>,
        )
    }
    return card(
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    {component.icon && <Icon name={component.icon} size={22} className="text-neutral-500 dark:text-slate-400 flex-none" />}
                    <h1 className="marque-page-title text-xl font-bold text-neutral-900 dark:text-slate-100 truncate">{title || '—'}</h1>
                </div>
                {subtitle && <p className="text-sm text-neutral-500 dark:text-slate-400 mt-1">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-6 flex-none">
                {metaFields.map(f => (
                    <HeaderMetaItem key={f} record={record} field={f}
                        meta={fieldMeta.find(m => m.name === f)}
                        fmt={fmt} scopeCompanyId={scopeCompanyId} fetchEntityById={fetchEntityById}
                        resolveCompanyLabel={resolveCompanyLabel} />
                ))}
                {statusControl}
            </div>
        </div>,
    )
}

// AB#1915 — section-header: a full-width, NON-scoping filled heading band (icon + title) that
// delineates a group of fields. It owns no children — the fields beneath stay flat siblings in the
// grid; this is purely a visual divider with a heading.
// AB#1928/1944 — vertical rhythm + indent, scoped to section-header bands (a view with no section-headers
// is unaffected). WITHIN a field the label hugs its value (the InputSlot label is `mb-1` ≈ 4px). BETWEEN
// rows/bands: `mt-4` (1rem) opens the gap from the PRECEDING field group into the band; `mb-1` (0.25rem)
// sets the gap from the band DOWN to its own first field row. Combined with the grid's `gap-y-2` (0.5rem)
// row gap this yields, smallest → largest:
//   1. within-group rows         = gap-y-2              ≈ 8px
//   2. band → its first field    = gap-y-2 + mb-1       ≈ 12px
//   3. last field → next band    = gap-y-2 + mt-4       ≈ 24px
// AB#1944 — fields UNDER a band are also indented `pl-4` (16px) so they nest under the heading (see the
// `grouped` set in ComponentGrid); the object-header, the bands, and ungrouped fields up top stay flush.
// (The first band, right after the object-header card, getting the top margin too is acceptable rhythm.)
// AB#1937 — chevron affordance on a collapsible section-header band: chevron-down when expanded,
// chevron-right when collapsed. Inline SVG (Tabler/lucide-style) — the named Icon set is for authored
// glyphs, not UI affordances, so the chevron stays out of LUCIDE_ICON_MAP.
function SectionChevron({ collapsed }: { collapsed: boolean }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            className="text-neutral-500 dark:text-slate-400 flex-none">
            {collapsed ? <path d="M9 6l6 6-6 6" /> : <path d="M6 9l6 6 6-6" />}
        </svg>
    )
}

function SectionHeaderSlot({ component, sectionCollapsed, onToggleSection }: SlotProps) {
    const title = component.title?.trim()
    // AB#1937 — the whole band is the toggle target when the grid wired it collapsible (a top-level band).
    // A nested/standalone section-header with no toggle renders as a plain band (non-collapsible).
    const collapsible = !!onToggleSection
    const collapsed = !!sectionCollapsed
    const cls = 'marque-section-header w-full text-left mt-4 mb-1 bg-neutral-100 dark:bg-slate-700/50 border border-neutral-200 dark:border-slate-600 rounded-md px-3 py-2 flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-slate-100'
    const inner = (
        <>
            {collapsible && <SectionChevron collapsed={collapsed} />}
            {component.icon && <Icon name={component.icon} size={16} className="text-neutral-500 dark:text-slate-400 flex-none" />}
            {/* AB#1915 — with no title and no icon the band is an empty filled bar; show a muted placeholder
                (mirrors StaticTextSlot's empty state) so the author sees an unlabelled heading to fill in. */}
            {title ? <span>{title}</span> : <span className="text-neutral-400 dark:text-slate-500 italic font-normal">Section heading</span>}
        </>
    )
    if (collapsible) {
        return (
            <button type="button" aria-expanded={!collapsed} onClick={onToggleSection}
                className={`${cls} cursor-pointer hover:bg-neutral-200/70 dark:hover:bg-slate-700/50 transition-colors`}>
                {inner}
            </button>
        )
    }
    return <div className={cls}>{inner}</div>
}

function StatusBadgeSlot({ component, view, context, entityFields, stateColors }: SlotProps) {
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const status = component.field ? String(record[component.field] ?? '') : ''
    // AB#2412 — the stored code still keys stateColors (authored against the code); only the text shown
    // in the pill is humanised.
    const statusLabel = formatEnumLabel(status)
    // AB#3281 — the entity-declared semantic tone WINS over the legacy per-view stateColors hex map
    // (unemitted since AB#1924, so in practice the tone wins over nothing); a value with no declared
    // tone falls back to stateColors then to the default blue, exactly as before.
    const fieldTone = component.field
        ? resolveOptionTone(metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field)?.enum, status)
        : undefined
    const color = fieldTone ? undefined : stateColors?.[status]
    return (
        <div className="flex items-center gap-2">
            {component.title && <span className="text-xs font-medium text-neutral-500 dark:text-slate-400">{component.title}:</span>}
            {status
                ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${fieldTone ? tonePillClasses[fieldTone] : 'bg-blue-100 text-blue-800'}`} style={color ? { backgroundColor: `${color}22`, color } : undefined}>{statusLabel}</span>
                : <span className="text-xs text-neutral-400 dark:text-slate-500 italic">—</span>}
        </div>
    )
}

function TabContainerSlot(props: SlotProps) {
    const [active, setActive] = useState(0)
    const tabId = useId()
    const tabs = props.component.tabs ?? []
    const selected = Math.min(active, Math.max(0, tabs.length - 1))
    // Keep compact scroll-snap and touch targets with the filled, rounded tab treatment.
    const compact = props.viewport === 'compact'
    const tabCls = compact
        ? 'px-4 py-2 min-h-[44px] snap-start text-sm font-medium whitespace-nowrap'
        : 'px-4 py-2 text-sm font-medium whitespace-nowrap'
    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 overflow-hidden">
            {tabs.length > 0 && (
                <div role="tablist" aria-label={props.component.title ?? 'Related records'} className={`flex gap-1 p-1.5 pb-0 bg-neutral-50 dark:bg-slate-900 overflow-x-auto${compact ? ' snap-x' : ''}`}>
                    {tabs.map((tab, i) => (
                        <button key={tab.slot} id={`${tabId}-tab-${i}`} aria-controls={`${tabId}-panel`} type="button" role="tab" aria-selected={i === selected} tabIndex={i === selected ? 0 : -1} onClick={() => setActive(i)}
                            onKeyDown={e => {
                                const next = e.key === 'ArrowRight' ? (i + 1) % tabs.length : e.key === 'ArrowLeft' ? (i + tabs.length - 1) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : undefined
                                if (next === undefined) return
                                e.preventDefault()
                                setActive(next)
                                const nextTab = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]
                                nextTab?.focus()
                                nextTab?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
                            }}
                            className={`${tabCls} rounded-t-lg transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 ${i === selected ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'text-neutral-600 dark:text-slate-300 hover:bg-neutral-100 dark:hover:bg-slate-700 hover:text-neutral-900 dark:hover:text-slate-100'}`}>
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}
            <div id={`${tabId}-panel`} role="tabpanel" aria-labelledby={tabs.length ? `${tabId}-tab-${selected}` : undefined} tabIndex={0} className="p-4">
                {tabs.length === 0
                    ? <div className="text-xs text-neutral-400 dark:text-slate-500 italic text-center">No tabs configured</div>
                    : <SlotChildren slot={tabs[selected]?.slot} parentProps={props} />}
            </div>
        </div>
    )
}

// Renders the components assigned to a named tab slot (component.tabSlot === slot).
//
// AB#2215 — this goes through ComponentGrid, exactly like the top-level grid and container children.
// It previously rendered a flat `space-y-4` stack, which silently dropped three things a tab's
// contents are entitled to: the 24-column layout (so `col`/`colSpan` were ignored and every
// component spanned the full width instead of packing two-up), the `pl-4` indent under a
// section-header band (AB#1944), and the collapsible-band wiring (AB#1937). Harmless while tab
// content was aggregate field-sections; visibly wrong once views became section-header bands plus
// per-field components (AB#1915), which is how it surfaced on customers CustomerAccount.
function SlotChildren({ slot, parentProps }: { slot?: string | undefined; parentProps: SlotProps }) {
    const children = slot ? parentProps.gridComponents.filter(c => c.type !== 'tab-container' && c.tabSlot === slot) : []
    if (children.length === 0) {
        return <div className="text-xs text-neutral-400 dark:text-slate-500 italic text-center">Empty tab</div>
    }
    // Mirrors ContainerSlot: edits resolve against the draft, display against the context.
    const evalData = parentProps.editable && parentProps.draft ? parentProps.draft : parentProps.context
    return <ComponentGrid components={children} props={parentProps} evalData={evalData} />
}

function UserCalendarSlot({ component }: SlotProps) {
    // AB#3238 — the agenda without an injected userCalendarComponent (the designer, previews): a
    // static shape naming the window and the declared sources. No fetch here by design — the designer
    // canvas must never issue tenant data reads, and a host that wants the live agenda injects the seam.
    const sources = component.sources ?? []
    return card(
        <>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-1">{component.title ?? 'My agenda'}</h3>
            <div className="text-[10px] uppercase tracking-wide text-neutral-400 dark:text-slate-500 mb-3">
                {component.window === 'week' ? 'This week' : 'Today'}
            </div>
            <div className="space-y-2">
                <div className="flex gap-3 items-start">
                    <div className="w-2 h-2 rounded-full bg-neutral-300 dark:bg-slate-600 mt-1.5 flex-none" />
                    <div className="text-xs text-neutral-400 dark:text-slate-500 italic">your connected calendar</div>
                </div>
                {sources.map((s, i) => (
                    <div key={i} className="flex gap-3 items-start">
                        <div className="w-2 h-2 rounded-full bg-neutral-300 dark:bg-slate-600 mt-1.5 flex-none" />
                        <div className="text-xs text-neutral-400 dark:text-slate-500 italic">
                            {s.entity} · {s.dateField}{s.titleField ? ` · ${s.titleField}` : ''}
                        </div>
                    </div>
                ))}
            </div>
            <div className="text-[10px] text-neutral-400 dark:text-slate-500 italic mt-2">Your agenda loads in the app</div>
        </>,
    )
}

function TimelineSlot({ component, context, fmt }: SlotProps) {
    // AB#3285 — multi-event mode without an injected timelineComponent (the designer, previews): a
    // static placeholder naming the declared sources. No fetch here by design — the designer canvas
    // must never issue tenant data reads, and a host that wants the live feed injects the seam.
    if ((component.sources?.length ?? 0) > 0) {
        return card(
            <>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-3">{component.title ?? 'Timeline'}</h3>
                <div className="space-y-2">
                    {component.sources!.map((s, i) => (
                        <div key={i} className="flex gap-3 items-start">
                            <div className="w-2 h-2 rounded-full bg-neutral-300 dark:bg-slate-600 mt-1.5 flex-none" />
                            <div className="text-xs text-neutral-400 dark:text-slate-500 italic">
                                {s.entity} · {s.dateField}{s.titleField ? ` · ${s.titleField}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="text-[10px] text-neutral-400 dark:text-slate-500 italic mt-2">Events load in the app</div>
            </>,
        )
    }
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const dateValue = component.dateField ? record[component.dateField] : undefined
    return card(
        <>
            {component.title && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-3">{component.title}</h3>}
            {dateValue
                ? <div className="flex gap-3 items-start"><div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-none" /><div className="text-sm text-neutral-700 dark:text-slate-300">{formatValue(dateValue, 'datetime', undefined, fmt)}</div></div>
                : <div className="text-xs text-neutral-400 dark:text-slate-500 italic text-center">No entries</div>}
        </>,
    )
}

// AB#2449 — the address block. Two data shapes: legacy `addressFields[]` (flat field names off the
// record — hand-curated views, read-only, kept verbatim) and the composite `field` binding (the
// generator's output — which previously rendered "No address" because only addressFields was read).
// The composite path renders via per-country metadata (addressMetadata.ts) and gains an edit mode
// on the QuantityInputSlot pattern: the whole composite object round-trips through the draft.
// AB#2459 — the country a blank address opens on. Derived from the tenant's own BCP 47 locale (the
// region subtag of en-GB is GB), so nothing is hardcoded and no new configuration surface is needed.
// Undefined when the locale carries no region — the editor then falls back to the ZZ superset exactly
// as before. Company's own country would be the better source, but it is free text until AB#2467.
function localeDefaultCountry(locale: string | undefined): string | undefined {
    if (!locale) { return undefined }
    const region = /^[A-Za-z]{2,3}[-_]([A-Za-z]{2})(?:[-_]|$)/.exec(locale)?.[1]
    return region ? region.toUpperCase() : undefined
}

function AddressBlockSlot({ component, view, context, entityFields, editable, draft, onChange, fetchAddressSearch, addressMapComponent, fmt }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange && !!component.field
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const title = component.title ?? component.label ?? baseMeta?.label

    // Legacy flat-field shape — exactly the pre-2449 behaviour.
    if (!component.field && component.addressFields) {
        const flat = (component.addressFields ?? []).map(f => String(record[f] ?? '')).filter(Boolean)
        return card(
            <>
                {title && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-2">{title}</h3>}
                {flat.length > 0
                    ? <address className="not-italic text-sm text-neutral-700 dark:text-slate-300 space-y-0.5">{flat.map((l, i) => <div key={i}>{l}</div>)}</address>
                    : <div className="text-xs text-neutral-400 dark:text-slate-500 italic">No address</div>}
            </>,
        )
    }

    // AB#2467 — the editor itself now lives in ./AddressEditor so hand-written pages (Company, Site)
    // can render the SAME composite without going through the view model. This slot is the adapter:
    // it resolves the bound value and writes the whole composite back through the draft.
    const value = asRecord(component.field ? record[component.field] : undefined) as AddressValue
    return (
        <AddressEditor
            value={value}
            onChange={next => onChange!(setBindingField(draft!, component.dataBinding, component.field!, next))}
            editable={isEdit}
            title={title}
            required={baseMeta?.required}
            readOnly={baseMeta?.readOnly}
            showMap={component.showMap}
            defaultCountry={localeDefaultCountry(fmt?.locale)}
            fetchAddressSearch={fetchAddressSearch}
            addressMapComponent={addressMapComponent}
        />
    )
}

// AB#2393/2395 — the editor for a quantity-amount ({ amount, unit }). Before this, composites fell through
// to a full-width plain text-input with no unit control at all, so a material's dimensions and weights were
// six oversized boxes and nowhere to choose mm or kg. Amount and unit are edited side by side; read mode
// shows "12.5 mm".
const unitOptionsCache = new WeakMap<FetchUnitOptions, Promise<{ code: string; name?: string }[]>>()

function useUnitOptions(fetchUnitOptions: FetchUnitOptions | undefined) {
    const [options, setOptions] = useState<{ code: string; name?: string }[]>([])
    useEffect(() => {
        if (!fetchUnitOptions) { return }
        let cancelled = false
        let promise = unitOptionsCache.get(fetchUnitOptions)
        if (!promise) {
            // AB#2410 — a rejected fetch is evicted rather than cached. Caching the failure as an empty
            // array pinned every quantity field to an empty unit list for the rest of the session, with
            // no way to recover and nothing to show the user why.
            promise = fetchUnitOptions()
            promise.catch(() => { unitOptionsCache.delete(fetchUnitOptions) })
            unitOptionsCache.set(fetchUnitOptions, promise)
        }
        void promise.then(opts => { if (!cancelled) { setOptions(opts) } }).catch(() => { /* surfaced by the host loader */ })
        return () => { cancelled = true }
    }, [fetchUnitOptions])
    return options
}

const connectionOptionsCache = new WeakMap<FetchConnectionOptions, Promise<{ name: string; kind?: string; active?: boolean }[]>>()

function useConnectionOptions(fetchConnectionOptions: FetchConnectionOptions | undefined) {
    const [options, setOptions] = useState<{ name: string; kind?: string; active?: boolean }[]>([])
    useEffect(() => {
        if (!fetchConnectionOptions) { return }
        let cancelled = false
        let promise = connectionOptionsCache.get(fetchConnectionOptions)
        if (!promise) {
            // AB#2410's lesson, kept: a rejected fetch is EVICTED rather than cached. Caching the
            // failure would pin every connection field to an empty list for the rest of the session.
            promise = fetchConnectionOptions()
            promise.catch(() => { connectionOptionsCache.delete(fetchConnectionOptions) })
            connectionOptionsCache.set(fetchConnectionOptions, promise)
        }
        void promise.then(opts => { if (!cancelled) { setOptions(opts) } }).catch(() => { /* surfaced by the host loader */ })
        return () => { cancelled = true }
    }, [fetchConnectionOptions])
    return options
}

/// AB#2981 — picks one of the tenant's configured outbound connections BY NAME.
function ConnectionPickerSlot({ component, view, context, entityFields, editable, draft, onChange, fetchConnectionOptions }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const label = component.label ?? component.title ?? baseMeta?.label ?? component.field
    const connections = useConnectionOptions(fetchConnectionOptions)

    const raw = component.field ? record[component.field] : undefined
    const value = raw == null ? '' : String(raw)

    const labelRow = label
        ? (
            <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">
                {label}{isEdit && baseMeta?.required && <span className="text-red-500 ml-0.5">*</span>}
            </div>
        )
        : null

    if (!isEdit) {
        // Read mode shows the stored NAME verbatim, not a resolved label: the name is what the
        // resolver looks up, and showing a friendly label would hide a connection that has since been
        // renamed or deleted — exactly the case an operator needs to see.
        return (
            <div>
                {labelRow}
                <div className="text-sm text-neutral-900 dark:text-slate-100">{value || '—'}</div>
            </div>
        )
    }

    // An option list that does not contain the stored value still has to show it, or opening the
    // record would silently re-point it at the first connection in the list on the next save.
    const known = connections.some(c => c.name === value)
    return (
        <div>
            {labelRow}
            <select
                value={value}
                onChange={e => {
                    if (!component.field) { return }
                    // setBindingField, like every other editor here — writing through a shallow copy
                    // of the draft would miss nested bindings.
                    onChange!(setBindingField(draft!, component.dataBinding, component.field, e.target.value || null))
                }}
                className="marque-input w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-neutral-900 dark:text-slate-100"
                data-testid={`connection-picker-${component.field ?? 'unbound'}`}
            >
                <option value="">— none —</option>
                {!known && value !== '' && (
                    <option value={value}>{value} (not configured)</option>
                )}
                {connections.map(c => (
                    <option key={c.name} value={c.name}>
                        {c.name}{c.kind ? ` — ${c.kind}` : ''}{c.active === false ? ' (inactive)' : ''}
                    </option>
                ))}
            </select>
        </div>
    )
}

const userOptionsCache = new WeakMap<FetchUserOptions, Promise<{ id: string; displayName: string }[]>>()

function useUserOptions(fetchUserOptions: FetchUserOptions | undefined) {
    const [options, setOptions] = useState<{ id: string; displayName: string }[]>([])
    useEffect(() => {
        if (!fetchUserOptions) { return }
        let cancelled = false
        let promise = userOptionsCache.get(fetchUserOptions)
        if (!promise) {
            // AB#2410's lesson, kept: a rejected fetch is EVICTED rather than cached, so a transient
            // directory failure does not pin every user field to an empty list for the session.
            promise = fetchUserOptions()
            promise.catch(() => { userOptionsCache.delete(fetchUserOptions) })
            userOptionsCache.set(fetchUserOptions, promise)
        }
        void promise.then(opts => { if (!cancelled) { setOptions(opts) } }).catch(() => { /* surfaced by the host loader */ })
        return () => { cancelled = true }
    }, [fetchUserOptions])
    return options
}

/// AB#3291 — picks a user from the tenant's directory. Stores the user's ID; shows the display name.
function UserPickerSlot({ component, view, context, entityFields, editable, draft, onChange, fetchUserOptions }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const label = component.label ?? component.title ?? baseMeta?.label ?? component.field
    const users = useUserOptions(fetchUserOptions)

    const raw = component.field ? record[component.field] : undefined
    const value = raw == null ? '' : String(raw)
    // Server-resolved label (record.__lookups, the AB#1914/AB#1921 pattern) wins — it resolves even
    // when the directory fetch has not landed; the fetched directory is the fallback and covers the
    // edit path, where the record may be a fresh draft with no server labels at all.
    const resolved = lookupLabel(record, component.field)
        ?? users.find(u => u.id === value)?.displayName

    const labelRow = label
        ? (
            <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">
                {label}{isEdit && baseMeta?.required && <span className="text-red-500 ml-0.5">*</span>}
            </div>
        )
        : null

    if (!isEdit) {
        // Read mode shows the display NAME, falling back to the raw id when nothing resolves it —
        // a deactivated or deleted user must stay visible as something, never as a blank.
        return (
            <div>
                {labelRow}
                <div className="text-sm text-neutral-900 dark:text-slate-100">{resolved ?? (value || '—')}</div>
            </div>
        )
    }

    // A stored id absent from the directory (deactivated user) still has to be offered, or opening
    // the record would silently re-point the field at the first listed user on the next save —
    // the connection-picker's rule, and it matters more here: owners leave companies.
    const known = users.some(u => u.id === value)
    return (
        <div>
            {labelRow}
            <select
                value={value}
                onChange={e => {
                    if (!component.field) { return }
                    onChange!(setBindingField(draft!, component.dataBinding, component.field, e.target.value || null))
                }}
                className="marque-input w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-neutral-900 dark:text-slate-100"
                data-testid={`user-picker-${component.field ?? 'unbound'}`}
                aria-label={label}
            >
                <option value="">— none —</option>
                {!known && value !== '' && (
                    <option value={value}>{resolved ?? value} (no longer in the directory)</option>
                )}
                {users.map(u => (
                    <option key={u.id} value={u.id}>{u.displayName}</option>
                ))}
            </select>
        </div>
    )
}

function QuantityInputSlot({ component, view, context, entityFields, editable, draft, onChange, fetchUnitOptions }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const label = component.label ?? component.title ?? baseMeta?.label ?? component.field
    const units = useUnitOptions(fetchUnitOptions)

    const raw = component.field ? record[component.field] : undefined
    const parts = asRecord(raw)
    const amount = parts.amount
    const unit = parts.unit == null ? '' : String(parts.unit)

    const emit = (next: { amount?: unknown; unit?: unknown }) => {
        if (!onChange || !component.field) { return }
        const merged = { ...parts, ...next }
        // Clearing both halves clears the field rather than storing an empty shell.
        const cleared = (merged.amount === '' || merged.amount == null) && !merged.unit
        onChange(setBindingField(draft!, component.dataBinding, component.field, cleared ? null : merged))
    }

    // AB#2410 — no width utility here. This string is shared by the amount input and the unit select,
    // and a `w-full` in it collided with the select's `w-28`: both are width utilities, `w-full` won in
    // the generated CSS, and with `shrink-0` the select claimed the whole row while the amount input
    // (flex-basis 0) collapsed to nothing. Widths are set per control below.
    const inputCls = 'rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-neutral-900 dark:text-slate-100 disabled:opacity-60'

    return (
        <div>
            {label && (
                <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">
                    {label}{isEdit && baseMeta?.required && <span className="text-red-500 ml-0.5">*</span>}
                </div>
            )}
            {isEdit && component.field
                ? (
                    <div className="flex gap-2">
                        {/* min-w-0 lets the amount input actually shrink inside the flex row rather than
                            being held at its intrinsic width. */}
                        <input type="number" className={`${inputCls} flex-1 min-w-0`} disabled={baseMeta?.readOnly}
                            step={requiresWholeQuantity(unit) ? 1 : 'any'}
                            aria-invalid={!!wholeQuantityError(parts)}
                            value={amount == null ? '' : String(amount)}
                            onChange={e => emit({ amount: e.target.value === '' ? null : Number(e.target.value) })} />
                        <select className={`${inputCls} w-28 shrink-0`} disabled={baseMeta?.readOnly}
                            value={unit} onChange={e => emit({ unit: e.target.value || null })}>
                            <option value="">—</option>
                            {/* The stored unit may predate the catalogue (or be a retired row); keep it selectable. */}
                            {unit && !units.some(u => u.code === unit) && <option value={unit}>{unit}</option>}
                            {units.map(u => <option key={u.code} value={u.code}>{u.code}</option>)}
                        </select>
                        {wholeQuantityError(parts) && <span role="alert" className="text-xs text-red-600">{wholeQuantityError(parts)}</span>}
                    </div>
                )
                : <div className="text-sm text-neutral-900 dark:text-slate-100">
                    {amount == null || amount === '' ? '—' : `${String(amount)}${unit ? ` ${unit}` : ''}`}
                </div>}
        </div>
    )
}

// AB#2445 — the GB sort-code control: three 2-digit boxes writing ONE canonical 6-digit string.
// Auto-advance when a box fills; Backspace on an empty box focuses the previous one (without eating
// its digit); paste of "12-34-56" / "12 34 56" / "123456" into any box distributes across all three;
// digits only (inputMode numeric for the mobile pad). Per-box aria-labels; the ONE combined error
// line is the parent's concern.
function SortCodeBoxes({ value, disabled, onChange }: {
    value: string
    disabled: boolean | undefined
    onChange: (canonical: string) => void
}) {
    const refs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)]
    const digits = value.replace(/\D/g, '')
    const boxes = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)]

    const emit = (parts: string[]) => onChange(parts.join(''))

    const handleChange = (i: number, raw: string) => {
        const clean = raw.replace(/\D/g, '')
        if (clean.length > 2) {
            // A paste landed here — distribute the whole string across the three boxes.
            const all = clean.slice(0, 6)
            emit([all.slice(0, 2), all.slice(2, 4), all.slice(4, 6)])
            const target = Math.min(2, Math.ceil(all.length / 2) - 1)
            refs[target]?.current?.focus()
            return
        }
        const next = [...boxes]
        next[i] = clean
        emit(next)
        if (clean.length === 2 && i < 2) { refs[i + 1]?.current?.focus() }
    }

    const handleKeyDown = (i: number, e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && boxes[i] === '' && i > 0) {
            e.preventDefault()
            refs[i - 1]?.current?.focus()
        }
    }

    return (
        <div className="flex items-center gap-1.5">
            {boxes.map((box, i) => (
                <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={6}
                    aria-label={`Sort code digits ${i * 2 + 1} and ${i * 2 + 2}`}
                    className="marque-input w-12 text-center rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 py-1 text-sm text-neutral-900 dark:text-slate-100 disabled:opacity-60"
                    value={box} disabled={disabled}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)} />
            ))}
        </div>
    )
}

// AB#2445 — per-country routing-field label for the non-GB single input (the routing PAIR is
// country-driven; GB alone gets the 3-box sort code).
const ROUTING_LABELS: Record<string, string> = {
    US: 'Routing number (ABA)',
    DE: 'Bankleitzahl',
    AU: 'BSB',
    IE: 'Bank code',
}

// AB#2445 — masked read display helpers: last-4 for the account number, fully-masked sort code,
// IBAN as country+check plus the last 4. Full values are AB#2447's reveal concern.
function maskAccountNumber(v: string): string { return v.length > 4 ? `••••${v.slice(-4)}` : '••••' }
function maskIban(v: string): string { return v.length > 8 ? `${v.slice(0, 4)} •••• ${v.slice(-4)}` : `${v.slice(0, 4)} ••••` }

// AB#2447 — a member value may arrive as the masked sentinel {__masked, last4}; helpers keep the
// block honest for both shapes (raw string pre-masking / designer preview, sentinel from the API).
function isSentinel(v: unknown): v is { __masked: true; last4?: string } {
    return typeof v === 'object' && v !== null && (v as Record<string, unknown>).__masked === true
}

// AB#4187 — scalar sensitive values use the same audited door as composite fields.
// Parents key this component by record snapshot and access scope. Never copy a reveal into
// the draft/cache, and discard pending results when that identity is unmounted.
function MaskedFieldValue({ value, field, meta, record, binding, view, fetchRevealField, fmt }: {
    value: unknown; field: string; meta: ViewFieldMeta | undefined; record: Record<string, unknown>
    binding: string | undefined; view: ViewDefinition; fetchRevealField: FetchRevealField | undefined
    fmt: FormatContext | undefined
}) {
    const [result, setResult] = useState<{ value: unknown } | null>(null)
    const [busy, setBusy] = useState(false)
    const [failed, setFailed] = useState(false)
    const alive = useRef(true)
    useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
    const root = (binding ?? '').replace(/^\$/, '')
    const entityType = view.context?.find(c => c.name === root)?.entityType
    const id = typeof record.id === 'string' ? record.id : undefined
    const canReveal = !!meta?.sensitive && !!fetchRevealField && !!entityType && !!id
    const reveal = async () => {
        if (!canReveal || busy) return
        setBusy(true)
        setFailed(false)
        try {
            const revealed = await fetchRevealField!(entityType!, id!, field)
            if (alive.current) {
                if (isSentinel(revealed)) setFailed(true)
                else setResult({ value: revealed })
            }
        } catch { if (alive.current) setFailed(true) }
        finally { if (alive.current) setBusy(false) }
    }
    return <div className="text-sm text-neutral-900 dark:text-slate-100 whitespace-pre-wrap break-words">
        {result ? enumOptionLabel(meta?.enum, result.value) ?? formatValue(result.value, meta?.type, meta?.customType, fmt, true) : formatValue(value)}
        {!result && canReveal && <button type="button" disabled={busy} onClick={() => { void reveal() }}
            className="ml-2 text-sm text-blue-600 dark:text-blue-400 underline disabled:opacity-60"
            aria-label={`Reveal ${meta?.label ?? field}`}>{busy ? 'Revealing…' : 'Reveal'}</button>}
        {failed && <span role="alert" className="ml-2 text-sm text-red-600 dark:text-red-400">Unable to reveal this field.</span>}
    </div>
}

function BankAccountBlockSlot({ component, view, context, entityFields, editable, draft, onChange, fetchRevealField }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const label = component.label ?? component.title ?? baseMeta?.label ?? component.field
    // AB#2447 — an entitled reveal replaces the served (masked) parts for THIS render only; the
    // value lives in component state, never in a cache.
    const [revealedParts, setRevealedParts] = useState<Record<string, unknown> | null>(null)
    const servedParts = asRecord(component.field ? record[component.field] : undefined)
    const parts = revealedParts ?? servedParts
    const member = (name: string): string => {
        const v = parts[name]
        if (v == null || isSentinel(v)) { return '' }
        return String(v)
    }
    const maskedMember = (name: string): string | undefined => {
        const v = parts[name]
        return isSentinel(v) ? `••••${v.last4 ?? ''}` : undefined
    }
    // The reveal targets the bound record (its id) + this field; entityType comes from the view's
    // context contract entry the binding roots at.
    const bindingRoot = (component.dataBinding ?? '').replace(/^\$/, '').split('.')[0]
    const boundEntityType = view.context?.find(c => c.name === bindingRoot)?.entityType
    const recordId = typeof record['id'] === 'string' ? record['id'] : undefined
    const canReveal = !revealedParts && !!fetchRevealField && !!baseMeta?.sensitive
        && !!boundEntityType && !!recordId && !!component.field
        && Object.values(servedParts).some(isSentinel)
    const [revealFailed, setRevealFailed] = useState(false)
    const reveal = () => {
        fetchRevealField!(boundEntityType!, recordId!, component.field!)
            .then(v => setRevealedParts(asRecord(v)))
            .catch(() => setRevealFailed(true))
    }

    const country = member('country')
    const isGb = country === 'GB'

    const emit = (next: Record<string, unknown>) => {
        if (!onChange || !component.field) { return }
        const merged: Record<string, unknown> = { ...parts, ...next }
        for (const k of Object.keys(merged)) {
            if (merged[k] === '' || merged[k] == null) { delete merged[k] }
        }
        // Clearing every member clears the field rather than storing an empty shell.
        onChange(setBindingField(draft!, component.dataBinding, component.field,
            Object.keys(merged).length === 0 ? null : merged))
    }

    const inputCls = 'w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-neutral-900 dark:text-slate-100 disabled:opacity-60'
    const fieldLabelCls = 'text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1'
    const sortCodeIncomplete = isGb && member('bankCode') !== '' && member('bankCode').replace(/\D/g, '').length !== 6

    if (!isEdit) {
        // Row values: a revealed/raw string renders per the local masking rules; a served sentinel
        // renders its last-4. Once revealed, the full values show.
        const numberish = (name: string, localMask: (v: string) => string): string =>
            maskedMember(name) ?? (revealedParts ? member(name) : localMask(member(name)))
        const rows: Array<[string, string]> = []
        if (member('bankName')) { rows.push(['Bank', member('bankName')]) }
        if (member('accountName')) { rows.push(['Account Name', member('accountName')]) }
        const routingLabel = isGb ? 'Sort Code' : (ROUTING_LABELS[country] ?? 'Bank / routing code')
        if (member('bankCode') || maskedMember('bankCode')) {
            rows.push([routingLabel, maskedMember('bankCode') ?? (revealedParts ? member('bankCode') : (isGb ? '••-••-••' : '••••'))])
        }
        if (member('accountNumber') || maskedMember('accountNumber')) { rows.push(['Account Number', numberish('accountNumber', maskAccountNumber)]) }
        if (member('iban') || maskedMember('iban')) { rows.push(['IBAN', numberish('iban', maskIban)]) }
        if (member('bic') || maskedMember('bic')) { rows.push(['BIC', maskedMember('bic') ?? member('bic')]) }
        if (country) { rows.push(['Country', COUNTRIES.find(c => c.code === country)?.name ?? country]) }
        return card(
            <>
                <div className="flex items-center justify-between mb-2">
                    {label && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200">{label}</h3>}
                    {canReveal && (
                        <button type="button" onClick={reveal}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            aria-label="Reveal full bank details">
                            Reveal
                        </button>
                    )}
                </div>
                {revealFailed && (
                    <div className="text-xs text-red-600 dark:text-red-400 mb-2">The details could not be revealed.</div>
                )}
                {rows.length === 0
                    ? <div className="text-xs text-neutral-400 dark:text-slate-500 italic">No bank details</div>
                    : (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            {rows.map(([k, v]) => (
                                <div key={k}>
                                    <dt className="text-xs text-neutral-500 dark:text-slate-400">{k}</dt>
                                    <dd className="text-neutral-900 dark:text-slate-100">{v}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
            </>,
        )
    }

    const knownCountry = COUNTRIES.some(c => c.code === country)
    return card(
        <>
            {label && (
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-2">
                    {label}{baseMeta?.required && <span className="text-red-500 ml-0.5">*</span>}
                </h3>
            )}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <div className={fieldLabelCls}>Bank</div>
                    <input type="text" className={inputCls} disabled={baseMeta?.readOnly}
                        value={member('bankName')} onChange={e => emit({ bankName: e.target.value })} />
                </div>
                <div>
                    <div className={fieldLabelCls}>Account Name</div>
                    <input type="text" className={inputCls} disabled={baseMeta?.readOnly}
                        value={member('accountName')} onChange={e => emit({ accountName: e.target.value })} />
                </div>
                <div>
                    <div className={fieldLabelCls}>Country</div>
                    <select className={inputCls} disabled={baseMeta?.readOnly}
                        value={country} onChange={e => emit({ country: e.target.value })}>
                        <option value="">—</option>
                        {country && !knownCountry && <option value={country}>{country}</option>}
                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <div className={fieldLabelCls}>{isGb ? 'Sort Code' : (ROUTING_LABELS[country] ?? 'Bank / routing code')}</div>
                    {isGb
                        ? <SortCodeBoxes value={member('bankCode')} disabled={baseMeta?.readOnly}
                            onChange={canonical => emit({ bankCode: canonical })} />
                        : <input type="text" className={inputCls} disabled={baseMeta?.readOnly}
                            value={member('bankCode')} onChange={e => emit({ bankCode: e.target.value })} />}
                    {sortCodeIncomplete && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">A sort code is six digits.</div>
                    )}
                </div>
                <div>
                    <div className={fieldLabelCls}>Account Number</div>
                    <input type="text" inputMode="numeric" className={inputCls} disabled={baseMeta?.readOnly}
                        value={member('accountNumber')} onChange={e => emit({ accountNumber: e.target.value })} />
                </div>
                <div>
                    <div className={fieldLabelCls}>IBAN</div>
                    <input type="text" className={inputCls} disabled={baseMeta?.readOnly}
                        value={member('iban')} onChange={e => emit({ iban: e.target.value })} />
                </div>
                <div>
                    <div className={fieldLabelCls}>BIC</div>
                    <input type="text" className={inputCls} disabled={baseMeta?.readOnly}
                        value={member('bic')} onChange={e => emit({ bic: e.target.value })} />
                </div>
            </div>
        </>,
    )
}

function RichTextSlot({ component, context }: SlotProps) {
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const text = (component.field ? String(record[component.field] ?? '') : '').replace(/<[^>]*>/g, '').trim()
    return card(
        <>
            {component.title && <h3 className="text-sm font-semibold text-neutral-700 dark:text-slate-200 mb-2">{component.title}</h3>}
            {text ? <p className="text-sm text-neutral-700 dark:text-slate-300 whitespace-pre-wrap">{text}</p> : <p className="text-sm text-neutral-400 dark:text-slate-500 italic">—</p>}
        </>,
    )
}

function MetricSlot({ component, view, context, entityFields, fmt, onNavigateToProcess, navigationScope, viewport }: SlotProps) {
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const fd = metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field)
    const value = component.field ? record[component.field] : undefined
    // AB#1958 — honour the component's own `format` (e.g. "money") as a fallback when there is no
    // entity-field meta type. A dashboard metric binds an aggregate series entry which has no entityType,
    // so `fd` is undefined and the authored format:"money" was silently ignored (raw number rendered).
    // AB#2300 — richer KPI tile: big tabular value, optional unit suffix, optional coloured callout line.
    const co = component.callout
    const coNum = co?.field ? formatValue(record[co.field], undefined, undefined, fmt) : null
    const coText = co?.text ? interpolateTokens(co.text, record) : ''
    const toneCls: Record<string, string> = {
        good: 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
        warn: 'border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300',
        critical: 'border-red-400 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-300',
        info: 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-300',
    }
    const label = component.label ?? component.title ?? fd?.label ?? component.field

    // AB#3989 — the tile presentation: the host's shell owns the title row (it shows the widget's
    // label), so the tile is the VALUE BLOCK alone — big value, unit, and the sub line with its callout —
    // bottom-aligned in whatever height the host gives it (tile.css .view-tile-metric, the prototype's
    // .metric). No card(), no label, none of the dashboard-metric-* vocabulary, so a host's module-
    // dashboard CSS (band surface, tile padding, clamp() sizes) has nothing to reach. A drill-down
    // makes the block the button, with the prototype's open-arrow in its corner; because the label
    // sits outside the button here, the button's accessible name carries label, value and callout.
    if (isTile(view)) {
        const formatted = formatValue(value, fd?.type ?? component.format ?? (typeof value === 'number' ? 'number' : undefined), fd?.customType, fmt)
        const hasCallout = !!co && !!(coNum || coText)
        const calloutText = hasCallout ? `${coNum ?? ''}${coNum && coText ? ' ' : ''}${coText}` : ''
        const block = (
            <>
                <span data-status-tone={isStatusTone(component.tone) ? component.tone : undefined}
                    className={`view-tile-metric-value${isStatusTone(component.tone) ? ` ${tonePillClasses[component.tone]}` : ''}`}>
                    {formatted}
                    {component.unit && <span className="view-tile-metric-unit">{component.unit}</span>}
                </span>
                <span className="view-tile-metric-sub">
                    {hasCallout && (
                        <span className="view-tile-callout" data-tone={co.tone ?? 'info'}>
                            {coNum && <b>{coNum}</b>}{coNum && coText ? ' ' : ''}{coText}
                        </span>
                    )}
                </span>
            </>
        )
        const tileDrill = component.drillDown?.process
        if (tileDrill && onNavigateToProcess) {
            const name = `${label ?? ''}: ${formatted}${component.unit ? ` ${component.unit}` : ''}${calloutText ? `, ${calloutText}` : ''}`
            return (
                <button type="button" className="view-tile-metric" aria-label={name}
                    onClick={() => (navigationScope ? onNavigateToProcess(tileDrill, navigationScope) : onNavigateToProcess(tileDrill))}>
                    {block}
                    <TileArrow />
                </button>
            )
        }
        return <div className="view-tile-metric">{block}</div>
    }

    const dashboard = view.kind === 'dashboard'
    const content = (
        <>
            <div className={`text-xs font-medium text-neutral-500 dark:text-slate-400 ${dashboard ? 'dashboard-metric-label' : ''}`}><span>{label}</span>{dashboard && component.drillDown?.process && onNavigateToProcess && <span aria-hidden="true">{'↗\uFE0E'}</span>}</div>
            <div data-status-tone={isStatusTone(component.tone) ? component.tone : undefined} className={`${dashboard ? 'dashboard-metric-value tabular-nums tracking-tight' : `mt-1 break-words font-bold tracking-tight tabular-nums ${viewport === 'compact' ? 'text-xl' : 'text-3xl'}`} ${isStatusTone(component.tone) ? tonePillClasses[component.tone] : 'text-neutral-900 dark:text-slate-100'}`}>
                {formatValue(value, fd?.type ?? component.format ?? (typeof value === 'number' ? 'number' : undefined), fd?.customType, fmt)}
                {component.unit && <span className="ml-1.5 text-xs font-semibold text-neutral-500 dark:text-slate-400">{component.unit}</span>}
            </div>
            {/* AB#2415 — the callout is optional, so a tile that has one was ~32px taller than its
                neighbours and the row rendered ragged. The grid cell already stretches (a real CSS
                grid with align-items:normal), so it is the CARD that has to fill it: `h-full`.
                `flex flex-col` + this spacer then pin the callout to the bottom so several tiles'
                callouts line up. The spacer is deliberate — `mt-auto` alongside the callout's own
                `mt-2` would be two margin-top utilities whose winner is decided by generated-CSS
                order, not by the className string. */}
            {co && (coNum || coText) && <div className="grow" />}
            {co && (coNum || coText) && (
                <div data-tone={co.tone ?? 'info'} className={dashboard ? `dashboard-metric-callout ${isStatusTone(co.tone) ? tonePillClasses[co.tone] : ''}` : `mt-2 rounded border-l-2 px-2 py-1 text-xs font-medium ${toneCls[co.tone ?? 'info'] ?? (isStatusTone(co.tone) ? tonePillClasses[co.tone] : toneCls.info)}`}>
                    {coNum && <span className="font-bold tabular-nums">{coNum}</span>}{coNum && coText ? ' ' : ''}{coText}
                </div>
            )}
        </>
    )

    // AB#2889 — the declared drill-down (ADR-0044 §13's missing half): with a target AND a host
    // navigation callback, the tile IS a button — real button semantics, so Enter/Space and focus
    // come from the platform rather than a re-implementation. Without either (the designer preview,
    // or a tile that declares no target) the card renders exactly as it always has. `text-left`
    // because a button centres its content by default and the tile is a left-aligned card.
    // Deliberately NO aria-label (review find): on a button it REPLACES the contents as the
    // accessible name, so a screen reader heard the label and never the count — and the count is
    // the entire point of the tile. The contents (label, value, unit, callout) ARE the name.
    // AB#3967 — the host's navigationScope rides along as the second argument; with none given the
    // call is the one-argument call it always was, so pre-scope hosts see no change at all.
    const drillTarget = component.drillDown?.process
    if (drillTarget && onNavigateToProcess) {
        return (
            <button
                type="button"
                onClick={() => (navigationScope ? onNavigateToProcess(drillTarget, navigationScope) : onNavigateToProcess(drillTarget))}
                className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-4 h-full w-full flex flex-col text-left cursor-pointer transition-colors hover:border-neutral-300 hover:bg-neutral-50 dark:hover:border-slate-500 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
                {content}
            </button>
        )
    }

    return card(content, 'h-full flex flex-col')
}

// AB#2763 — a read-only table over a JSON-array FIELD: a field declared string/text that holds JSON
// (a job run's outputs). Exists because "what the run did NOT do" — the skip list with reasons — is
// the point of a run summary, and a JSON blob in a text area is not a summary anyone reads. Generic:
// any diagnostic JSON field renders the same way. Strings render as a single column; objects take
// their columns from tableColumns (or, with none declared, from the first row's keys).
function JsonTableSlot({ component, view, context, viewport, tableLayoutStore }: SlotProps) {
    const [filter, setFilter] = useState('')
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const raw = component.field ? record?.[component.field] : undefined

    let parsed: unknown = raw
    if (typeof raw === 'string' && raw.trim().length > 0) {
        try { parsed = JSON.parse(raw) } catch { parsed = undefined }
    }
    const source = parsed
    if (component.jsonPath && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const seg of component.jsonPath.split('.')) {
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                parsed = (parsed as Record<string, unknown>)[seg]
            } else { parsed = undefined; break }
        }
    }

    const allRows = Array.isArray(parsed) ? parsed
        : component.jsonObjectAsRow && parsed && typeof parsed === 'object' ? [parsed] : undefined
    // AB#3953 / AB#3954 — hooks sit ABOVE the early returns below. The declared tableColumns are the
    // defaults; a table with none derives them from the first row's keys — a data-driven default set
    // the reconciliation copes with (a stored field no row carries drops, a new key splices in).
    const objectRows = !!allRows && allRows.every(r => r && typeof r === 'object' && !Array.isArray(r))
    const declaredColumns: readonly TableColumn[] = component.tableColumns && component.tableColumns.length > 0
        ? component.tableColumns
        : objectRows && allRows && allRows.length > 0 ? Object.keys(allRows[0] as Record<string, unknown>).map(k => ({ field: k })) : EMPTY_COLUMNS
    const identity = tableIdentity(view, component)
    const layout = useTableLayout(identity, declaredColumns, EMPTY_COLUMNS, useTableLayoutStore(tableLayoutStore))
    const configurator = useColumnConfigurator({ identity, layout, viewport, labelOf: f => declaredColumns.find(c => c.field === f)?.label ?? f })
    if (component.comparisonReport) return <ComparisonReport rows={allRows ?? []} options={component.comparisonReport}
        title={component.title} emptyText={component.emptyText} exportCsv={component.exportCsv} />
    const rows = component.filterable && filter ? allRows?.filter(row => JSON.stringify(row).toLowerCase().includes(filter.toLowerCase())) : allRows
    // Idle (no cog) the heading is byte-for-byte what it was; the cog shares its line when present.
    const heading = configurator.cog
        ? <div className="mb-1 flex items-center justify-between gap-2">
            {component.title && <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{component.title}</div>}
            {configurator.cog}
        </div>
        : component.title
            ? <div className="mb-1 text-sm font-medium text-neutral-700 dark:text-neutral-300">{component.title}</div>
            : null
    if (!allRows || allRows.length === 0) {
        return (
            <div>
                {heading}
                <div className="rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                    {component.emptyText ?? 'Nothing to show.'}
                </div>
            </div>
        )
    }

    const displayRows = rows ?? []
    // AB#3953 — the user's effective columns (order + hidden) over the declared / derived defaults.
    const columns: Array<{ field: string; label?: string; hrefTemplate?: string; valueLookup?: { path: string; keyField: string } }> = objectRows
        ? layout.columns
        : [{ field: '', label: component.label ?? '' }]

    const cell = (value: unknown): string => {
        if (value === null || value === undefined) { return '' }
        if (typeof value === 'object') { return JSON.stringify(value) }
        return String(value)
    }

    const valueOf = (row: unknown, column: typeof columns[number]): unknown => {
        if (column.valueLookup) {
            const map = asRecord(resolveBinding(`$source.${column.valueLookup.path}`, { source }))
            const key = resolveBinding(`$row.${column.valueLookup.keyField}`, { row })
            return map?.[String(key)] ?? 0
        }
        if (!objectRows) return row
        return Object.prototype.hasOwnProperty.call(row, column.field)
            ? (row as Record<string, unknown>)[column.field] : resolveBinding(`$row.${column.field}`, { row })
    }
    const downloadCsv = () => {
        const escape = (value: unknown) => `"${(typeof value === 'string' ? value.replace(/^[\s]*[=+@-]/, "'$&") : cell(value)).replaceAll('"', '""')}"`
        const csv = [columns.map(c => escape(c.label ?? c.field)).join(','),
            ...displayRows.map(row => columns.map(c => escape(valueOf(row, c))).join(','))].join('\r\n')
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
        const link = document.createElement('a'); link.href = url; link.download = 'report.csv'; link.click(); URL.revokeObjectURL(url)
    }

    const totals = new Map<string, Record<string, number>>()
    for (const row of displayRows) {
        const group = (component.totalGroupFields ?? []).map(field => cell(resolveBinding(`$row.${field}`, { row }))).join(' · ')
        for (const name of component.totalFields ?? []) {
            const column = columns.find(c => c.field === name)
            if (!column) continue
            const amount = Number(valueOf(row, column))
            if (!Number.isFinite(amount)) continue
            const sum = totals.get(group) ?? {}
            sum[name] = (sum[name] ?? 0) + amount
            totals.set(group, sum)
        }
    }

    return (
        <div>
            {heading}
            {component.filterable && <input aria-label={`Filter ${component.title ?? 'table'}`} value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter rows…" className="marque-input mb-2 mr-2 rounded border border-neutral-300 px-3 py-1 text-sm dark:border-slate-600 dark:bg-slate-800" />}
            {component.exportCsv && <button type="button" onClick={downloadCsv} className="marque-button mb-2 rounded border border-neutral-300 px-3 py-1 text-sm dark:border-slate-600">Export CSV</button>}
            {[...totals].map(([group, sums]) => <p key={group} className="mb-2 text-sm font-medium">{group}{group && ': '}{Object.entries(sums).map(([name, amount]) => `${columns.find(c => c.field === name)?.label ?? name} ${amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}`).join(' · ')}</p>)}
            {configurator.frame(<div className="marque-table-frame overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
                <table className="marque-table w-full text-sm">
                    <thead>
                        <tr className="border-b border-neutral-200 bg-neutral-50 text-left dark:border-neutral-800 dark:bg-neutral-900">
                            {columns.map((c, i) => {
                                // AB#3953 — drop target + drag grip while configuring; unchanged idle.
                                const decoration = configurator.header(c.field, i)
                                return (
                                    <th key={c.field} {...decoration.props} className={`px-3 py-1.5 font-medium text-neutral-600 dark:text-neutral-400${decoration.className ? ` ${decoration.className}` : ''}`}>
                                        {decoration.grip
                                            ? <div className="flex items-center gap-1"><span className="min-w-0 flex-1">{c.label ?? c.field}</span>{decoration.grip}</div>
                                            : (c.label ?? c.field)}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {displayRows.map((r, i) => (
                            <tr key={i} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                                {objectRows
                                    ? columns.map(c => {
                                        const value = cell(valueOf(r, c))
                                        let missing = false
                                        const href = c.hrefTemplate?.replace(/\{([^{}]+)\}/g, (_, field: string) => {
                                            const id = resolveBinding(`$row.${field}`, { row: r })
                                            if (id === null || id === undefined || id === '') { missing = true }
                                            return encodeURIComponent(String(id ?? ''))
                                        })
                                        const local = href?.startsWith('/') && !href.startsWith('//') && !missing
                                        return <td key={c.field} className="px-3 py-1.5 text-neutral-800 dark:text-neutral-200">
                                            {local ? <a href={href} className="text-blue-600 underline">{value}</a> : value}
                                        </td>
                                    })
                                    : <td className="px-3 py-1.5 text-neutral-800 dark:text-neutral-200">{cell(r)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>)}
        </div>
    )
}

function AlertBannerSlot({ component }: SlotProps) {
    const styles: Record<string, string> = {
        info: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-300',
        warning: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300',
        success: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300',
        error: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300',
    }
    return <div className={`rounded-md border px-3 py-2 text-sm ${styles[component.alertVariant ?? 'info']}`}>{component.message ?? component.title ?? ''}</div>
}

// AB#1860 — static-text: a literal caption/label. `{field}` tokens interpolate against the
// bound/inherited context. Display-only (a composed string cannot two-way bind).
function StaticTextSlot({ component, context, view }: SlotProps) {
    const raw = component.text ?? ''
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const text = interpolateTokens(raw, record)
    return <div className={`${view.kind === 'dashboard' ? 'dashboard-caption ' : ''}text-sm text-neutral-700 dark:text-slate-300 whitespace-pre-wrap`}>{text || <span className="text-neutral-400 dark:text-slate-500 italic">Text</span>}</div>
}

function SeparatorSlot({ component, view }: SlotProps) {
    if (view.kind === 'dashboard' && component.separatorLabel) {
        return <h2 className="dashboard-section-title">{component.separatorLabel}</h2>
    }
    const border = component.separatorStyle === 'dashed' ? 'border-dashed' : component.separatorStyle === 'dotted' ? 'border-dotted' : 'border-solid'
    return (
        <div className="flex items-center gap-3 py-1">
            <hr className={`flex-1 border-t ${border} border-neutral-300 dark:border-slate-600`} />
            {component.separatorLabel && <span className="text-xs text-neutral-400 dark:text-slate-500">{component.separatorLabel}</span>}
            {component.separatorLabel && <hr className={`flex-1 border-t ${border} border-neutral-300`} />}
        </div>
    )
}

function ImageViewerSlot({ component, context }: SlotProps) {
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const src = component.field ? String(record[component.field] ?? '') : ''
    return card(
        src
            ? <img src={src} alt={component.imageAlt ?? ''} style={{ height: component.imageHeight ?? 160 }} className="object-contain" />
            : <div className="text-xs text-neutral-400 dark:text-slate-500 italic text-center" style={{ height: component.imageHeight ?? 160 }}>No image</div>,
    )
}

function ProgressBarSlot({ component, context }: SlotProps) {
    const record = asRecord(resolveBinding(component.dataBinding, context))
    const value = component.field ? Number(record[component.field] ?? 0) : 0
    const max = component.progressMax ?? 100
    const pct = Math.max(0, Math.min(100, (value / max) * 100))
    return card(
        <>
            {component.title && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{component.title}</div>}
            <div className="h-2 w-full rounded-full bg-neutral-100 dark:bg-slate-700 overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${pct}%` }} /></div>
            <div className="text-[11px] text-neutral-400 dark:text-slate-500 mt-1">{value} / {max}</div>
        </>,
    )
}

// AB#2290 — segmented pipeline bar: one horizontal bar split into segments sized by each row's value,
// with a legend below. Binds a context COLLECTION (dataBinding) an aggregate stage produced — the same
// [{key,value}] shape the chart consumes — so a dashboard can show a status/lifecycle pipeline at a
// glance. Palette matches the chart's series colours; dark-ready from the start.
const SEGMENT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16']
    .map((color, i) => `var(--chart-series-${i + 1}, ${color})`)
function SegmentedBarSlot({ component, view, context }: SlotProps) {
    const raw = resolveBinding(component.dataBinding, context)
    const labelField = component.labelField ?? 'key'
    const valueField = component.valueField ?? 'value'
    const rows = Array.isArray(raw) ? raw as Array<Record<string, unknown>> : []
    const segs = rows.map((r, i) => {
        const n = Number(r[valueField])
        const colors = SEGMENT_COLORS
        return { label: String(r[labelField] ?? ''), value: Number.isFinite(n) ? n : 0, color: colors[i % colors.length] }
    })
    const total = segs.reduce((s, x) => s + x.value, 0)
    // AB#3989 — the tile presentation: bar and legend, vertically centred in the tile, no card and no
    // title (the prototype's .seg). The bar is one accessible image naming every segment.
    if (isTile(view)) {
        return (
            <div className="view-tile-segments">
                {total === 0 ? <p className="view-tile-empty">No data</p> : (
                    <>
                        <div className="view-tile-segments-bar" role="img" aria-label={segs.map(s => `${s.label} ${s.value}`).join(', ')}>
                            {segs.filter(s => s.value > 0).map((s, i) => (
                                <span key={i} title={`${s.label}: ${s.value}`} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
                            ))}
                        </div>
                        <ul className="view-tile-legend">
                            {segs.map((s, i) => (
                                <li key={i}><span className="view-tile-swatch" style={{ background: s.color }} />{s.label} <b>{s.value}</b></li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        )
    }
    return card(
        <>
            {component.title && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-2">{component.title}</div>}
            {total === 0 ? (
                <div className="text-xs text-neutral-400 dark:text-slate-500 italic py-2">No data</div>
            ) : (
                <>
                    <div className="flex h-5 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-slate-700">
                        {segs.filter(s => s.value > 0).map((s, i) => (
                            <div key={i} title={`${s.label}: ${s.value}`} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
                        ))}
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
                        {segs.map((s, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5 text-xs text-neutral-600 dark:text-slate-300">
                                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
                                {s.label}
                                <b className="tabular-nums text-neutral-900 dark:text-slate-100">{s.value}</b>
                            </span>
                        ))}
                    </div>
                </>
            )}
        </>,
    )
}

// AB#2303 — bar-list: labelled horizontal bars (label | track | right-aligned value) from a
// {key,value} collection an aggregate stage produced. Cleaner than a recharts horizontal bar for
// top-N / breakdown lists; matches the dashboard prototype's DashBar.
function BarListSlot({ component, view, context, fmt }: SlotProps) {
    const raw = resolveBinding(component.dataBinding, context)
    const labelField = component.labelField ?? 'key'
    const valueField = component.valueField ?? 'value'
    const rows = Array.isArray(raw) ? raw as Array<Record<string, unknown>> : []
    const items = rows.map(r => {
        const value = Number(readDataField(r, valueField))
        return { label: String(readDataField(r, labelField) ?? ''), value: Number.isFinite(value) ? value : 0 }
    })
    const max = items.reduce((largest, item) => Math.max(largest, Math.abs(item.value)), 1)
    const fmtVal = (v: number) => formatValue(v, component.valueFormat === 'money' ? 'money' : 'number', undefined, fmt)
    // AB#3989 — the tile presentation: the prototype's .barrow — label | track | value on one line —
    // centred in the tile, no card and no title. Rows that do not fit are clipped by the host, never
    // scrolled (a bar list is read at a glance; a scrolling one is a table).
    if (isTile(view)) {
        return (
            <div className="view-tile-barlist">
                {items.length === 0 ? <p className="view-tile-empty">No data</p> : items.map((it, i) => (
                    <div key={i} className="view-tile-barrow">
                        <span className="view-tile-barrow-label" title={it.label}>{it.label}</span>
                        <span className="view-tile-bar-track" aria-hidden="true"><span className="view-tile-bar-fill" style={{ width: `${(Math.abs(it.value) / max) * 100}%` }} /></span>
                        <span className="view-tile-barrow-value">{fmtVal(it.value)}</span>
                    </div>
                ))}
            </div>
        )
    }
    return card(
        <>
            {component.title && <div className="mb-4 text-sm font-semibold text-neutral-800 dark:text-slate-200">{component.title}</div>}
            {items.length === 0 ? (
                <div className="py-2 text-xs italic text-neutral-400 dark:text-slate-500">No data</div>
            ) : (
                <div className="flex flex-col gap-4">
                    {items.map((it, i) => (
                        <div key={i} className="min-w-0">
                            <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
                                <span className="min-w-0 break-words text-neutral-700 dark:text-slate-300">{it.label}</span>
                                <span className="ml-auto font-semibold tabular-nums text-neutral-900 dark:text-slate-100">{fmtVal(it.value)}</span>
                            </div>
                            <div aria-hidden="true" className="h-2 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-slate-700">
                                <div className="h-full rounded-full bg-blue-500 dark:bg-blue-400" style={{ width: `${(Math.abs(it.value) / max) * 100}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>, 'h-full',
    )
}

// AB#2302 — data-table: a dashboard table binding a context collection with typed cells (money /
// percent / signed delta / status badge / RAG age), for cost-review-queue style tables the plain
// renderer cannot express.
const DT_TONE: Record<string, string> = {
    good: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    warn: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    critical: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
    info: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
}
function DataTableSlot({ component, view, context, fmt, viewport, tableLayoutStore }: SlotProps) {
    const raw = resolveBinding(component.dataBinding, context)
    const rows = Array.isArray(raw) ? raw as Array<Record<string, unknown>> : []
    // AB#3953 / AB#3954 — the user's own column layout over the declared tableColumns.
    const defaults = component.tableColumns ?? EMPTY_COLUMNS
    const identity = tableIdentity(view, component)
    const layout = useTableLayout(identity, defaults, EMPTY_COLUMNS, useTableLayoutStore(tableLayoutStore))
    const cols = layout.columns
    const configurator = useColumnConfigurator({ identity, layout, viewport, labelOf: f => defaults.find(c => c.field === f)?.label ?? f })
    // Idle (no cog) the title line is byte-for-byte what it was; the cog shares it when present.
    const titleLine = configurator.cog
        ? <div className="mb-2 flex items-center justify-between gap-2">
            {component.title && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400">{component.title}</div>}
            {configurator.cog}
        </div>
        : component.title && <div className="mb-2 text-xs font-medium text-neutral-500 dark:text-slate-400">{component.title}</div>
    const cell = (col: { type?: string; badgeTone?: string }, v: unknown) => {
        const n = Number(v)
        if (col.type === 'money') return <span className="tabular-nums">{formatValue(v, 'money', undefined, fmt)}</span>
        if (col.type === 'percent') return <span className="tabular-nums">{Number.isFinite(n) ? n.toFixed(1) + '%' : ''}</span>
        if (col.type === 'delta') return <span className={`font-medium tabular-nums ${n >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{Number.isFinite(n) ? (n >= 0 ? '+' : '') + n.toFixed(1) + '%' : ''}</span>
        if (col.type === 'badge') return <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${DT_TONE[col.badgeTone ?? 'info']}`}>{String(v ?? '')}</span>
        if (col.type === 'age') {
            // Accept either a numeric day count or an ISO timestamp (e.g. the folded-in createdAt), from
            // which we derive days-since. RAG bands: <4d good, 4-6d warn, >=7d critical.
            const days = Number.isFinite(n) ? n : (() => { const ms = Date.parse(String(v)); return Number.isFinite(ms) ? Math.floor((Date.now() - ms) / 86400000) : NaN })()
            const t = days >= 7 ? 'critical' : days >= 4 ? 'warn' : 'good'
            return <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${DT_TONE[t]}`}>{Number.isFinite(days) ? days + 'd' : String(v ?? '')}</span>
        }
        return <span>{String(v ?? '')}</span>
    }
    const stacked = (
        <div className="space-y-3">{rows.map((row, ri) => <dl key={ri} className="rounded border p-3 space-y-2">{cols.map((col, ci) => <div key={ci} className="flex justify-between gap-4"><dt className="text-xs text-neutral-500 dark:text-slate-400">{col.label ?? col.field}</dt><dd className="text-sm text-right break-words min-w-0">{cell(col, row[col.field])}</dd></div>)}</dl>)}</div>
    )
    const table = (
        <table className="marque-table w-full text-sm">
            <thead>
                <tr className="border-b border-neutral-200 dark:border-slate-700">
                    {cols.map((c, i) => {
                        // AB#3953 — drop target + drag grip while configuring; unchanged idle.
                        const decoration = configurator.header(c.field, i)
                        return (
                            <th key={c.field} {...decoration.props} className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-slate-500 ${c.align === 'right' ? 'text-right' : 'text-left'}${decoration.className ? ` ${decoration.className}` : ''}`}>
                                {decoration.grip
                                    ? <div className="flex items-center gap-1"><span className="min-w-0 flex-1">{c.label ?? c.field}</span>{decoration.grip}</div>
                                    : (c.label ?? c.field)}
                            </th>
                        )
                    })}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, ri) => (
                    <tr key={ri} className="border-b border-neutral-100 last:border-0 dark:border-slate-800">
                        {cols.map((c, ci) => <td key={ci} className={`px-2 py-1.5 text-neutral-700 dark:text-slate-300 ${c.align === 'right' || c.type === 'money' || c.type === 'delta' || c.type === 'percent' ? 'text-right' : 'text-left'}`}>{cell(c, r[c.field])}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    )
    // AB#3989 — the tile presentation: no card and no title (the host's shell carries it); the column
    // cog, when there is one, sits in a slim tools row; the ROWS scroll inside the tile (tile.css
    // .view-tile-scroll, the prototype's .tablewrap) so a long table never grows the widget and the
    // tile itself never scrolls. The phone tier keeps its stacked cards, scrolling the same way.
    if (isTile(view)) {
        return (
            <div className="view-tile-table">
                {configurator.cog && <div className="view-tile-tools">{configurator.cog}</div>}
                {configurator.frame(
                    <div className="view-tile-scroll">
                        {rows.length === 0 ? <div className="view-tile-empty">No data</div> : viewport === 'compact' ? stacked : table}
                    </div>,
                )}
            </div>
        )
    }
    if (viewport === 'compact') return card(<>
        {titleLine}
        {configurator.frame(rows.length === 0 ? <div className="py-2 text-xs italic text-neutral-400 dark:text-slate-500">No data</div> : stacked)}
    </>)
    return card(
        <>
            {titleLine}
            {configurator.frame(rows.length === 0 ? (
                <div className="py-2 text-xs italic text-neutral-400 dark:text-slate-500">No data</div>
            ) : (
                <div className="marque-table-frame overflow-x-auto">
                    {table}
                </div>
            ))}
        </>,
    )
}

// ── AB#2541 — structured-field editors ───────────────────────────────────────────────────────────
//
// Several pricing fields are declared as `string` but hold JSON, and were authored by typing that JSON
// into a text area — with labels that said so ("Dimensions (JSON)"). A pricing rule's `dimensions` is
// the worst case: it defines what the rule MATCHES, so it is on the critical path of every rule anyone
// authors, and it failed silently in two ways. A mistyped dimension key ("customergroup") matched
// nothing and fell back to list price; an id typed where a code belongs did the same (AB#2524, a real
// live defect). Both are structurally impossible once the key comes from a list and the value from a
// picker.
//
// These slots keep JSON as the STORAGE format — the engine, PricingConfigLoader (which parses
// object-or-string) and the AB#2170 SAP importer all read it, and existing tenant rows must keep
// working untouched. Only the authoring surface changes.
//
// Values are read tolerantly (an object/array already parsed, OR a string holding one) and written
// back as a compact JSON string, matching what the loader accepts either way.

/** Parse a field that may hold an object already, or a string containing one. Anything else ⇒ {}. */
function asJsonObject(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) { return value as Record<string, unknown> }
    if (typeof value === 'string' && value.trim() !== '') {
        try {
            const parsed = JSON.parse(value) as unknown
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) { return parsed as Record<string, unknown> }
        } catch { /* not JSON yet — treat as empty rather than destroying what is there */ }
    }
    return {}
}

/** Parse a field that may hold an array already, or a string containing one. Anything else ⇒ []. */
function asJsonArray(value: unknown): unknown[] {
    if (Array.isArray(value)) { return value as unknown[] }
    if (typeof value === 'string' && value.trim() !== '') {
        try {
            const parsed = JSON.parse(value) as unknown
            if (Array.isArray(parsed)) { return parsed as unknown[] }
        } catch { /* as above */ }
    }
    return []
}

/**
 * Serialise back to the field. An EMPTY structure writes `undefined`, never `"{}"` or `"[]"` — an
 * empty dimensions map means "catch-all" to the engine, and writing an empty literal would persist a
 * value where absent is meaningful.
 */
function toJsonField(value: Record<string, unknown> | unknown[]): string | undefined {
    const empty = Array.isArray(value) ? value.length === 0 : Object.keys(value).length === 0;
    return empty ? undefined : JSON.stringify(value)
}

const ROW_BTN = 'text-xs px-2 py-1 rounded border border-neutral-300 dark:border-slate-600 text-neutral-600 dark:text-slate-300 hover:border-blue-400 hover:text-blue-600'

// ── AB#2687 — dimension-rows match operators ─────────────────────────────────────────────────────
// The stored shape is the determination engine's DeterminationBinding, whose `eq` case is sugared to
// a bare string — so a rule that only matches exactly stores the SAME flat map the pricing engine has
// always read, and a module that declares no `operators` is byte-identical to before.

const OPERATOR_LABELS: Record<string, string> = { eq: 'is', prefix: 'starts with', in: 'one of', range: 'between' }

function bindingOp(value: unknown): string {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const op = (value as Record<string, unknown>)['op']
        if (typeof op === 'string' && op.length > 0) { return op }
    }
    return 'eq'
}

// The one piece of text a row carries across an operator change, so switching operator refines a
// half-written rule instead of blanking it.
function bindingComparand(value: unknown): string {
    if (typeof value === 'string') { return value }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const o = value as Record<string, unknown>
        if (typeof o['value'] === 'string') { return o['value'] }
        if (Array.isArray(o['values'])) { return o['values'].map(String).join(', ') }
        if (o['min'] !== undefined && o['min'] !== null) { return String(o['min']) }
        return ''
    }
    return value === undefined || value === null ? '' : String(value)
}

function bindingBound(value: unknown, key: 'min' | 'max'): string {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const v = (value as Record<string, unknown>)[key]
        if (v !== undefined && v !== null) { return String(v) }
    }
    return ''
}

function splitList(text: string): string[] {
    return text.split(',').map(x => x.trim()).filter(x => x.length > 0)
}

function toBinding(op: string, comparand: string): unknown {
    switch (op) {
        case 'prefix': return { op, value: comparand }
        case 'in': return { op, values: splitList(comparand) }
        case 'range': {
            const n = Number(comparand)
            return comparand.trim() !== '' && Number.isFinite(n) ? { op, min: n } : { op }
        }
        default: return comparand
    }
}

function toRangeBinding(min: string, max: string): unknown {
    const out: Record<string, unknown> = { op: 'range' }
    if (min.trim() !== '' && Number.isFinite(Number(min))) { out['min'] = Number(min) }
    if (max.trim() !== '' && Number.isFinite(Number(max))) { out['max'] = Number(max) }
    return out
}

// Read-only prose. A rule list and a decision trace are read by people who did not author the rule,
// so "starts with SW1" has to be on the screen — not a JSON fragment they have to decode.
export function describeBinding(value: unknown): string {
    switch (bindingOp(value)) {
        case 'prefix': return `starts with ${bindingComparand(value)}`
        case 'in': {
            const values = (value as Record<string, unknown>)['values']
            return Array.isArray(values) && values.length > 0 ? `one of ${values.map(String).join(', ')}` : 'one of (none)'
        }
        case 'range': {
            const min = bindingBound(value, 'min')
            const max = bindingBound(value, 'max')
            if (min !== '' && max !== '') { return `between ${min} and ${max}` }
            if (min !== '') { return `${min} or more` }
            if (max !== '') { return `${max} or less` }
            return 'any value'
        }
        default: return String(value ?? '')
    }
}

/**
 * AB#2541 — `dimension-rows`: the match key of a pricing rule or promotion, authored as rows of
 * (dimension, value) instead of a JSON object.
 *
 * The dimension vocabulary is AUTHORED on the component (`dimensionOptions`), each entry optionally
 * carrying a `lookup` so the value control becomes the right picker — a material picker that submits
 * the material CODE, a customer picker that submits the account CODE. A dimension with no lookup gets
 * a plain text input. Extra options may additionally be merged from view context via
 * `optionsBinding`, so a module can offer its tenant-declared PricingDimension rows without this
 * component needing a fetch seam of its own.
 *
 * AB#2687 — `operators`, when authored, adds a per-row operator select. See the binding helpers
 * above for the stored shape; a module that declares none keeps exactly the AB#2541 behaviour.
 */
function DimensionRowsSlot({ component, editable, draft, onChange, fetchEntityOptions, fetchEntityById, fetchLookupOptions, scopeCompanyId, context }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const current = asJsonObject(component.field ? record[component.field] : undefined)
    const label = component.label ?? component.title ?? component.field

    type DimOption = NonNullable<ViewComponent['dimensionOptions']>[number]
    const authored: DimOption[] = component.dimensionOptions ?? []

    // AB#2708 — options FETCHED from a company-scoped entity, for the RECORD's company. See
    // `optionsEntity` in viewTypes for why the optionsBinding/nav-process route cannot serve one.
    const optionsEntity = component.optionsEntity
    const oeEntity = optionsEntity?.entity
    const oeValueField = optionsEntity?.valueField ?? 'dimensionKey'
    const oeLabelField = optionsEntity?.labelField ?? 'displayName'
    const [fetched, setFetched] = useState<DimOption[]>([])
    useEffect(() => {
        if (!oeEntity || !fetchEntityOptions) { setFetched([]); return }
        let ignore = false
        fetchEntityOptions(oeEntity, oeLabelField, oeValueField, undefined, scopeCompanyId)
            .then(rows => {
                if (ignore) { return }
                setFetched(rows
                    .filter(r => typeof r.value === 'string' && r.value.length > 0)
                    .map(r => ({ key: r.value, label: r.label || r.value })))
            })
            // A vocabulary that cannot be loaded must not blank the one already on screen.
            .catch(() => { /* keep whatever is showing */ })
        return () => { ignore = true }
    }, [fetchEntityOptions, oeEntity, oeValueField, oeLabelField, scopeCompanyId])

    const fromContext = (resolveBinding(component.optionsBinding, context) as unknown[] | undefined) ?? []
    const extra: DimOption[] = Array.isArray(fromContext)
        ? fromContext
            .filter(r => r && typeof r === 'object')
            .map(r => r as Record<string, unknown>)
            .filter(r => typeof r['dimensionKey'] === 'string' && r['active'] !== false)
            .map(r => ({ key: r['dimensionKey'] as string, label: (r['displayName'] as string) ?? (r['dimensionKey'] as string) }))
        : []
    // Authored keys win, then the fetched vocabulary, then anything a binding staged. Deliberately a
    // MERGE, not a fallback: the shipped keys are always offered and the tenant's rows ADD to them.
    const options = [...authored, ...[...fetched, ...extra].filter(
        (e, i, all) => !authored.some(a => a.key === e.key) && all.findIndex(x => x.key === e.key) === i)]
    const operators = component.operators ?? []
    // AB#2696 — in-progress text of any "one of" row, keyed by dimension. Held only while the field has
    // focus; the stored binding remains the parsed list.
    const [listText, setListText] = useState<Record<string, string>>({})

    const entries = Object.entries(current)
    const write = (next: Record<string, unknown>) => {
        onChange!(setBindingField(draft!, component.dataBinding, component.field!, toJsonField(next)))
    }

    if (!isEdit || !component.field) {
        return (
            <div>
                {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
                {entries.length === 0
                    ? <div className="text-sm text-neutral-400 dark:text-slate-500 italic">Applies to everything</div>
                    : <ul className="text-sm space-y-0.5">
                        {entries.map(([k, v]) => (
                            <li key={k} className="flex gap-2">
                                <span className="text-neutral-500 dark:text-slate-400">{options.find(o => o.key === k)?.label ?? k}</span>
                                <span className="font-medium">{describeBinding(v)}</span>
                            </li>
                        ))}
                    </ul>}
            </div>
        )
    }

    return (
        <div>
            {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
            <div className="space-y-1.5">
                {entries.map(([key, value], i) => {
                    const def = options.find(o => o.key === key)
                    const op = bindingOp(value)
                    // The value control is a normal EditField driven by a synthesised meta, so a dimension
                    // declaring a lookup gets the SAME searchable picker every FK field uses — and it
                    // submits the lookup's valueField, which is the code the engine matches on. Only the
                    // exact operator can use a picker: a prefix or a range names a class of values, not
                    // one existing record, so those fall back to plain input (AB#2687).
                    const valueMeta: ViewFieldMeta = def?.lookup && op === 'eq'
                        ? { name: key, type: 'entity-ref', lookupField: def.lookup }
                        : { name: key, type: 'string' }
                    return (
                        <div key={`${key}-${i}`} className="flex gap-1.5 items-start" data-testid={`dimension-row-${i}`}>
                            <select
                                className={`${V3_INPUT_CLS} max-w-[40%]`}
                                value={key}
                                aria-label="Dimension"
                                onChange={e => {
                                    const nextKey = e.target.value
                                    if (nextKey === key) { return }
                                    // Rebuild in order so the row does not jump position when its key changes.
                                    const next: Record<string, unknown> = {}
                                    for (const [k, v] of entries) { next[k === key ? nextKey : k] = k === key ? '' : v }
                                    write(next)
                                }}
                            >
                                {!options.some(o => o.key === key) && <option value={key}>{key}</option>}
                                {options.map(o => (
                                    <option key={o.key} value={o.key} disabled={o.key !== key && key in current && o.key in current}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                            {operators.length > 0 && (
                                <select
                                    className={`${V3_INPUT_CLS} max-w-[28%]`}
                                    value={op}
                                    aria-label={`How ${def?.label ?? key} matches`}
                                    data-testid={`dimension-operator-${i}`}
                                    onChange={e => write({ ...current, [key]: toBinding(e.target.value, bindingComparand(value)) })}
                                >
                                    {!operators.includes(op) && <option value={op}>{OPERATOR_LABELS[op] ?? op}</option>}
                                    {operators.map(o => <option key={o} value={o}>{OPERATOR_LABELS[o] ?? o}</option>)}
                                </select>
                            )}
                            {op === 'range' ? (
                                <div className="flex-1 flex gap-1.5">
                                    <input
                                        type="number" className={V3_INPUT_CLS} value={bindingBound(value, 'min')}
                                        aria-label={`${def?.label ?? key} from`} placeholder="from"
                                        onChange={e => write({ ...current, [key]: toRangeBinding(e.target.value, bindingBound(value, 'max')) })} />
                                    <input
                                        type="number" className={V3_INPUT_CLS} value={bindingBound(value, 'max')}
                                        aria-label={`${def?.label ?? key} to`} placeholder="to"
                                        onChange={e => write({ ...current, [key]: toRangeBinding(bindingBound(value, 'min'), e.target.value) })} />
                                </div>
                            ) : op === 'in' ? (
                                // AB#2696 — a "one of" list needs its RAW text held while it is being
                                // typed. Deriving the input's value from the parsed binding meant every
                                // keystroke round-tripped through splitList, which drops empty segments —
                                // so the moment you typed the separating comma it was parsed away and
                                // React restored the input without it, and a second value could never be
                                // entered. The parsed values are still what gets stored.
                                <div className="flex-1">
                                    <input
                                        className={V3_INPUT_CLS}
                                        value={listText[key] ?? bindingComparand(value)}
                                        aria-label={`${def?.label ?? key} values`}
                                        placeholder="SW1, SW2"
                                        data-testid={`dimension-list-${i}`}
                                        onChange={e => {
                                            const raw = e.target.value
                                            setListText(t => ({ ...t, [key]: raw }))
                                            write({ ...current, [key]: toBinding('in', raw) })
                                        }}
                                        onBlur={() => setListText(t => {
                                            // Leaving the field re-syncs to the stored shape, so a stray
                                            // trailing comma tidies itself rather than lingering forever.
                                            const next = { ...t }
                                            delete next[key]
                                            return next
                                        })} />
                                </div>
                            ) : (
                                <div className="flex-1">
                                    <EditField
                                        value={op === 'eq' ? value : bindingComparand(value)} meta={valueMeta}
                                        fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById}
                                        fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId}
                                        onChange={v => write({ ...current, [key]: op === 'eq' ? v : toBinding(op, v === undefined || v === null ? '' : String(v)) })} />
                                </div>
                            )}
                            <button
                                type="button" className={ROW_BTN} aria-label={`Remove ${key}`}
                                data-testid={`dimension-remove-${i}`}
                                onClick={() => {
                                    const next = { ...current }
                                    delete next[key]
                                    write(next)
                                }}
                            >✕</button>
                        </div>
                    )
                })}
            </div>
            {entries.some(([, v]) => bindingOp(v) === 'in') && (
                <p className="text-xs text-neutral-400 dark:text-slate-500 mt-1">Separate the values of a &ldquo;one of&rdquo; list with commas.</p>
            )}
            <button
                type="button" className={`${ROW_BTN} mt-2`} data-testid="dimension-add"
                disabled={options.length > 0 && options.every(o => o.key in current)}
                onClick={() => {
                    const nextKey = options.find(o => !(o.key in current))?.key
                    if (!nextKey) { return }
                    write({ ...current, [nextKey]: '' })
                }}
            >+ Add condition</button>
            {entries.length === 0 && (
                <p className="text-xs text-neutral-400 dark:text-slate-500 mt-1 italic">
                    No conditions — this applies to everything in its layer.
                </p>
            )}
        </div>
    )
}

/**
 * AB#2541 — `value-rows`: an array of objects (quantity breaks, channel adjustments) authored as a
 * table of typed inputs instead of a JSON array. Columns are declared on the component.
 */
function ValueRowsSlot({ component, editable, draft, onChange, context }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const rows = asJsonArray(component.field ? record[component.field] : undefined)
        .filter(r => r && typeof r === 'object')
        .map(r => r as Record<string, unknown>)
    const cols = component.rowColumns ?? []
    const label = component.label ?? component.title ?? component.field

    const write = (next: Record<string, unknown>[]) => {
        onChange!(setBindingField(draft!, component.dataBinding, component.field!, toJsonField(next)))
    }

    // AB#2546 — DISCRIMINATED rows: each row's fields depend on its own `kind`. Channel adjustments are
    // four different shapes sharing one array (uplift, rounding, skuOverride, marginFloor), so a single
    // column set would show four mostly-empty columns per row with nothing to say which matter. These
    // render as stacked cards — a kind select, then only that kind's fields — rather than a table.
    //
    // A row whose kind has no schema keeps its stored values untouched and says so: an adjustment this
    // build does not understand must survive being opened and saved, not be silently emptied.
    if (component.rowKindField) {
        const kindField = component.rowKindField
        const schemas = component.rowKindSchemas ?? {}
        const kinds = Object.keys(schemas)
        return (
            <div>
                {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
                {rows.length === 0 && <div className="text-sm text-neutral-400 dark:text-slate-500 italic mb-1">None</div>}
                <div className="space-y-2">
                    {rows.map((row, i) => {
                        const rowKind = String(row[kindField] ?? '')
                        const schema = schemas[rowKind]
                        return (
                            <div key={i} data-testid={`kind-row-${i}`} className="rounded border border-neutral-200 dark:border-slate-700 p-2">
                                <div className="flex gap-1.5 items-center mb-1.5">
                                    {isEdit
                                        ? <select
                                            className={`${V3_INPUT_CLS} max-w-[50%]`} value={rowKind} aria-label="Kind"
                                            onChange={e => {
                                                // Changing kind drops the OLD kind's values (they mean nothing to the new
                                                // one) but keeps the kind itself. Deliberate and local to one row.
                                                write(rows.map((r, j) => j === i ? { [kindField]: e.target.value } : r))
                                            }}>
                                            {!kinds.includes(rowKind) && <option value={rowKind}>{rowKind || '—'}</option>}
                                            {kinds.map(k => <option key={k} value={k}>{schemas[k]?.label ?? k}</option>)}
                                        </select>
                                        : <span className="text-sm font-medium">{schema?.label ?? rowKind}</span>}
                                    {isEdit && (
                                        <button type="button" className={`${ROW_BTN} ml-auto`} aria-label={`Remove ${rowKind || 'adjustment'}`}
                                            data-testid={`kind-row-remove-${i}`}
                                            onClick={() => write(rows.filter((_, j) => j !== i))}>✕</button>
                                    )}
                                </div>
                                {schema
                                    ? <div className="grid grid-cols-2 gap-2">
                                        {schema.fields.map(f => (
                                            <div key={f.name}>
                                                <div className="text-xs text-neutral-500 dark:text-slate-400 mb-0.5">{f.label ?? f.name}</div>
                                                {isEdit
                                                    ? <EditField
                                                        value={row[f.name]}
                                                        meta={{ name: f.name, type: f.type ?? 'string', ...(f.options ? { enum: f.options } : {}) }}
                                                        onChange={v => write(rows.map((r, j) => j === i ? { ...r, [f.name]: v } : r))} />
                                                    : <span className="text-sm">{String(row[f.name] ?? '—')}</span>}
                                            </div>
                                        ))}
                                    </div>
                                    : <div className="text-xs text-neutral-400 dark:text-slate-500 italic" data-testid={`kind-row-unknown-${i}`}>
                                        This build does not know how to edit a &lsquo;{rowKind}&rsquo; adjustment. Its settings
                                        are kept as they are.
                                    </div>}
                            </div>
                        )
                    })}
                </div>
                {isEdit && kinds.length > 0 && (
                    <button type="button" className={`${ROW_BTN} mt-2`} data-testid="kind-row-add"
                        onClick={() => write([...rows, { [kindField]: kinds[0]! }])}
                    >+ {component.addRowLabel ?? 'Add'}</button>
                )}
            </div>
        )
    }

    return (
        <div>
            {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
            {rows.length === 0 && <div className="text-sm text-neutral-400 dark:text-slate-500 italic mb-1">None</div>}
            {rows.length > 0 && (
                <table className="marque-table w-full text-sm">
                    <thead>
                        <tr>
                            {cols.map(c => (
                                <th key={c.name} className="text-left text-xs font-medium text-neutral-500 dark:text-slate-400 pb-1">{c.label ?? c.name}</th>
                            ))}
                            {isEdit && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i} data-testid={`value-row-${i}`}>
                                {cols.map(c => (
                                    <td key={c.name} className="pr-1.5 pb-1">
                                        {isEdit
                                            ? <EditField
                                                value={row[c.name]}
                                                meta={{ name: c.name, type: c.type ?? 'string', ...(c.options ? { enum: c.options } : {}) }}
                                                onChange={v => write(rows.map((r, j) => j === i ? { ...r, [c.name]: v } : r))} />
                                            : <span>{String(row[c.name] ?? '')}</span>}
                                    </td>
                                ))}
                                {isEdit && (
                                    <td className="pb-1">
                                        <button
                                            type="button" className={ROW_BTN} aria-label={`Remove row ${i + 1}`}
                                            data-testid={`value-remove-${i}`}
                                            onClick={() => write(rows.filter((_, j) => j !== i))}
                                        >✕</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {isEdit && (
                <button
                    type="button" className={`${ROW_BTN} mt-1.5`} data-testid="value-row-add"
                    onClick={() => write([...rows, {}])}
                >+ {component.addRowLabel ?? 'Add row'}</button>
            )}
        </div>
    )
}

/**
 * AB#2546 — `kind-fields`: an object whose SHAPE depends on another field's value.
 *
 * A pricing layer's `settings` is the case this exists for: a matrix layer needs an ordered axis list,
 * a tax layer needs a strategy, most kinds need nothing at all. A flat form would show every control
 * for every kind — mostly blank, with no indication which ones matter — which is exactly why the
 * generic row editor was rejected for this field rather than pressed into service.
 *
 * `schemaKindField` names the sibling that discriminates, `kindSchemas` maps each value to the
 * fields that value takes. A kind with no entry renders a sentence saying so, which is the honest
 * answer for a base-price layer that genuinely has no settings.
 *
 * **Unrecognised keys are preserved.** Writes land on top of whatever was already stored, so a setting
 * this build does not know about — from an import, a newer version, or hand-authoring — survives being
 * opened and saved. Quietly destroying working configuration would be far worse than the text area
 * this replaces.
 */
function KindFieldsSlot({ component, editable, draft, onChange, context }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const current = asJsonObject(component.field ? record[component.field] : undefined)
    const label = component.label ?? component.title ?? component.field

    const kind = component.schemaKindField ? String(record[component.schemaKindField] ?? '') : ''
    const schema = component.kindSchemas?.[kind]
    const write = (next: Record<string, unknown>) => {
        onChange!(setBindingField(draft!, component.dataBinding, component.field!, toJsonField(next)))
    }

    if (!schema || schema.length === 0) {
        return (
            <div>
                {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
                <div className="text-sm text-neutral-400 dark:text-slate-500 italic" data-testid="kind-fields-none">
                    {kind ? `A ${kind} layer takes no extra settings.` : 'Choose a kind to see its settings.'}
                </div>
            </div>
        )
    }

    return (
        <div>
            {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
            <div className="grid grid-cols-2 gap-2">
                {schema.map(f => (
                    <div key={f.name} data-testid={`kind-field-${f.name}`}>
                        <div className="text-xs text-neutral-500 dark:text-slate-400 mb-0.5">{f.label ?? f.name}</div>
                        {isEdit
                            ? (f.type === 'code-list'
                                ? <InlineCodeList
                                    codes={asJsonArray(current[f.name]).filter(c => typeof c === 'string').map(c => c as string)}
                                    onChange={v => write({ ...current, [f.name]: v })} />
                                : <EditField
                                    value={current[f.name]}
                                    meta={{ name: f.name, type: f.type ?? 'string', ...(f.options ? { enum: f.options } : {}) }}
                                    onChange={v => write({ ...current, [f.name]: v })} />)
                            : <span className="text-sm">{String(current[f.name] ?? '—')}</span>}
                    </div>
                ))}
            </div>
        </div>
    )
}

/** AB#2546 — the ORDERED chip list used for an axis list inside a kind schema. Order is shown because
 *  a matrix's first axis is its rows and the second its columns — an unordered list would be wrong. */
function InlineCodeList({ codes, onChange }: { codes: string[]; onChange: (next: string[]) => void }) {
    const [entry, setEntry] = useState('')
    const add = () => {
        const v = entry.trim()
        if (v === '' || codes.includes(v)) { setEntry(''); return }
        onChange([...codes, v]); setEntry('')
    }
    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-1">
                {codes.map((c, i) => (
                    <span key={c} data-testid={`axis-chip-${i}`} className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-neutral-100 dark:bg-slate-700">
                        <span className="text-neutral-400 font-mono">{i + 1}</span>{c}
                        <button type="button" aria-label={`Remove ${c}`} className="text-neutral-400 hover:text-red-500"
                            onClick={() => onChange(codes.filter(x => x !== c))}>✕</button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1.5">
                <input className={V3_INPUT_CLS} value={entry} aria-label="Add axis" data-testid="axis-entry"
                    onChange={e => setEntry(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} />
                <button type="button" className={ROW_BTN} data-testid="axis-add" onClick={add}>Add</button>
            </div>
        </div>
    )
}

/**
 * AB#2541 — `code-list`: an array of plain codes (channel groups, adjustment-set target values)
 * authored as chips instead of a JSON array of strings.
 */
function CodeListSlot({ component, editable, draft, onChange, context, fetchEntityOptions, scopeCompanyId }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const codes = asJsonArray(component.field ? record[component.field] : undefined)
        .filter(c => typeof c === 'string' && c !== '')
        .map(c => c as string)
    const label = component.label ?? component.title ?? component.field
    const [entry, setEntry] = useState('')

    // AB#2690 — the optional closed vocabulary. Reuses resolveCellLookup so `sourceBy` (AB#2645)
    // behaves EXACTLY as it does on a field or a grid cell: the discriminator is resolved against
    // this record, so an adjustment set targeting channels offers channel codes and one targeting
    // something else offers that instead. Declaring the vocabulary is what turns a silent typo into
    // an impossible input — ChannelPass.Run returns with no warning when nothing matches, so an
    // adjustment set that reaches nobody is indistinguishable from one that simply is not applying.
    const { meta: vocab } = resolveCellLookup(
        component.codeLookup ? { lookupField: component.codeLookup } as ViewFieldMeta : undefined,
        record)
    const vocabLookup = vocab?.lookupField
    const canPick = !!vocabLookup && !!fetchEntityOptions
    const vocabSource = vocabLookup?.sourceEntity
    const vocabValueField = vocabLookup?.valueField
    const vocabDisplayField = vocabLookup?.displayField
    const vocabRollup = vocabLookup?.companyFilter?.rollup
    // Serialised for dependency identity — the declaration is a fresh object every render.
    const vocabFiltersKey = vocabLookup?.optionFilters ? JSON.stringify(vocabLookup.optionFilters) : undefined
    const loadCodes = useCallback((searchTerm: string) =>
        fetchEntityOptions!(vocabSource!, vocabDisplayField, vocabValueField,
            undefined, scopeCompanyId, searchTerm,
            vocabRollup !== undefined ? { rollup: vocabRollup } : undefined,
            vocabFiltersKey !== undefined
                ? JSON.parse(vocabFiltersKey) as Record<string, string | number | boolean>
                : undefined),
    [fetchEntityOptions, vocabSource, vocabDisplayField, vocabValueField, scopeCompanyId, vocabRollup, vocabFiltersKey])

    const write = (next: string[]) => {
        onChange!(setBindingField(draft!, component.dataBinding, component.field!, toJsonField(next)))
    }
    const add = () => {
        const v = entry.trim()
        if (v === '' || codes.includes(v)) { setEntry(''); return }
        write([...codes, v])
        setEntry('')
    }

    return (
        <div>
            {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1">{label}</div>}
            <div className="flex flex-wrap gap-1 mb-1.5">
                {codes.length === 0 && <span className="text-sm text-neutral-400 dark:text-slate-500 italic">None</span>}
                {codes.map((c, i) => (
                    <span key={c} data-testid={`code-chip-${i}`} className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-neutral-100 dark:bg-slate-700 text-neutral-700 dark:text-slate-200">
                        {c}
                        {isEdit && (
                            <button
                                type="button" aria-label={`Remove ${c}`} data-testid={`code-remove-${i}`}
                                className="text-neutral-400 hover:text-red-500"
                                onClick={() => write(codes.filter(x => x !== c))}
                            >✕</button>
                        )}
                    </span>
                ))}
            </div>
            {isEdit && (canPick ? (
                // A declared vocabulary makes this a PICKER, not a typeable box: that is the whole
                // point of the ticket. Omit codeLookup for an open vocabulary and the free-text
                // editor below is unchanged.
                <div data-testid="code-picker">
                    <AsyncLookupCombobox
                        value=""
                        onChange={v => { const c = v.trim(); if (c !== '' && !codes.includes(c)) { write([...codes, c]) } }}
                        loadOptions={loadCodes}
                        placeholder={`Add ${label ?? 'code'}`} />
                </div>
            ) : (
                <div className="flex gap-1.5">
                    <input
                        className={V3_INPUT_CLS} value={entry} data-testid="code-entry"
                        aria-label={`Add ${label ?? 'code'}`}
                        onChange={e => setEntry(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} />
                    <button type="button" className={ROW_BTN} data-testid="code-add" onClick={add}>Add</button>
                </div>
            ))}
        </div>
    )
}

// AB#1400 — standalone input controls. Render an editable control bound to component.field in edit
// mode; show the formatted value in readonly mode. The component type drives the control kind.
function InputSlot({ component, view, context, entityFields, permissions, editable, draft, onChange, fetchEntityOptions, fetchEntityById, fetchEntityRecord, fetchLookupOptions, fetchRevealField, scopeCompanyId, editFieldComponent, fieldHistoryComponent, fmt }: SlotProps) {
    const EF = editFieldComponent ?? EditField
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const value = component.field ? record[component.field] : undefined
    let externalHref: string | undefined
    if (component.linkValue && typeof value === 'string') {
        try {
            const url = new URL(value)
            if (url.protocol === 'https:' && !url.username && !url.password) externalHref = url.href
        } catch { /* Invalid or masked values remain plain text. */ }
    }
    const label = component.label ?? component.title ?? baseMeta?.label ?? component.field
    // AB#2518 — copyFrom needs the whole field list (to find what seeds off this field), a record of
    // what WE seeded (to tell a stale previous seed from a user's edit), and a live view of the draft.
    // The draft ref is the fix for a real bug: onChange takes a value, not an updater, so a callback
    // that closed over the pre-lookup draft would silently discard anything typed while it was in flight.
    const fieldMeta = metaFor(component.dataBinding, view, entityFields)
    const seededRef = useRef<Record<string, unknown>>({})
    const draftRef = useRef(draft)
    draftRef.current = draft

    const typeByControl: Record<string, string | undefined> = {
        'number-input': 'number', 'date-picker': 'date', 'checkbox': 'boolean', 'toggle': 'boolean',
        'select': 'enum', 'multi-select': 'enum', 'radio-group': 'enum', 'text-area': 'text', 'text-input': 'text',
    }
    // AB#1878 — a tags field stays 'tags' (a chip editor) even when authored as a multi-select component,
    // rather than being collapsed to 'enum' by the control map.
    // AB#1900 — pass the REAL field meta through (required / readOnly / type / lookupField / compute /
    // label) instead of synthesizing a partial one, so a direct field renders exactly like one in a
    // field-section. The field's true type drives formatting + step + select + lookup; the component
    // type only chooses the control when the field has no known type. tags stays tags (chip editor).
    const synthType = baseMeta?.type === 'tags' ? 'tags' : (baseMeta?.type ?? typeByControl[component.type])
    // AB#2959 — lock an immutable-after-create field once the bound record exists.
    const synthMeta: ViewFieldMeta = lockImmutableAfterCreate({
        ...baseMeta,
        name: component.field ?? baseMeta?.name ?? '',
        ...(synthType ? { type: synthType } : {}),
        // an author-supplied options list overrides the field's enum.
        ...(component.options ? { enum: component.options } : {}),
        // AB#2429 — an authored radio-group renders actual radios; it used to collapse to the enum
        // select through the control map above, making the component indistinguishable from 'select'.
        ...(component.type === 'radio-group' ? { radio: true } : {}),
    }, record)

    // AB#1901 — a field renders BARE (no implicit card frame); the grid gap provides spacing and an
    // explicit container is the only thing that draws a frame.
    return (
        <div data-guidance-field={isEdit ? guidanceField(view, component.dataBinding, component.field) : undefined} data-guidance-company={scopeCompanyId} data-guidance-new={!record.id}>
            {label && (
                <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <span>
                        {label}{isEdit && baseMeta?.required && <span className="text-red-500 ml-0.5">*</span>}
                    </span>
                    {/* AB#2382 — component.helpText overrides the entity's authored hint, mirroring
                        the `label` precedence one line above. */}
                    <FieldHint text={component.helpText ?? baseMeta?.helpText} label={label} />
                    {/* AB#2767 — the field's own change history, offered only when the entity declares
                        fieldHistory on this field AND the host injects the affordance (the designer
                        injects nothing). Resolution of entity + record mirrors the AB#2447 reveal. */}
                    {(() => {
                        if (!baseMeta?.fieldHistory || !fieldHistoryComponent || !component.field) { return null }
                        const historyRoot = (component.dataBinding ?? '').replace(/^\$/, '').split('.')[0]
                        const historyEntityType = view.context?.find(c => c.name === historyRoot)?.entityType
                        const historyRecordId = typeof record['id'] === 'string' ? record['id'] : undefined
                        if (!historyEntityType || !historyRecordId) { return null }
                        const History = fieldHistoryComponent
                        return <History entityType={historyEntityType} recordId={historyRecordId}
                            field={component.field} label={label} />
                    })()}
                </div>
            )}
            {(!isEdit || synthMeta.readOnly) && isSentinel(value) && component.field ? <MaskedFieldValue
                key={JSON.stringify([component.dataBinding, record, component.field, permissions, scopeCompanyId])}
                value={value} field={component.field} meta={baseMeta} record={record}
                binding={component.dataBinding} view={view} fetchRevealField={fetchRevealField} fmt={fmt} /> : isEdit && component.field
                ? <EF value={value} meta={synthMeta} multiline={component.type === 'text-area'}
                    quantityUnit={baseMeta?.quantityUnitField ? record[baseMeta.quantityUnitField] : undefined}
                    quantityUnitReference={quantityReference(baseMeta, fieldMeta, record)}
                    fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId}
                    {...(synthMeta.metaOptions ? { metaOptionsEntity: resolveMetaOptionsEntity(synthMeta, record) } : {})}
                    onChange={v => {
                        const next = setBindingField(draft!, component.dataBinding, component.field!, v)
                        draftRef.current = next
                        onChange!(next)
                        // AB#2518 — seed anything declaring copyFrom against this field. The user's own
                        // change lands immediately; the copied value follows when the lookup returns.
                        void applyCopyFrom(
                            asRecord(resolveBinding(component.dataBinding, next)), component.field!, v,
                            fieldMeta, fetchEntityRecord, seededRef.current, scopeCompanyId,
                            () => draftRef.current ? asRecord(resolveBinding(component.dataBinding, draftRef.current)) : undefined,
                        ).then(patch => {
                            if (Object.keys(patch).length === 0) { return }
                            // Re-derive from the LIVE draft, not the pre-lookup snapshot: the user may
                            // have changed another field while the fetch was in flight.
                            let seeded = draftRef.current ?? next
                            for (const [f, val] of Object.entries(patch)) {
                                seeded = setBindingField(seeded, component.dataBinding, f, val)
                            }
                            draftRef.current = seeded
                            onChange!(seeded)
                        })
                    }} />
                // AB#1921 — read mode prefers the server-resolved FK label (record.__lookups[field]) over the
                // raw value (a GUID), mirroring the ListView cell. Non-FK fields have no lookup entry and fall
                // through to formatValue. Edit mode (the FK select) is unchanged.
                // AB#1925 — formatValue branches on the custom type (month-number → month name).
                // AB#2266 — a lookupValue field (the generator emits these as 'select' components) resolves
                // its stored code to the lookup label client-side; code shown until options load / on failure.
                : externalHref ? <a href={externalHref} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline break-all">{String(value)}</a>
                : <div className={`text-sm text-neutral-900 dark:text-slate-100${component.type === 'text-area' ? ' whitespace-pre-wrap break-words' : ''}`}>{baseMeta?.type === 'lookupValue' && baseMeta.lookupType
                    ? <LookupValueText value={value} lookupType={baseMeta.lookupType} fetchLookupOptions={fetchLookupOptions} scopeCompanyId={scopeCompanyId} />
                    : lookupLabel(record, component.field) ?? enumOptionLabel(synthMeta.enum, value) ?? formatValue(value, synthMeta.type, synthMeta.customType, fmt, true)}</div>}
        </div>
    )
}

// AB#1403 — communications-panel: re-homes the AB#1395 documents surface into the view model. Lists
// the documents linked to the bound record (entity type from its context entry, id from the record).
function CommunicationsPanelSlot({ component }: SlotProps) {
    // AB#1622 — the documents/communications surface fetches tenant data, which the shared package
    // must not couple to. In the shared renderer it renders a labelled placeholder; the tenant's own
    // copy keeps the live DocumentsPanel until the dedup follow-up injects it as a slot.
    return card(<div className="text-xs text-neutral-400 dark:text-slate-500 italic">{component.title ?? 'Documents'} (preview)</div>)
}

// AB#1883 — slider: a range input over a numeric field with min/max/step from the component. Pure (no
// tenant coupling), so it renders in both the designer preview and the tenant runtime.
function SliderSlot({ component, view, context, entityFields, editable, draft, onChange }: SlotProps) {
    const isEdit = !!editable && !!draft && !!onChange
    const record = asRecord(resolveBinding(component.dataBinding, isEdit ? draft! : context))
    const baseMeta = component.field ? metaFor(component.dataBinding, view, entityFields).find(m => m.name === component.field) : undefined
    const raw = component.field ? record[component.field] : undefined
    const value = typeof raw === 'number' ? raw : (Number(raw) || 0)
    const label = component.label ?? component.title ?? baseMeta?.label ?? component.field
    const min = component.min ?? 0
    const max = component.max ?? 100
    const step = component.step ?? 1
    return card(
        <>
            {label && <div className="text-xs font-medium text-neutral-500 dark:text-slate-400 mb-0.5">{label} <span className="text-neutral-400 dark:text-slate-500">· {value}</span></div>}
            <input
                type="range" min={min} max={max} step={step} value={value}
                disabled={!isEdit || !component.field}
                onChange={e => { if (isEdit && component.field) { onChange!(setBindingField(draft!, component.dataBinding, component.field, Number(e.target.value))) } }}
                className="w-full"
            />
        </>,
    )
}

function PlaceholderSlot({ component }: SlotProps) {
    // AB#1884 — map-pin renders a map-styled placeholder in the designer (the live Google map is injected
    // only in the tenant runtime); the caption shows the bound lat/lng fields so the author can verify it.
    if (component.type === 'map-pin') {
        const coords = [component.latField, component.lngField].filter(Boolean).join(' · ')
        return (
            <div
                className="rounded-lg border border-dashed border-neutral-300 dark:border-slate-600 p-6 flex flex-col items-center justify-center gap-1 min-h-[120px] text-neutral-400"
                style={{ backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 11px,rgba(0,0,0,0.04) 12px),repeating-linear-gradient(90deg,transparent,transparent 11px,rgba(0,0,0,0.04) 12px)' }}
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
                </svg>
                <span className="text-xs italic">Map{coords ? ` · ${coords}` : ''}</span>
            </div>
        )
    }
    // AB#1889 — chart renders a bar-styled placeholder in the designer (the live recharts render is
    // injected only in the tenant runtime); the caption shows the chart type + bound series.
    if (component.type === 'chart') {
        const src = component.dataBinding ?? ''
        // AB#3286 — a scatter previews sample points with the authored quadrant lines as inline SVG
        // (no recharts in the designer chunk, AB#2051). Threshold lines only draw when authored, so
        // the preview reflects the quadrant layout the author configured.
        const scatterSvg = (
            <svg width="48" height="32" viewBox="0 0 48 32" aria-hidden="true" opacity="0.6">
                {component.quadrants?.x != null && <line x1="24" y1="0" x2="24" y2="32" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />}
                {component.quadrants?.y != null && <line x1="0" y1="16" x2="48" y2="16" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />}
                <g fill="currentColor">
                    <circle cx="8" cy="24" r="2.5" /><circle cx="15" cy="10" r="2.5" /><circle cx="20" cy="20" r="2.5" />
                    <circle cx="31" cy="7" r="2.5" /><circle cx="36" cy="14" r="2.5" /><circle cx="42" cy="26" r="2.5" />
                </g>
            </svg>
        )
        return (
            <div className="rounded-lg border border-dashed border-neutral-300 dark:border-slate-600 p-4 flex flex-col items-center justify-center gap-2 min-h-[120px] text-neutral-400">
                {component.chartType === 'scatter' ? scatterSvg : (
                    <svg width="48" height="32" viewBox="0 0 48 32" fill="currentColor" aria-hidden="true" opacity="0.6">
                        <rect x="2" y="16" width="8" height="16" rx="1" /><rect x="14" y="8" width="8" height="24" rx="1" /><rect x="26" y="20" width="8" height="12" rx="1" /><rect x="38" y="4" width="8" height="28" rx="1" />
                    </svg>
                )}
                <span className="text-xs italic">{component.chartType ?? 'bar'} chart{src ? ` · ${src}` : ''}{Array.isArray(component.series) && component.series.length ? ` · ${component.series.map(s => `${s?.label ?? 'Series'} (${s?.chartType ?? component.chartType ?? 'bar'})`).join(', ')}` : ''}{component.referenceLine ? ` · reference ${component.referenceLine.value}${component.referenceLine.label ? ` ${component.referenceLine.label}` : ''}` : ''}</span>
            </div>
        )
    }
    // AB#1883 — button renders as a styled, inert button in the designer (the tenant runs the process).
    if (component.type === 'button') {
        return (
            <button type="button" disabled
                className="marque-button rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white opacity-70 cursor-default">
                {component.label ?? component.title ?? 'Action'}
            </button>
        )
    }
    // AB#1883 — barcode/qr render a barcode-styled placeholder in the designer (the live bwip-js render is
    // injected only in the tenant runtime).
    if (component.type === 'barcode' || component.type === 'qr-barcode') {
        const isQr = component.type === 'qr-barcode' || component.barcodeFormat === 'qr'
        return (
            <div className="rounded border border-dashed border-neutral-300 dark:border-slate-600 p-3 flex flex-col items-center justify-center gap-1 min-h-[72px] text-neutral-500">
                <div
                    className="h-8 w-28"
                    style={isQr
                        ? { backgroundImage: 'repeating-conic-gradient(rgba(0,0,0,0.45) 0% 25%, transparent 0% 50%)', backgroundSize: '8px 8px' }
                        : { backgroundImage: 'repeating-linear-gradient(90deg, currentColor 0 2px, transparent 2px 5px)' }}
                    aria-hidden="true"
                />
                <span className="text-[10px] italic text-neutral-400 dark:text-slate-500">{isQr ? 'QR' : (component.barcodeFormat ?? 'barcode')}</span>
            </div>
        )
    }
    // AB#1647 — a custom component shows its componentName so the designer preview labels it (v1 has no
    // live render of first-party components in the designer; the tenant runtime renders them for real).
    const label = component.type === 'custom'
        ? (component.componentName ?? 'custom component')
        : (component.title ?? component.type)
    return (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg border border-dashed border-neutral-300 dark:border-slate-600 p-4 flex items-center justify-center">
            <span className="text-xs text-neutral-400 dark:text-slate-500 italic">{label}</span>
        </div>
    )
}

// ── Dispatcher ───────────────────────────────────────────────────────────────

// AB#1863 — containers (pure-visual frames) scope nothing, and collection-bound tables bind their own
// relationship/collection, so neither defaults to the primary entity.
// AB#2169 — the pricing components bind their own collections (itemsBinding / traceBinding /
// cellsBinding, falling back to an explicit dataBinding), so none of them defaults to the primary entity.
const PRIMARY_DEFAULT_EXCLUDE = new Set<string>(['container', 'section-header', 'tab-container', 'line-items-table', 'related-entity-list', 'stack-canvas', 'calc-trace', 'matrix-grid', 'managed-cache'])

// AB#1863 — the view's primary entity context name (e.g. "order"), used to default an unbound leaf so
// authors set just `field`. Undefined for dashboards / when no singular primary context exists.
function primaryBinding(view: ViewDefinition): string | undefined {
    return view.context?.find(c => c.entityType === view.entityType && !c.collection)?.name
}

function RenderComponent(props: SlotProps) {
    // AB#1863 — implicit primary-context binding: a leaf with no explicit/inherited dataBinding resolves
    // against the view's primary entity (so `field` alone reads $primary.field). Containers and collection
    // tables keep their own (absent) binding.
    const { component, view } = props
    const primary = component.dataBinding || PRIMARY_DEFAULT_EXCLUDE.has(component.type)
        ? undefined
        : primaryBinding(view)
    const p: SlotProps = primary ? { ...props, component: { ...component, dataBinding: primary } } : props
    switch (p.component.type) {
        case 'container': return <ContainerSlot {...p} />
        case 'section-header': return <SectionHeaderSlot {...p} />
        case 'field-section': return <FieldSectionSlot {...p} />
        case 'summary-panel': return <SummaryPanelSlot {...p} />
        case 'line-items-table': return <LineItemsSlot {...p} />
        case 'related-entity-list': {
            // AB#2316 — the tenant injects a live, fetching related-list with modal add/edit; the designer
            // and preview omit it and fall back to the read-only, context-bound LineItemsSlot (unchanged).
            const Related = p.relatedListComponent
            // AB#2568 — the live draft rides along so the slot's prefill can watch its driver fields.
            return Related ? <Related component={p.component} view={p.view} context={p.context} draft={p.draft} /> : <LineItemsSlot {...p} />
        }
        case 'object-header': return <ObjectHeaderSlot {...p} />
        case 'status-badge': return <StatusBadgeSlot {...p} />
        case 'tab-container': return <TabContainerSlot {...p} />
        case 'comparison-list': {
            const config = p.component.comparisonList
            if (!config || !p.component.dataBinding?.trim()) return <p role="alert">Comparison list configuration or record binding is missing.</p>
            if (config.selectedIdField && p.component.dataBinding.replace(/^\$/, '').includes('.')) {
                return <p role="alert">Comparison selection requires a root record binding.</p>
            }
            return <ComparisonList definition={config} record={asRecord(resolveBinding(p.component.dataBinding, p.draft ?? p.context))}
                onSelect={p.editable && p.draft && p.onChange && config.selectedIdField
                    ? id => p.onChange!(setBindingField(p.draft!, p.component.dataBinding, config.selectedIdField!, id)) : undefined} />
        }
        case 'message-list': {
            const config = p.component.messageList
            if (!config || !p.component.dataBinding?.trim()) return <p role="alert">Message list configuration or record binding is missing.</p>
            if (config.selectedIdField && p.component.dataBinding.replace(/^\$/, '').includes('.')) {
                return <p role="alert">Message selection requires a root record binding.</p>
            }
            return <MessageList definition={config} record={asRecord(resolveBinding(p.component.dataBinding, p.draft ?? p.context))}
                onSelect={p.editable && p.draft && p.onChange && config.selectedIdField
                    ? id => p.onChange!(setBindingField(p.draft!, p.component.dataBinding, config.selectedIdField!, id)) : undefined} />
        }
        case 'planning-timeline': {
            const config = p.component.planningTimeline
            if (!config) return <p role="alert">Planning timeline configuration is missing.</p>
            if (!p.component.dataBinding || p.component.dataBinding.replace(/^\$/, '').includes('.')) {
                return <p role="alert">Planning timelines require a root record binding.</p>
            }
            const editing = p.editable && p.draft && p.onChange
            const source = editing ? p.draft! : p.context
            return <PlanningTimeline definition={config} label={p.component.title} record={asRecord(resolveBinding(p.component.dataBinding, source))}
                onSelect={editing && config.selectedIdField ? id => p.onChange!(setBindingField(p.draft!, p.component.dataBinding, config.selectedIdField!, id)) : undefined}
                onChange={editing ? rows => p.onChange!(setBindingField(p.draft!, p.component.dataBinding, config.rowsField, rows)) : undefined} />
        }
        case 'timeline': {
            // AB#3285 — sources[] switches the component into multi-event mode: the tenant injects the
            // live feed (POST /api/v3/timeline); a host without the seam (the designer) gets the static
            // sources preview inside TimelineSlot. No sources = the legacy single-dateField rendering.
            const Timeline = p.timelineComponent
            if ((p.component.sources?.length ?? 0) > 0 && Timeline) {
                return <Timeline component={p.component} view={p.view} context={p.context} />
            }
            return <TimelineSlot {...p} />
        }
        case 'user-calendar': {
            // AB#3238 — the tenant injects the live agenda; every other host (the designer canvas,
            // previews) gets the static shape, which never fetches.
            const Agenda = p.userCalendarComponent
            return Agenda
                ? <Agenda component={p.component} view={p.view} context={p.context} />
                : <UserCalendarSlot {...p} />
        }
        case 'address-block': return <AddressBlockSlot {...p} />
        case 'bank-account-block': return <BankAccountBlockSlot {...p} />
        case 'connection-picker': return <ConnectionPickerSlot {...p} />  // AB#2981
        case 'user-picker': return <UserPickerSlot {...p} />  // AB#3291
        case 'quantity-input': return <QuantityInputSlot {...p} />
        case 'rich-text': return <RichTextSlot {...p} />
        case 'static-text': return <StaticTextSlot {...p} />
        case 'metric': return <MetricSlot {...p} />
        case 'alert-banner': return <AlertBannerSlot {...p} />
        case 'json-table': return <JsonTableSlot {...p} />
        case 'separator': return <SeparatorSlot {...p} />
        case 'image-viewer': return <ImageViewerSlot {...p} />
        case 'progress-bar': return <ProgressBarSlot {...p} />
        case 'segmented-bar': return <SegmentedBarSlot {...p} />
        case 'bar-list': return <BarListSlot {...p} />
        case 'data-table': return <DataTableSlot {...p} />
        case 'communications-panel': {
            // AB#1866 — tenant injects the live DocumentsPanel; designer falls back to the placeholder.
            const Docs = p.documentsComponent
            return Docs ? <Docs component={p.component} view={p.view} context={p.context} /> : <CommunicationsPanelSlot {...p} />
        }
        case 'map-pin': {
            // AB#1884 — tenant injects the live Google map (when a key is configured); designer + keyless
            // tenant fall back to the map-styled placeholder.
            const Map = p.mapComponent
            return Map ? <Map component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'button': {
            // AB#1883 — tenant injects the live ButtonSlot (runs component.process via raiseComponentEvent);
            // designer falls back to a styled, inert button placeholder.
            const Btn = p.buttonComponent
            return Btn ? <Btn component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'barcode':
        case 'qr-barcode': {
            // AB#1883 — tenant injects the live BarcodeSlot (bwip-js renders the bound value); the designer
            // falls back to a barcode-styled placeholder (the lib stays out of the designer bundle).
            const Barcode = p.barcodeComponent
            return Barcode ? <Barcode component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'chart': {
            // AB#1889 — tenant injects the live ChartSlot (recharts, bound to an aggregate-stage context
            // collection); the designer + keyless preview fall back to a chart-styled placeholder.
            const Chart = p.chartComponent
            return Chart ? <Chart component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'custom': {
            // AB#1866 — tenant injects the live custom-component slot; designer falls back to the placeholder.
            const Custom = p.customComponent
            return Custom ? <Custom component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'audit-trail': {
            // AB#2046 — tenant injects the live change-history panel (AuditLogPanel, backed by the AB#2018
            // per-record audit endpoint); the designer falls back to the labelled placeholder. Previously this
            // authorable type fell through to the default placeholder even in the tenant runtime (dead grey box).
            const Audit = p.auditTrailComponent
            return Audit ? <Audit component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'document-flow': {
            // AB#2046 — tenant injects the live document flow (V3DocumentFlow → the quote→order→delivery→invoice
            // graph); the designer falls back to the placeholder. Same dead-placeholder fix as audit-trail.
            const Flow = p.documentFlowComponent
            return Flow ? <Flow component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'search-panel': {
            // AB#2046/2121 — tenant injects the live search surface (SearchPanel → the V3 entities query with a
            // `search` term, drilling into a result's detail view); the designer falls back to the placeholder.
            // Same dead-placeholder fix as audit-trail / document-flow (formerly this only ever showed the placeholder).
            const Search = p.searchPanelComponent
            return Search ? <Search component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'stack-canvas': {
            // AB#2169 — tenant injects the live drag-reorderable pricing layer stack; designer keeps the placeholder.
            const Stack = p.stackCanvasComponent
            return Stack ? <Stack component={p.component} view={p.view} context={p.context} editable={p.editable} /> : <PlaceholderSlot {...p} />
        }
        case 'calc-trace': {
            // AB#2169 — tenant injects the live pricing waterfall trace (PricedLine layers); designer keeps the placeholder.
            const Trace = p.calcTraceComponent
            return Trace ? <Trace component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'managed-cache': {
            // AB#3063 — tenant injects the live cache stats/Clear card; designer keeps the placeholder.
            const Cache = p.managedCacheComponent
            return Cache ? <Cache component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'determination-trace': {
            // AB#2679 — tenant injects the live decision trace (why THIS site / account / printer);
            // designer keeps the placeholder.
            const Decision = p.determinationTraceComponent
            return Decision ? <Decision component={p.component} view={p.view} context={p.context} /> : <PlaceholderSlot {...p} />
        }
        case 'matrix-grid': {
            // AB#2169 — tenant injects the live N-dimension rate grid editor; designer keeps the placeholder.
            const Matrix = p.matrixGridComponent
            return Matrix ? <Matrix component={p.component} view={p.view} context={p.context} editable={p.editable} /> : <PlaceholderSlot {...p} />
        }
        // AB#1873 — lookup-field is an FK picker: route it to InputSlot, which renders the searchable
        // lookup from the field's lookupField meta (AB#1786). Previously fell to PlaceholderSlot even
        // though the generator emits it for entity-ref / calendar-ref fields.
        // AB#2541 — structured-field editors: the same field, the same stored JSON, authored through
        // typed rows instead of a text area full of braces.
        case 'dimension-rows': return <DimensionRowsSlot {...p} />
        case 'value-rows': return <ValueRowsSlot {...p} />
        case 'code-list': return <CodeListSlot {...p} />
        case 'kind-fields': return <KindFieldsSlot {...p} />
        case 'lookup-field':
        case 'text-input':
        case 'text-area':
        case 'number-input':
        case 'date-picker':
        case 'select':
        case 'multi-select':
        case 'radio-group':
        case 'checkbox':
        case 'toggle': return <InputSlot {...p} />
        case 'slider': return <SliderSlot {...p} />
        // AB#1873/2386 — file-attachments has its own seam. It previously routed to documentsComponent,
        // which the tenant wires to the AB#1395 generated-document tracker: read-only, with no way to attach
        // anything. It now routes to attachmentsComponent (the live AB#2021 attachment panel); the designer,
        // which injects neither, keeps the placeholder.
        case 'file-attachments': {
            const Attachments = p.attachmentsComponent
            return Attachments ? <Attachments component={p.component} view={p.view} context={p.context} draft={p.draft} editable={p.editable} onChange={p.onChange} /> : <PlaceholderSlot {...p} />
        }
        default: return <PlaceholderSlot {...p} />
    }
}

// ── ListView (AB#1401) ───────────────────────────────────────────────────────

// AB#2045 — controlled column-sort state: the HOST owns it (and runs the actual sort — the tenant's
// paged list re-queries server-side with sort/dir); ListView only renders the affordance and reports
// header clicks through onSortChange.
export interface ListSortState {
    field: string
    dir: 'asc' | 'desc'
}

// AB#2045 — the header-click cycle contract: unsorted (or another) column → asc, asc → desc,
// desc → none (null). Pure + exported so the cycle is unit-testable without a DOM.
export function cycleListSort(current: ListSortState | null | undefined, field: string): ListSortState | null {
    if (!current || current.field !== field) { return { field, dir: 'asc' } }
    if (current.dir === 'asc') { return { field, dir: 'desc' } }
    return null
}

export interface ListViewProps {
    view: ViewDefinition
    rows: Record<string, unknown>[]
    entityFields: EntityFieldMeta
    onRowClick?: ((row: Record<string, unknown>) => void) | undefined
    // AB#1588 — shell-first rendering: the shell paints before the data arrives; skeleton rows show.
    loading?: boolean | undefined
    // AB#2045 — controlled sorting: when the host wires onSortChange the column headers become clickable
    // buttons (cycling asc → desc → none via cycleListSort) with a ▲/▼/⇅ indicator + aria-sort (the
    // ⇅-when-inactive vocabulary matches the CrossModuleRefsPage sortable header). Hosts that omit it
    // (the designer preview) keep the previous inert headers — no behaviour change.
    sort?: ListSortState | null | undefined
    onSortChange?: ((sort: ListSortState | null) => void) | undefined
    // AB#2049 — tenant locale + base currency for cell formatting (dates / numbers / money). Undefined
    // for the designer preview → the previous browser-locale, bare-number output (byte-identical).
    fmt?: FormatContext | undefined
    // AB#2271 — an optional trailing action cell per row (e.g. Unarchive on archived rows). When omitted
    // (every existing host, incl. the designer preview) NO extra column is rendered — fully unchanged.
    // The render returns null for a row that should show no action (an empty cell). The consumer's control
    // must stopPropagation so activating it never also fires the row's onRowClick drill-down.
    rowAction?: ((row: Record<string, unknown>) => ReactNode) | undefined
    // AB#2603 — an optional LEADING cell per row, mirroring rowAction at the other end of the table.
    // Its one caller is the company column on an unfiltered company-scoped list: AB#2414 drops companyId
    // from generated list columns because a list is normally already narrowed by the caller's company
    // filter — true when a company is chosen, false for "All companies", where two rows differing only
    // by company become indistinguishable (Ben, 2026-08-06: two "Quote" rows on Document Defaults).
    // A prop rather than a generated column so it appears exactly when the filter is off, costs no
    // regeneration of any view, and stays absent in the designer preview, which has no company filter.
    leadingColumn?: { label: string; render: (row: Record<string, unknown>) => ReactNode } | undefined
    // AB#3280 — forces the layout tier (tests, the designer phone preview). Absent = the ambient
    // ViewportProvider tier, which without a provider is 'wide' — the pre-3280 table, byte-for-byte.
    viewport?: ViewportTier | undefined
    // AB#3954 — the per-user column layout store. Explicit prop wins; else the ambient
    // TableLayoutStoreProvider (the tenant mounts one); else in-memory (the designer preview).
    tableLayoutStore?: TableLayoutStore | undefined
    // AB#3953 — the table identity to personalise under. Defaults to tableIdentity(view)
    // (`<moduleId>/<viewKey>`); a host rendering a list it BUILT (RelatedEntityListSlot) passes the
    // parent view + component identity, since its synthetic view has neither module nor key.
    identity?: string | undefined
    // AB#3954 — reports the EFFECTIVE visible columns (after the user's layout) whenever they change,
    // so a host deriving search fields / export columns from `list.columns` can follow them.
    onColumnsChange?: ((columns: TableColumn[]) => void) | undefined
}

// Renders a list view's `list.columns` over the collection bound into its context (provided by the
// nav process / open-view). Field metadata for formatting comes from the view's primary entity.
export function ListView({ view, rows, entityFields, onRowClick, loading, sort, onSortChange, fmt, rowAction, leadingColumn, viewport, tableLayoutStore, identity: identityProp, onColumnsChange }: ListViewProps) {
    const ambientTier = useViewport()
    const tier = viewport ?? ambientTier
    // AB#3953 / AB#3954 — the EFFECTIVE columns: the module's defaults reconciled with the user's
    // stored layout (order, hidden defaults, added `availableColumns`). With no identity or no store
    // this is exactly `list.columns`, as before.
    const defaults = view.list?.columns ?? EMPTY_COLUMNS
    const available = view.list?.availableColumns ?? EMPTY_COLUMNS
    const identity = identityProp ?? tableIdentity(view)
    const layoutStore = useTableLayoutStore(tableLayoutStore)
    const layout = useTableLayout(identity, defaults, available, layoutStore)
    const cols = layout.columns
    // AB#1648 — entityType is optional (absent for dashboards); ListView is only used for entity views.
    // AB#2737 — a `source` list's rows are the COLLECTION's rows, so its columns are the collection's
    // fields. buildEntityFieldMeta already registers a parent's children[] under the collection key, so
    // this is a lookup rather than new plumbing. Parent columns are handled in `fieldMeta` below.
    const parentMeta = (view.entityType ? entityFields[view.entityType] : undefined) ?? []
    const childCollection = view.list?.source?.childCollection
    const meta = (childCollection ? entityFields[childCollection] : undefined) ?? parentMeta
    // A projected parent field arrives as the flat key `parent.<name>`; resolve its metadata from the
    // PARENT so it formats as a date, money or enum like it does on the document. Without this every
    // parent column renders as a bare unformatted string, which reads as a styling bug (AB#2512 again).
    const fieldMeta = (field: string) => field.startsWith('parent.')
        ? parentMeta.find(m => m.name === field.slice('parent.'.length))
        : meta.find(m => m.name === field)
    const alignClass = (a?: string) => a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left'
    // AB#2045 — the sort button fills its th, so its own flex justification must mirror the column align.
    const justifyClass = (a?: string) => a === 'right' ? 'justify-end' : a === 'center' ? 'justify-center' : 'justify-start'

    // AB#3953 — the shared configurator (cog + panel + drag). Idle, it decorates nothing.
    const configurator = useColumnConfigurator({
        identity, layout, viewport: tier,
        labelOf: field => layout.allColumns.find(c => c.field === field)?.label ?? fieldMeta(field)?.label ?? field,
    })
    // AB#3954 — tell the host which columns are in effect (search fields, export columns follow them).
    // Reported by field signature, never by array identity, so a re-render cannot loop the host.
    const signature = cols.map(c => c.field).join('\u0000')
    const reported = useRef<string | null>(null)
    useEffect(() => {
        if (!onColumnsChange || reported.current === signature) { return }
        reported.current = signature
        onColumnsChange(cols)
    }, [signature, cols, onColumnsChange])
    // A sort on a column the user has hidden is cleared: the header that showed the ▲ is gone, and an
    // order nobody can see is an order nobody can reason about.
    useEffect(() => {
        if (sort && onSortChange && !cols.some(c => c.field === sort.field)) { onSortChange(null) }
    }, [sort, onSortChange, signature, cols])

    // AB#2043 — FK-label resolution folded up from the tenant fork so this ONE ListView (used by both the
    // tenant runtime and the designer preview) resolves FK columns identically. FK labels are resolved
    // server-side per page and attached to each row under `__lookups` (keyed by FK field); the cell prefers
    // that label over the raw GUID, mirroring the read-mode field cell / object-header pattern (lookupLabel).
    // The designer preview's rows carry no `__lookups`, so lookupLabel returns undefined and the cell falls
    // straight through to formatValue — no behaviour change in the preview (AB#1914/1925).
    // AB#2421 — a related-entity roll-up column (row.__rollups) wins first: its field names a declared
    // roll-up, not a data key, so nothing else in the chain could ever render it.
    const cell = (row: Record<string, unknown>, field: string) => {
        const fd = fieldMeta(field)
        const text = rollupLabel(row, field) ?? lookupLabel(row, field) ?? formatValue(row[field], fd?.type, fd?.customType, fmt)
        // AB#3281 — a value whose enum option declares a semantic tone renders as a toned pill; the
        // TEXT is exactly what the cell showed before, so colour is never the only signal. A value
        // with no declared tone keeps the plain-text cell byte-for-byte.
        const tone = resolveOptionTone(fd?.enum, row[field])
        return tone
            ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tonePillClasses[tone]}`}>{text}</span>
            : text
    }

    // AB#3281 — list.rowAccent: a left-edge accent strip on each row, coloured by the named field's
    // declared tone. Applied to the row's FIRST rendered cell (a <tr> border is unreliable across
    // border models); a row whose value carries no tone gets no strip and is untouched.
    const accentField = view.list?.rowAccent?.field
    const rowAccent = (row: Record<string, unknown>): string | undefined => {
        if (!accentField) { return undefined }
        const tone = resolveOptionTone(fieldMeta(accentField)?.enum, row[accentField])
        return tone ? `border-l-4 ${toneAccentBorderClasses[tone]}` : undefined
    }

    // AB#3289 — single-level grouping (list.groupBy): rows render under one header <tr> per distinct
    // value of the group field, each carrying the group's row count and the sum of every declared
    // `totals` column's numeric values. CLIENT-SIDE over the rows the host passed (one fetched page):
    // the host (PagedListView) defaults the server sort to the group field so groups arrive
    // contiguous, but a group spanning a page boundary continues on the next page — counts/totals are
    // per page. The header label resolves like a cell (roll-up → FK label → formatted value), so an
    // enum groups by its label and an FK by its display value, never the raw id.
    const groupBy = view.list?.groupBy
    const colCount = Math.max(cols.length + (rowAction ? 1 : 0) + (leadingColumn ? 1 : 0), 1)
    const groupedRows = (() => {
        if (!groupBy) { return null }
        const order: { key: string; label: string; rows: Record<string, unknown>[] }[] = []
        const byKey = new Map<string, { key: string; label: string; rows: Record<string, unknown>[] }>()
        for (const row of rows) {
            const key = String(row[groupBy.field] ?? '')
            let g = byKey.get(key)
            if (!g) {
                const fd = fieldMeta(groupBy.field)
                const label = rollupLabel(row, groupBy.field) ?? lookupLabel(row, groupBy.field)
                    ?? formatValue(row[groupBy.field], fd?.type, fd?.customType, fmt)
                g = { key, label, rows: [] }
                byKey.set(key, g)
                order.push(g)
            }
            g.rows.push(row)
        }
        return order
    })()
    const groupHeaderRow = (g: { key: string; label: string; rows: Record<string, unknown>[] }) => {
        const totals = (groupBy?.totals ?? []).map(t => {
            const fd = fieldMeta(t)
            const sum = g.rows.reduce((acc, r) => typeof r[t] === 'number' ? acc + (r[t] as number) : acc, 0)
            const label = cols.find(c => c.field === t)?.label ?? fd?.label ?? t
            return `${label}: ${formatValue(sum, fd?.type, fd?.customType, fmt)}`
        })
        return (
            <tr key={`group:${g.key}`} className="bg-neutral-50 dark:bg-slate-900/50" data-group={g.key}>
                <td colSpan={colCount} className="px-3 py-1.5 text-xs font-semibold text-neutral-600 dark:text-slate-300">
                    {g.label}
                    <span className="ml-2 font-normal text-neutral-400 dark:text-slate-500">
                        {g.rows.length} row{g.rows.length === 1 ? '' : 's'}{totals.length > 0 ? ` · ${totals.join(' · ')}` : ''}
                    </span>
                </td>
            </tr>
        )
    }

    // AB#3280 — compact (phone) tier: cards instead of a table. The card projection is
    // `list.cardFields` when the view declares it (first entry = card title, the rest label/value
    // rows), else the first three list columns — so every shipped view gets a usable phone card
    // with no re-authoring. Cells render through the SAME cell() as the table (FK labels, roll-ups,
    // tones, formatting), rowAccent keeps its left strip, groupBy keeps its headers, and a
    // clickable card mirrors the row's keyboard contract (AB#2052). Wide is untouched below.
    if (tier === 'compact' || view.list?.presentation === 'cards') {
        const declared = view.list?.cardFields
        const cardCols: TableColumn[] = declared && declared.length > 0
            ? declared.map(f => cols.find(c => c.field === f) ?? { field: f })
            : cols.slice(0, 3)
        const [titleCol, ...restCols] = cardCols
        const colLabel = (c: TableColumn) => c.label ?? fieldMeta(c.field)?.label ?? c.field
        const renderCard = (row: Record<string, unknown>, key: number | string) => (
            <div key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row) }
                } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                className={`bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-3 min-h-[44px] ${rowAccent(row) ?? ''}${onRowClick ? ' cursor-pointer hover:bg-neutral-50 dark:hover:bg-slate-700/50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-neutral-900 dark:text-slate-100 min-w-0">
                        {titleCol ? cell(row, titleCol.field) : '—'}
                    </div>
                    {rowAction && (
                        // Same guard as the table's action cell (AB#2748): the action's in-tree dialogs
                        // must never bubble a click into the card's drill-down.
                        <div className="flex-none" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                            {rowAction(row)}
                        </div>
                    )}
                </div>
                {leadingColumn && (
                    <div className="mt-1 text-xs text-neutral-500 dark:text-slate-400">{leadingColumn.render(row)}</div>
                )}
                {restCols.length > 0 && (
                    <dl className="mt-1.5 space-y-1">
                        {restCols.map(c => (
                            <div key={c.field} className="flex items-baseline justify-between gap-3 text-xs">
                                <dt className="text-neutral-500 dark:text-slate-400 flex-none">{colLabel(c)}</dt>
                                <dd className="text-neutral-900 dark:text-slate-100 text-right min-w-0 truncate" title={String(lookupLabel(row, c.field) ?? row[c.field] ?? "")}>{cell(row, c.field)}</dd>
                            </div>
                        ))}
                    </dl>
                )}
            </div>
        )
        const cards = (
            <div className={view.list?.presentation === "cards" ? "grid gap-4" : "space-y-2"}
                style={view.list?.presentation === 'cards' ? { gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))' } : undefined}
                data-viewport="compact">
                {loading && rows.length === 0 ? (
                    Array.from({ length: 5 }, (_, i) => (
                        <div key={`skeleton-${i}`} className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-3">
                            <div className="h-3 rounded bg-neutral-100 dark:bg-slate-700 animate-pulse" />
                        </div>
                    ))
                ) : rows.length === 0 ? (
                    <div className="px-3 py-6 text-center text-neutral-400 dark:text-slate-500 text-xs italic">No records</div>
                ) : groupedRows ? groupedRows.map(g => (
                    <div key={`group:${g.key}`} className="space-y-2" data-group={g.key}>
                        <div className="px-1 pt-1 text-xs font-semibold text-neutral-600 dark:text-slate-300">
                            {g.label}
                            <span className="ml-2 font-normal text-neutral-400 dark:text-slate-500">{g.rows.length} row{g.rows.length === 1 ? '' : 's'}</span>
                        </div>
                        {g.rows.map((row, i) => renderCard(row, `${g.key}:${i}`))}
                    </div>
                )) : rows.map((row, i) => renderCard(row, i))}
            </div>
        )
        // AB#3953 — the cog sits above the cards; the panel stacks below them (compact tier). A list
        // with no identity renders `cards` exactly as before.
        return configurator.frame(configurator.cog
            ? <div><div className="flex justify-end pb-1">{configurator.cog}</div>{cards}</div>
            : cards)
    }

    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 overflow-hidden">
            {/* AB#3953 — the column cog, top-right, only when the table has an identity to personalise under. */}
            {configurator.cog && <div className="flex justify-end px-1 pt-1">{configurator.cog}</div>}
            {configurator.frame(<div className="marque-table-frame overflow-x-auto">
                <table className={"marque-table " + (`w-full text-sm ${view.list?.compactCells ? "table-fixed" : ""}`)}>
                    <thead className="bg-neutral-50 dark:bg-slate-900/50 border-b border-neutral-200 dark:border-slate-700">
                        <tr>{leadingColumn && (
                            <th className="px-3 py-2 text-xs font-medium text-neutral-500 dark:text-slate-400 text-left">
                                {leadingColumn.label}
                            </th>
                        )}{cols.map((c, ci) => {
                            const label = c.label ?? fieldMeta(c.field)?.label ?? c.field
                            // AB#2045 — this column's active direction (undefined when unsorted / uncontrolled).
                            const dir = sort && sort.field === c.field ? sort.dir : undefined
                            // AB#2421 — a column authored sortable:false (a roll-up: the server cannot
                            // sort it and would silently fall back to created_at DESC while the header
                            // claimed the sort) renders a plain label, no button, no aria-sort.
                            const sortableHere = !!onSortChange && c.sortable !== false
                            // AB#3953 — in configuration mode the heading is a drop target and carries a
                            // drag grip; idle, `decoration` is empty and the markup is unchanged.
                            const decoration = configurator.header(c.field, ci)
                            const heading = sortableHere ? (
                                // AB#2045 — a real button (keyboard-reachable) filling the th; clicking
                                // cycles asc → desc → none. ▲/▼ mark the active direction, ⇅ marks a
                                // sortable-but-inactive column (CrossModuleRefsPage vocabulary).
                                <button type="button" onClick={() => onSortChange(cycleListSort(sort, c.field))}
                                    className={`inline-flex w-full items-center cursor-pointer select-none hover:text-neutral-700 dark:hover:text-slate-200 ${justifyClass(c.align)}`}>
                                    {label}
                                    <span className="ml-1 text-[10px]" aria-hidden="true">{dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '⇅'}</span>
                                </button>
                            ) : label
                            return (
                                <th key={c.field} {...decoration.props} className={`px-3 py-2 text-xs font-medium text-neutral-500 dark:text-slate-400 ${alignClass(c.align)}${decoration.className ? ` ${decoration.className}` : ''}`}
                                    style={c.width ? { width: c.width } : undefined}
                                    aria-sort={sortableHere ? (dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none') : undefined}>
                                    {decoration.grip
                                        ? <div className="flex items-center gap-1"><div className="min-w-0 flex-1">{heading}</div>{decoration.grip}</div>
                                        : heading}
                                </th>
                            )
                        })}{rowAction && <th className="px-3 py-2 text-xs font-medium text-neutral-500 dark:text-slate-400 text-right" aria-label="Actions" />}</tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-slate-700">
                        {loading && rows.length === 0 ? (
                            // AB#1588 — skeleton rows while the nav process / page fetch is in flight.
                            Array.from({ length: 5 }, (_, i) => (
                                <tr key={`skeleton-${i}`}>
                                    {leadingColumn && (
                                        <td className="px-3 py-2">
                                            <div className="h-3 rounded bg-neutral-100 dark:bg-slate-700 animate-pulse" />
                                        </td>
                                    )}
                                    {cols.map(c => (
                                        <td key={c.field} className="px-3 py-2">
                                            <div className="h-3 rounded bg-neutral-100 dark:bg-slate-700 animate-pulse" />
                                        </td>
                                    ))}
                                    {rowAction && <td className="px-3 py-2" />}
                                </tr>
                            ))
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={Math.max(cols.length + (rowAction ? 1 : 0) + (leadingColumn ? 1 : 0), 1)} className="px-3 py-6 text-center text-neutral-400 dark:text-slate-500 text-xs italic">No records</td></tr>
                        ) : groupedRows ? groupedRows.flatMap(g => [
                            // AB#3289 — grouped: one header row per distinct group value, then that
                            // group's rows through the SAME renderer as the flat list (row keys are
                            // namespaced by group so two groups' rows can never collide).
                            groupHeaderRow(g),
                            ...g.rows.map((row, i) => renderRow(row, `${g.key}:${i}`)),
                        ]) : rows.map((row, i) => renderRow(row, i))}
                    </tbody>
                </table>
            </div>)}
        </div>
    )

    function renderRow(row: Record<string, unknown>, key: number | string) {
        return (
            // AB#2052 — row drill-down is the primary navigation gesture, so a clickable row must be
            // keyboard-operable, not mouse-only. A <tr> cannot take role="button" without breaking the
            // table's row/cell semantics (a row in a rowgroup must stay a `row`), so we keep the native
            // <tr> and make it focusable (tabIndex=0) + activate on Enter/Space via onKeyDown. Space is
            // preventDefault'd so activating a row never also scrolls the page. The focus ring uses the
            // package's focus vocabulary (focus:ring-1 focus:ring-blue-500, inset so it reads inside the
            // row's own bounds). Rows WITHOUT onRowClick stay fully inert — no tabIndex, no handler, no
            // ring — so a non-navigable list is unchanged and adds no stray tab stops.
            <tr key={key}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                onKeyDown={onRowClick ? e => {
                                    // Enter and Space both activate; preventDefault stops Space from also scrolling
                                    // the page (its native default) when it triggers the drill-down.
                                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row) }
                                } : undefined}
                                tabIndex={onRowClick ? 0 : undefined}
                                className={onRowClick ? 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-slate-700/50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500' : undefined}>
                                {leadingColumn && (
                                    <td className={`px-3 py-2 text-neutral-500 dark:text-slate-400 whitespace-nowrap ${rowAccent(row) ?? ''}`}>
                                        {leadingColumn.render(row)}
                                    </td>
                                )}
                                {cols.map((c, ci) => (
                                    // AB#2043 — cell() prefers the server-resolved FK label (row.__lookups[field])
                                    // then falls to formatValue (AB#1925 month-number → month name).
                                    // AB#3281 — the row's accent strip lands on the first rendered cell (the
                                    // leading column when present, else the first data column).
                                    <td key={c.field} className={`px-3 py-2 text-neutral-900 dark:text-slate-100 ${alignClass(c.align)}${!leadingColumn && ci === 0 ? ` ${rowAccent(row) ?? ''}` : ''}`}>
                                        {view.list?.compactCells ? <div className="truncate" title={String(lookupLabel(row, c.field) ?? row[c.field] ?? "")}>{cell(row, c.field)}</div> : cell(row, c.field)}
                                    </td>
                                ))}
                                {rowAction && (
                                    // AB#2271 — trailing action cell.
                                    //
                                    // AB#2748 — the CELL stops propagation, not just the control inside it. A row
                                    // action's dialogs (confirm, the pending user-section, the follow-on picker) are
                                    // `fixed inset-0` divs rendered IN THE REACT TREE — Modal.tsx says "Deliberately
                                    // NOT portalled" — so despite covering the screen they are DOM descendants of
                                    // this row. A stopPropagation on the button does nothing for them: clicking
                                    // CANCEL closed the dialog and then bubbled to the row, navigating the user away
                                    // from the list they had just declined to act on; Confirm did the action AND
                                    // navigated, unmounting the list mid-refetch. Guarding here covers every overlay
                                    // any host renders, which is why it is not left to each caller.
                                    //
                                    // onKeyDown too: the row activates on Enter/Space (AB#2052), and a user-section
                                    // dialog is full of inputs somebody will press Enter in.
                                    <td className="px-3 py-2 text-right whitespace-nowrap"
                                        onClick={e => e.stopPropagation()}
                                        onKeyDown={e => e.stopPropagation()}>{rowAction(row)}</td>
                                )}
                            </tr>
        )
    }
}

// ── ViewRenderer ─────────────────────────────────────────────────────────────

export interface ViewRendererProps {
    workspaceData?: DataWorkspaceProps['data']
    workspaceIdentity?: string | undefined
    workspaceLoading?: boolean | undefined
    workspaceError?: string | undefined
    onWorkspaceReload?: (() => void) | undefined
    onWorkspaceRecordAction?: DataWorkspaceProps['onRecordAction']
    workspaceEditor?: DataWorkspaceProps['editor']
    workspaceToolbar?: DataWorkspaceProps['toolbar']
    onWorkspaceNavigate?: DataWorkspaceProps['onNavigate']
    /** Host-owned action bindings, rendered in the shared record header rather than beside a duplicate page title. */
    headerActions?: ReactNode
    headerToolbar?: ReactNode
    headerNavigation?: ReactNode
    view: ViewDefinition
    context: ViewContext
    entityFields: EntityFieldMeta
    /**
     * AB#2879 — the current user's permission codes, so a component can be gated on one.
     *
     * The renderer previously had NO access to permissions at all, which made the one spelling that
     * already existed for a component permission gate — `showIf: "permission:CODE"` — evaluate
     * `permissions?.has(code) ?? false`, i.e. ALWAYS FALSE. It did not fail loudly; it hid the
     * component from every user including those who held the code. Anyone who tried the documented
     * gate would have concluded the feature was broken, which it was.
     *
     * Undefined means "not known yet" and FAILS OPEN — the same convention ActionButton and the
     * archive control use. Hiding during the load flicker is worse than briefly showing something the
     * server will refuse anyway; the server is the enforcement boundary, this is presentation.
     */
    permissions?: ReadonlySet<string> | undefined
    /**
     * AB#2889 — host navigation for a metric tile's declared `drillDown` (the unshipped second half
     * of AB#2879 / ADR-0044 §13). The renderer is host-agnostic and holds no router; the tenant host
     * supplies this and maps the process name to /app/view/<process> — the exact spelling a navigate
     * action and the sidebar already use. Hosts that omit it (the designer preview) render every
     * tile display-only, exactly as before drill-down existed.
     *
     * AB#3967 — the optional second argument is `navigationScope` below, forwarded verbatim by the
     * clicked tile. A host that predates the scope receives the one-argument call it always did.
     */
    onNavigateToProcess?: ((processName: string, scope?: DrillDownScope) => void) | undefined
    /**
     * AB#3967 — the scope a drill-down should carry into its target: the cockpit passes its active
     * tab's company / site / valuation area, and a metric tile's click hands it to
     * `onNavigateToProcess` as the second argument. Absent (a module dashboard, the designer) = the
     * callback receives the process name only, exactly as before.
     */
    navigationScope?: DrillDownScope | undefined
    stateColors?: Record<string, string> | undefined
    // AB#1400 — pass these to render the edit grid with input controls bound to the draft.
    editable?: boolean | undefined
    draft?: ViewContext | undefined
    onChange?: ((draft: ViewContext) => void) | undefined
    // AB#1839 — designer-preview parity: the host wires an FK option loader and the in-screen company
    // so the preview renders the company picker + reactive dependent re-scoping like the tenant runtime.
    fetchEntityOptions?: FetchEntityOptions | undefined
    // AB#2040 — host fetch-by-id label resolver for the searchable FK combobox (off-page selection label).
    fetchEntityById?: FetchEntityById | undefined
    // AB#2518 — fetches a referenced record's fields so a copyFrom can seed from it.
    fetchEntityRecord?: FetchEntityRecord | undefined
    // AB#2520 — host seam that prices a provisional document line. Absent (designer preview) = no
    // live pricing at all.
    fetchLinePrice?: FetchLinePrice | undefined
    // AB#2266 — host reference-data lookup option loader: a lookupValue field renders as a value select
    // (edit) and resolves its stored code to the lookup label (read). Hosts that omit it (the designer
    // preview) keep the previous free-text input / raw-code display — no behaviour change.
    fetchLookupOptions?: FetchLookupOptions | undefined
    // AB#2393/2395 — host unit-of-measure catalogue loader for the quantity-input unit select. Hosts that
    // omit it (the designer preview) render the amount input with an empty unit list.
    fetchUnitOptions?: FetchUnitOptions | undefined
    // AB#2981 — host-provided outbound-connection loader for the connection-picker editor.
    fetchConnectionOptions?: FetchConnectionOptions | undefined
    // AB#3291 — host user-directory loader for the user-picker editor. Hosts that omit it (the
    // designer preview) render the select with only the stored value.
    fetchUserOptions?: FetchUserOptions | undefined
    // AB#2447 — host audited-reveal loader for a sensitive field's full value. Hosts that omit it
    // (the designer preview) render the mask with no reveal affordance.
    fetchRevealField?: FetchRevealField | undefined
    // AB#2767 — host-injected per-field change-history affordance (button + modal). Rendered beside a
    // field's label when its meta declares fieldHistory; the designer injects nothing and shows nothing.
    fieldHistoryComponent?: React.ComponentType<{ entityType: string; recordId: string; field: string; label?: string | undefined }> | undefined
    // AB#2450 — host address lookup (autocomplete + resolve). Omitted = manual-only address editor.
    fetchAddressSearch?: FetchAddressSearch | undefined
    // AB#2451 — host address map for showMap address blocks. Omitted = no map (designer preview).
    addressMapComponent?: AddressMapComponent | undefined
    scopeCompanyId?: string | undefined
    // AB#1839 — when the entity is company-scoped the preview auto-injects a non-deletable company
    // system slot (it is rendered here, never authored into the grid, so it cannot be removed/redefined).
    companyScoped?: boolean | undefined
    // AB#1866 — host seams: the tenant injects its live custom-component / documents / FK-aware EditField;
    // the designer omits them and the renderer uses placeholders + the preview EditField.
    customComponent?: CustomSlotComponent | undefined
    documentsComponent?: CustomSlotComponent | undefined
    // AB#2386 — file-attachments seam (the live upload/download/delete panel over the AB#2021 attachment
    // endpoints). Deliberately separate from documentsComponent, which keeps rendering the AB#1395
    // generated-document / comms tracker for communications-panel.
    attachmentsComponent?: CustomSlotComponent | undefined
    mapComponent?: CustomSlotComponent | undefined
    buttonComponent?: CustomSlotComponent | undefined
    barcodeComponent?: CustomSlotComponent | undefined
    chartComponent?: CustomSlotComponent | undefined
    // AB#2046/2121 — audit-trail / document-flow / search-panel seams (same as the pieces above): the tenant
    // injects its live change-history panel, document-flow graph, and search surface; the designer (and any
    // host that omits a seam) keeps the placeholder.
    auditTrailComponent?: CustomSlotComponent | undefined
    documentFlowComponent?: CustomSlotComponent | undefined
    searchPanelComponent?: CustomSlotComponent | undefined
    // AB#2316 — host-injected live related-entity-list: fetches a child entity's rows scoped to the parent
    // FK and provides modal add/edit (running the child's own save process). The designer/preview omit it,
    // so `related-entity-list` falls back to the read-only, context-bound LineItemsSlot (unchanged).
    relatedListComponent?: CustomSlotComponent | undefined
    // AB#2169 — pricing platform seams (stack-canvas / calc-trace / matrix-grid), same contract as above.
    stackCanvasComponent?: CustomSlotComponent | undefined
    calcTraceComponent?: CustomSlotComponent | undefined
    // AB#2679 � the determination decision trace (why THIS site / account / printer was chosen).
    determinationTraceComponent?: CustomSlotComponent | undefined
    matrixGridComponent?: CustomSlotComponent | undefined
    // AB#3063 — module-owned cache stats/Clear card; display+action seam, no `editable` (nothing to gate).
    managedCacheComponent?: CustomSlotComponent | undefined
    // AB#3285 — timeline multi-event mode seam (see the SlotProps declaration above).
    timelineComponent?: CustomSlotComponent | undefined
    // AB#3238 — user-calendar: the tenant injects the live agenda (GET /api/calendar/me interleaved
    // with POST /api/v3/agenda). A host without the seam renders the static UserCalendarSlot preview.
    userCalendarComponent?: CustomSlotComponent | undefined
    editFieldComponent?: EditFieldComponent | undefined
    // AB#2640 — host loader for a child grid's create-form preview rows (see FetchPreviewRows).
    fetchPreviewRows?: FetchPreviewRows | undefined
    // AB#2049 — tenant formatting context (BCP 47 locale + ISO 4217 base currency) for dates / numbers /
    // money. The tenant thin host wires the real values (from useLocale + the company's baseCurrency); the
    // designer preview omits it and formatValue falls back to the previous browser-locale, bare-number output.
    formatting?: FormatContext | undefined
    // AB#2655 — host-provided synchronous company-id → label resolver for the object-header's company
    // meta item (see SlotProps.resolveCompanyLabel). The tenant wires the accessible-companies cache;
    // the designer omits it and the raw value renders as before.
    resolveCompanyLabel?: ((companyId: string) => string | undefined) | undefined
    // AB#3280 — forces the layout tier ('compact' | 'wide') regardless of the ambient
    // ViewportProvider: tests pin both tiers with it and the designer phone preview forces
    // 'compact'. Absent = the provider's tier, which without a provider is 'wide' (the pre-3280
    // desktop layout, byte-for-byte).
    viewport?: ViewportTier | undefined
    // AB#3954 — the per-user table layout store (see SlotProps.tableLayoutStore).
    tableLayoutStore?: TableLayoutStore | undefined
}

// AB#1839 — the auto-injected, non-deletable company picker shown on company-scoped views. It is part
// of the renderer, NOT the view definition, so an author can never delete it, move it, or redefine it
// as a free-form field — matching the id/system-field treatment.
function CompanySystemSlot({ fetchEntityOptions, scopeCompanyId }: { fetchEntityOptions?: FetchEntityOptions | undefined; scopeCompanyId?: string | undefined }) {
    const [options, setOptions] = useState<{ value: string; label: string }[]>([])
    useEffect(() => {
        if (!fetchEntityOptions) { return }
        let ignore = false
        fetchEntityOptions('Company', undefined, undefined, undefined, scopeCompanyId)
            .then(o => { if (!ignore) { setOptions(o) } })
            .catch(() => { /* preview — keep current options on error */ })
        return () => { ignore = true }
    }, [fetchEntityOptions, scopeCompanyId])

    return (
        <div className="marque-panel bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-2 mb-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">Company</label>
                <span className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-slate-400 bg-neutral-100 dark:bg-slate-700 rounded px-1.5 py-0.5">system</span>
            </div>
            <select value={scopeCompanyId ?? ''} disabled
                className="marque-input w-full max-w-sm rounded border border-neutral-300 dark:border-slate-600 px-2 py-1.5 text-sm bg-neutral-50 dark:bg-slate-900 text-neutral-700 dark:text-slate-300">
                {options.length === 0 && <option value="">—</option>}
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    )
}

export function ViewRenderer(props: ViewRendererProps) {
    if (props.view.workspace) {
        return <DataWorkspace definition={props.view.workspace} context={props.context} permissions={props.permissions}
            data={props.workspaceData} identity={props.workspaceIdentity} loading={props.workspaceLoading}
            error={props.workspaceError} onReload={props.onWorkspaceReload} onRecordAction={props.onWorkspaceRecordAction} editor={props.workspaceEditor} toolbar={props.workspaceToolbar} onNavigate={props.onWorkspaceNavigate} />
    }
    return <GridViewRenderer {...props} />
}

function GridViewRenderer({ view, context, entityFields, permissions, onNavigateToProcess, navigationScope, stateColors, editable, draft, onChange, fetchEntityOptions, fetchEntityById, fetchEntityRecord, fetchLinePrice, fetchLookupOptions, fetchUnitOptions, fetchConnectionOptions, fetchUserOptions, fetchRevealField, fetchAddressSearch, addressMapComponent, scopeCompanyId, companyScoped, customComponent, documentsComponent, attachmentsComponent, mapComponent, buttonComponent, barcodeComponent, chartComponent, auditTrailComponent, documentFlowComponent, searchPanelComponent, relatedListComponent, stackCanvasComponent, calcTraceComponent, determinationTraceComponent, matrixGridComponent, managedCacheComponent, timelineComponent, userCalendarComponent, editFieldComponent, fieldHistoryComponent, fetchPreviewRows, formatting, resolveCompanyLabel, viewport, headerActions, headerToolbar, headerNavigation, tableLayoutStore }: ViewRendererProps) {
    // AB#3280 — resolve the layout tier once at the top so every nested grid and slot agrees:
    // explicit prop (tests, designer phone preview) beats the ambient ViewportProvider, whose
    // no-provider default is 'wide'.
    const ambientTier = useViewport()
    const tier = viewport ?? ambientTier
    // AB#1400 — edit mode renders the editGrid (falling back to the display grid).
    const grid = editable ? (view.editGrid ?? view.grid) : view.grid
    const gridComponents = grid.components ?? []
    const evalData = editable && draft ? draft : context
    // Top-level components only; tab-slot children are rendered by their TabContainer.
    const topLevel = gridComponents.filter(c => !(c as ViewComponent & { tabSlot?: string }).tabSlot)
    const baseProps: SlotProps = {
        component: topLevel[0] ?? { type: 'separator', col: 1, row: 1, colSpan: 1, rowSpan: 1 },
        view, context, entityFields, permissions, onNavigateToProcess, navigationScope, stateColors, editable, draft, onChange, gridComponents, depth: 0, viewport: tier,
        fetchEntityOptions, fetchEntityById, fetchEntityRecord, fetchLinePrice, fetchLookupOptions, fetchUnitOptions, fetchConnectionOptions, fetchUserOptions, fetchRevealField, fetchAddressSearch, addressMapComponent, scopeCompanyId, customComponent, documentsComponent, attachmentsComponent, mapComponent, buttonComponent, barcodeComponent, chartComponent, auditTrailComponent, documentFlowComponent, searchPanelComponent, relatedListComponent, stackCanvasComponent, calcTraceComponent, determinationTraceComponent, matrixGridComponent, managedCacheComponent, timelineComponent, userCalendarComponent, editFieldComponent, fieldHistoryComponent, fetchPreviewRows,
        fmt: formatting, resolveCompanyLabel, tableLayoutStore,
    }

    const workspace = recordWorkspaceLayout(view, grid)
    if (workspace) {
        const record = asRecord(resolveBinding(workspace.header.dataBinding, context))
        return <ViewportProvider force={tier}><div data-module={view.moduleId}>
            <RecordWorkspace key={`${view.viewKey ?? view.entityType ?? view.moduleId}:${String(record.id ?? 'new')}`}
                title={!workspace.header.field && workspace.header.title === view.displayName ? undefined : view.displayName ?? view.entityType}
                header={componentVisible(workspace.header, baseProps, evalData) && <ObjectHeaderSlot {...baseProps} component={workspace.header} pageHeader />}
                actions={headerActions} toolbar={headerToolbar} navigation={headerNavigation}
                guidanceCompany={scopeCompanyId} guidanceNew={!!editable && !record.id}
                guidanceGeneral={guidanceFields(view, workspace.general)}
                general={<>
                    {companyScoped && editable && <div className="mb-4"><CompanySystemSlot fetchEntityOptions={fetchEntityOptions} scopeCompanyId={scopeCompanyId} /></div>}
                    <RecordGeneral components={workspace.general} props={baseProps} evalData={evalData} guidanceNew={!!editable && !record.id} />
                </>}
                tabs={workspace.tabs.filter(tab => componentVisible(tab.owner, baseProps, evalData)
                    && tab.components.some(c => componentVisible(c, baseProps, evalData))).map(tab => ({
                    key: tab.key, label: tab.label,
                    guidanceFields: guidanceFields(view, tab.components),
                    content: <ComponentGrid components={tab.components} props={baseProps} evalData={evalData} />,
                }))} />
        </div></ViewportProvider>
    }
    // AB#3989 — a tile view's root is a flex column that fills the host (tile.css .view-tile-host), not
    // the stacked page (space-y-4) a module view renders into.
    return (
        <ViewportProvider force={tier}><div data-module={view.moduleId} className={isTile(view) ? 'view-tile-host' : 'space-y-4'}>
            {companyScoped && editable && (
                <CompanySystemSlot fetchEntityOptions={fetchEntityOptions} scopeCompanyId={scopeCompanyId} />
            )}
            <ComponentGrid components={topLevel} props={baseProps} evalData={evalData} />
        </div></ViewportProvider>
    )
}

function componentVisible(component: ViewComponent, props: SlotProps, evalData: ViewContext) {
    return (!component.permission || (props.permissions?.has(component.permission) ?? true))
        && (!component.showIf || evaluateShowIf(component.showIf, asRecord(resolveBinding(component.dataBinding, evalData)), props.permissions))
}

function RecordGeneral({ components, props, evalData, guidanceNew }: { components: ViewComponent[]; props: SlotProps; evalData: ViewContext; guidanceNew: boolean }) {
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
    const groups = recordFieldGroups(components.filter(component => componentVisible(component, props, evalData)))
    return <div className={`grid gap-5 ${props.viewport === 'compact' ? 'grid-cols-1' : 'grid-cols-1 @3xl:grid-cols-2'}`}>
        {groups.map(group => group.heading ? <section key={group.key}
            className={`marque-panel min-w-0 rounded-lg border border-neutral-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 ${group.fullWidth ? 'col-span-full' : ''}`}>
            <button type="button" aria-expanded={!collapsed.has(group.key)}
                data-guidance-fields={guidanceFields(props.view, group.components).join(' ')}
                data-guidance-company={props.scopeCompanyId} data-guidance-new={guidanceNew} data-guidance-open={!collapsed.has(group.key)}
                onClick={() => setCollapsed(previous => { const next = new Set(previous); if (next.has(group.key)) next.delete(group.key); else next.add(group.key); return next })}
                className="flex w-full items-center gap-2 text-left text-sm font-medium text-neutral-900 dark:text-slate-100 rounded focus-visible:ring-2 focus-visible:ring-blue-500">
                {group.heading.icon && <Icon name={group.heading.icon} size={16} className="text-neutral-500 dark:text-slate-400" />}
                <span>{group.heading.title}</span><Icon name={collapsed.has(group.key) ? 'ChevronRight' : 'ChevronDown'} size={14} className="ml-auto text-neutral-400 dark:text-slate-500" />
            </button>
            <div hidden={collapsed.has(group.key)} className="mt-4"><ComponentGrid components={group.components} props={props} evalData={evalData} /></div>
        </section> : <div key={group.key} className="min-w-0 col-span-full"><ComponentGrid components={group.components} props={props} evalData={evalData} /></div>)}
    </div>
}
